import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Filter, Users, User, FileText, Upload, Download, Eye, X, Package, RotateCcw, Check, Building, UserMinus, Calendar, AlertTriangle, AlertCircle, Bell, CheckCircle, Clock, Minus, FileUp, Loader2, Lock, CreditCard, ArrowUpDown, ArrowUp, ArrowDown, Archive, FolderInput, ClipboardList, Phone, Mail, Briefcase, ChevronLeft } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import AddEmployeeModal from './components/modals/AddEmployeeModal.jsx';
import EditEmployeeModal from './components/modals/EditEmployeeModal.jsx';
import SalaryHistoryModal from './components/modals/SalaryHistoryModal.jsx';
import DeleteEmployeeModal from './components/modals/DeleteEmployeeModal.jsx';
import PayrollsModal from './components/modals/PayrollsModal.jsx';
import DocumentsDetailModal from './components/modals/DocumentsDetailModal.jsx';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';

// Helper function to extract error message from API responses
const getErrorMessage = (error, defaultMsg = 'Bir hata oluştu') => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMsg;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors come as array of objects with 'msg' field
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return defaultMsg;
};

// Tek bir çalışan için (year, month) maaş çizelgesi satırını hesaplar.
// Maaş Çizelgesi sekmesiyle BİREBİR aynı mantık (tek kaynak).
const computeMonthlySalaryRow = (emp, year, month, advancesYearData, overtimeMap, extraDeductionsBreakdown) => {
  const attendance = emp.attendance || {};
  const daysInMonth = new Date(year, month, 0).getDate();
  const hireDate = emp.hire_date ? new Date(emp.hire_date) : null;
  const monthEnd = new Date(year, month, 0);
  let expectedWorkingDays = daysInMonth;
  if (hireDate) {
    if (hireDate.getFullYear() === year && (hireDate.getMonth() + 1) === month) {
      expectedWorkingDays = daysInMonth - hireDate.getDate() + 1;
    } else if (hireDate > monthEnd) {
      expectedWorkingDays = 0;
    }
  }
  let presentDays = 0, halfDays = 0, paidLeaveDays = 0, unpaidLeaveDays = 0, sickDays = 0,
      annualDays = 0, holidayDays = 0, absentDays = 0, weekendDays = 0, holidayOvertimeDays = 0;
  Object.values(attendance).forEach(status => {
    switch (status) {
      case 'present': presentDays++; break;
      case 'half_day': halfDays++; break;
      case 'paid_leave': paidLeaveDays++; break;
      case 'unpaid_leave': unpaidLeaveDays++; break;
      case 'sick': sickDays++; break;
      case 'annual': annualDays++; break;
      case 'holiday': holidayDays++; break;
      case 'absent': absentDays++; break;
      case 'weekend': weekendDays++; break;
      case 'holiday_overtime': holidayOvertimeDays++; break;
      default: break;
    }
  });
  const fullPayDays = presentDays + sickDays + annualDays + holidayDays + weekendDays + holidayOvertimeDays + paidLeaveDays;
  const halfPayDays = halfDays;
  const rawWorkedDays = fullPayDays + (halfPayDays * 0.5);
  const workedDays = Math.min(rawWorkedDays, expectedWorkingDays);
  const rawPhysicalWorkDays = fullPayDays + halfPayDays;
  const physicalWorkDays = Math.min(rawPhysicalWorkDays, expectedWorkingDays);
  const currentSalary = emp.current_salary || 0;
  const dailySalary = currentSalary / 30;
  const supports = emp.supports || [];
  const findSupport = (n) => supports.find(s => s.type === n || s.support_type === n || s.name === n);
  const yemekKartiAylik = findSupport('yemek_karti')?.amount || 0;
  const yolParasiAylik = findSupport('yol_parasi')?.amount || 0;
  const saglikSigortasiAylik = findSupport('saglik_sigortasi')?.amount || 0;
  const yemekKartiGunluk = yemekKartiAylik / 30;
  const yolParasiGunluk = yolParasiAylik / 30;
  const devamsizGunlerYemekYol = absentDays + unpaidLeaveDays;
  let yemekKartiHesaplanan = 0;
  if (expectedWorkingDays < daysInMonth) {
    yemekKartiHesaplanan = yemekKartiGunluk * physicalWorkDays;
  } else {
    yemekKartiHesaplanan = Math.max(0, yemekKartiAylik - (yemekKartiGunluk * devamsizGunlerYemekYol));
  }
  const yolParasiHesaplanan = Math.max(0, yolParasiGunluk * workedDays);
  const empAdvances = (advancesYearData || []).find(a => a.employee_id === emp.employee_id);
  const avansKesintisi = empAdvances?.deductions?.[month] || 0;
  const breakdown = (extraDeductionsBreakdown || {})[emp.employee_id] || { sgk: 0, damage: 0, other: 0 };
  const ekKesintiler = (breakdown.sgk || 0) + (breakdown.damage || 0) + (breakdown.other || 0);
  const saatlikUcret = dailySalary / 7.5;
  const empOvertime = (overtimeMap || {})[emp.employee_id] || { weekday_hours: 0, holiday_hours: 0 };
  const fazlaMesaiNormal = saatlikUcret * empOvertime.weekday_hours * 1.5;
  const fazlaMesaiTatil = saatlikUcret * empOvertime.holiday_hours * 2;
  const toplamFazlaMesai = fazlaMesaiNormal + fazlaMesaiTatil;
  const calisilanMaas = Math.max(0, dailySalary * workedDays);
  const yatirilacakMaas = (calisilanMaas + toplamFazlaMesai + yolParasiHesaplanan) - (avansKesintisi + ekKesintiler);
  const yemekYolFarki = (yemekKartiAylik + yolParasiAylik) - (yemekKartiHesaplanan + yolParasiHesaplanan);
  const toplamKesintiler = (avansKesintisi + ekKesintiler) + yemekYolFarki;
  const toplamHakedis = yatirilacakMaas + yemekKartiHesaplanan + saglikSigortasiAylik;
  const anlasilanToplam = currentSalary + yemekKartiAylik + yolParasiAylik + saglikSigortasiAylik;
  return {
    employee_id: emp.employee_id, name: emp.name, branch_name: emp.branch_name,
    position_name: emp.position_name, termination_date: emp.termination_date,
    employment_status: emp.employment_status, hire_date: emp.hire_date,
    expected_working_days: expectedWorkingDays, current_salary: currentSalary,
    agreed_meal_card: yemekKartiAylik, agreed_travel: yolParasiAylik,
    agreed_health_insurance: saglikSigortasiAylik, agreed_total: anlasilanToplam,
    daily_salary: dailySalary, present_days: presentDays, half_days: halfDays,
    paid_leave_days: paidLeaveDays, unpaid_leave_days: unpaidLeaveDays, sick_days: sickDays,
    annual_days: annualDays, holiday_days: holidayDays, absent_days: absentDays,
    worked_days: workedDays, maas_hakedis: calisilanMaas, physical_work_days: physicalWorkDays,
    fazla_mesai_normal: fazlaMesaiNormal, fazla_mesai_tatil: fazlaMesaiTatil,
    fazla_mesai_saat_normal: empOvertime.weekday_hours, fazla_mesai_saat_tatil: empOvertime.holiday_hours,
    yemek_karti: yemekKartiHesaplanan, yol_parasi: yolParasiHesaplanan,
    saglik_sigortasi: saglikSigortasiAylik, avans_kesintisi: avansKesintisi,
    ek_kesintiler: ekKesintiler, ek_kesinti_sgk: breakdown.sgk || 0,
    ek_kesinti_zarar: breakdown.damage || 0, ek_kesinti_diger: breakdown.other || 0,
    yatirilacak_maas: yatirilacakMaas, toplam_hakedis: toplamHakedis, toplam_kesintiler: toplamKesintiler
  };
};

const ATT_STATUS_LABELS = {
  present: 'Geldi', half_day: 'Yarım Gün', absent: 'Gelmedi', paid_leave: 'Ücretli İzin',
  unpaid_leave: 'Ücretsiz İzin', sick: 'Raporlu', annual: 'Yıllık İzin', holiday: 'Resmi Tatil',
  weekend: 'Hafta Sonu', holiday_overtime: 'Tatil Mesaisi'
};

const TR_MONTHS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];

// CSV (Excel uyumlu, TR karakter + BOM) indirme
const downloadCsv = (rows, fileName) => {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return (s.includes(';') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(';'), ...rows.map(r => headers.map(h => esc(r[h])).join(';'))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `${fileName}.csv`;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 250);
};


const EmployeeList = ({ viewOnly = false }) => {
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [archivedSubcontractors, setArchivedSubcontractors] = useState([]);
  const [showArchivedSubcontractors, setShowArchivedSubcontractors] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('fortis'); // 'fortis' or subcontractor_id
  const [showTerminated, setShowTerminated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRehireListModal, setShowRehireListModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editViewOnly, setEditViewOnly] = useState(false);
  const [detailTab, setDetailTab] = useState('genel');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSalaryHistoryModal, setShowSalaryHistoryModal] = useState(false);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [newSalary, setNewSalary] = useState({ amount: '', effective_date: '', reason: '' });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // İşten ayrılmış çalışan kontrolü - modallarda düzenlemeyi engelle
  const isTerminatedEmployee = selectedEmployee?.employment_status === 'terminated' || !!selectedEmployee?.termination_date;
  
  // Çalışanlar Tablosu Sıralama State
  const [employeeSortColumn, setEmployeeSortColumn] = useState(null);
  const [employeeSortDirection, setEmployeeSortDirection] = useState('asc');
  
  // Günlük Mesai State - viewOnly modda devre dışı
  const [activeTab, setActiveTab] = useState('employees'); // 'employees', 'attendance', or 'monthly'
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [missingAttendance, setMissingAttendance] = useState({ count: 0, records: [] });
  const [showMissingWarning, setShowMissingWarning] = useState(true);
  const [attendanceSortColumn, setAttendanceSortColumn] = useState(null);
  const [attendanceSortDirection, setAttendanceSortDirection] = useState('asc');
  
  // Aylık Mesai State
  const [monthlyDate, setMonthlyDate] = useState(new Date());
  const [monthlyData, setMonthlyData] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlySortColumn, setMonthlySortColumn] = useState(null);
  const [monthlySortDirection, setMonthlySortDirection] = useState('asc');
  
  // Onaylı İzin Talepleri (gün bazında)
  const [approvedLeavesByEmployee, setApprovedLeavesByEmployee] = useState({});
  const [showLeaveDetailModal, setShowLeaveDetailModal] = useState(false);
  const [selectedLeaveDetail, setSelectedLeaveDetail] = useState(null);

  // Mesai Notları (Şube yöneticisi notları - gün bazında kırmızı gösterim)
  const [monthlyNotes, setMonthlyNotes] = useState({}); // {"employee_id|YYYY-MM-DD": {note,...}}
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteModalEmp, setNoteModalEmp] = useState(null); // {employee_id, name}
  const [noteModalDate, setNoteModalDate] = useState(null);
  const [noteModalText, setNoteModalText] = useState('');
  const [noteModalSaving, setNoteModalSaving] = useState(false);
  
  // İzinler (Yıllık Özet) State
  const [yearlyYear, setYearlyYear] = useState(new Date().getFullYear());
  const [yearlyData, setYearlyData] = useState([]);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [leavesSortColumn, setLeavesSortColumn] = useState(null);
  const [leavesSortDirection, setLeavesSortDirection] = useState('asc');
  
  // İzin Ekle Modal State
  const [showAddLeaveModal, setShowAddLeaveModal] = useState(false);
  const [newLeave, setNewLeave] = useState({
    employee_id: '',
    izinTuru: '', // 'yillik', 'mazeret', 'ucretsiz'
    izinBaslangic: '',
    izinBitis: '',
    aciklama: '',
    belgeler: []
  });
  
  // Avanslar State
  const [advancesYear, setAdvancesYear] = useState(new Date().getFullYear());
  const [advancesData, setAdvancesData] = useState([]);
  const [advancesLoading, setAdvancesLoading] = useState(false);
  const [advancesSubTab, setAdvancesSubTab] = useState('taken'); // 'taken' or 'deductions'
  const [showAdvanceDetailModal, setShowAdvanceDetailModal] = useState(false);
  const [advanceDetailData, setAdvanceDetailData] = useState(null);
  const [advanceDetailLoading, setAdvanceDetailLoading] = useState(false);
  const [advancesSortColumn, setAdvancesSortColumn] = useState(null);
  const [advancesSortDirection, setAdvancesSortDirection] = useState('asc');
  
  // Maaş Çizelgesi State
  const [salarySheetMonth, setSalarySheetMonth] = useState(new Date().getMonth() + 1);
  const [salarySheetYear, setSalarySheetYear] = useState(new Date().getFullYear());
  const [salarySheetData, setSalarySheetData] = useState([]);
  const [salarySheetLoading, setSalarySheetLoading] = useState(false);
  const [salarySortColumn, setSalarySortColumn] = useState(null);
  const [salarySortDirection, setSalarySortDirection] = useState('asc');
  
  // Ek Kesintiler State
  const [extraDeductionsMonth, setExtraDeductionsMonth] = useState(new Date().getMonth() + 1);
  const [extraDeductionsYear, setExtraDeductionsYear] = useState(new Date().getFullYear());
  const [extraDeductionsData, setExtraDeductionsData] = useState([]);
  const [extraDeductionsLoading, setExtraDeductionsLoading] = useState(false);
  const [showAddDeductionModal, setShowAddDeductionModal] = useState(false);
  const [showDeductionDetailModal, setShowDeductionDetailModal] = useState(false);
  const [selectedDeductionEmployee, setSelectedDeductionEmployee] = useState(null);
  const [selectedDeductionType, setSelectedDeductionType] = useState('');
  const [deductionDetailData, setDeductionDetailData] = useState(null);
  const [newDeduction, setNewDeduction] = useState({ amount: '', description: '' });
  const [deductionFile, setDeductionFile] = useState(null);
  const [deductionsSortColumn, setDeductionsSortColumn] = useState(null);
  const [deductionsSortDirection, setDeductionsSortDirection] = useState('asc');
  
  // Fazla Mesai State
  const [overtimeMonth, setOvertimeMonth] = useState(new Date().getMonth() + 1);
  const [overtimeYear, setOvertimeYear] = useState(new Date().getFullYear());
  const [overtimeData, setOvertimeData] = useState([]);
  const [overtimeLoading, setOvertimeLoading] = useState(false);
  const [showAddOvertimeModal, setShowAddOvertimeModal] = useState(false);
  const [newOvertime, setNewOvertime] = useState({ employee_id: '', date: '', type: 'weekday', hours: '', description: '', documents: [] });
  const [overtimeSortColumn, setOvertimeSortColumn] = useState(null);
  const [overtimeSortDirection, setOvertimeSortDirection] = useState('asc');
  
  // Avans Ekle State
  const [showAddAdvanceModal, setShowAddAdvanceModal] = useState(false);
  const [newAdvance, setNewAdvance] = useState({
    employee_id: '',
    amount: '',
    installments: 1,
    given_date: '',           // Avans veriliş tarihi - Çekilen Avanslar için
    deduction_start_date: '', // Kesinti başlama tarihi - Aylık Kesintiler için
    description: '',
    documents: []
  });
  
  const [selectedSupports, setSelectedSupports] = useState({
    yemek_karti: { enabled: false, amount: '' },
    yol_parasi: { enabled: false, amount: '' },
    saglik_sigortasi: { enabled: false, amount: '' }
  });
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [documentForm, setDocumentForm] = useState({ type: '', custom_name: '', file_url: '', file_name: '' });
  const [activeDocType, setActiveDocType] = useState(null);
  const [customDocuments, setCustomDocuments] = useState([]);
  const [showCustomDocInput, setShowCustomDocInput] = useState(false);
  const [customDocName, setCustomDocName] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showAssetsModal, setShowAssetsModal] = useState(false);
  const [employeeAssets, setEmployeeAssets] = useState([]);
  const [employeeAssetsArchive, setEmployeeAssetsArchive] = useState([]);
  const [moveDialog, setMoveDialog] = useState({ open: false, item: null, sourceKey: null });
  const [newAsset, setNewAsset] = useState({ asset_name: '', asset_description: '', serial_number: '', notes: '' });
  const [showSubcontractorModal, setShowSubcontractorModal] = useState(false);
  const [showSubcontractorListModal, setShowSubcontractorListModal] = useState(false);
  const [selectedSubDetail, setSelectedSubDetail] = useState(null);
  const [newSubcontractor, setNewSubcontractor] = useState({ 
    name: '', tax_number: '', phone: '', email: '', contact_person: '', 
    contacts: [{ name: '', role: '', phone: '', email: '' }], 
    branch_id: '',
    start_date: '',
    end_date: '',
    provided_service: '',
    custom_fields: [] // { key: '', value: '' }
  });
  
  // Bordrolar Modal State
  const [showPayrollsModal, setShowPayrollsModal] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [newPayroll, setNewPayroll] = useState({ month: '', year: new Date().getFullYear(), file_name: '', file_url: '' });
  
  // Evraklar Modal State
  const [showDocumentsDetailModal, setShowDocumentsDetailModal] = useState(false);
  const [documentsDetail, setDocumentsDetail] = useState({
    izin_talepleri: [],
    raporlar: [],
    savunmalar: [],
    sikayetler: [],
    tutanaklar: [],
    avanslar: [],
    ise_giris_bildirgesi: [],
    aile_durum_bildirimi: [],
    is_sozlesmesi: [],
    kvkk_belgesi: [],
    aydinlatma_metni: [],
    gorev_tanim_belgesi: [],
    isg_egitim_belgeleri: []
  });
  const [activeDocCategory, setActiveDocCategory] = useState('izin_talepleri');
  const [hrDocReqTab, setHrDocReqTab] = useState(null); // İK Belge Talepleri seçili belge adı (title)
  const [docReqPendingCounts, setDocReqPendingCounts] = useState({}); // employee_id -> cevap bekleyen belge sayısı
  const [newDocumentDetail, setNewDocumentDetail] = useState({ name: '', file_url: '' });
  
  // Document Reminder/Alarm State
  const [documentReminders, setDocumentReminders] = useState([]);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [newReminder, setNewReminder] = useState({
    document_type: '',
    document_name: '',
    due_date: '',
    notes: ''
  });
  
  // İşten Ayrılma Modal State
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [terminationData, setTerminationData] = useState({
    termination_date: new Date().toISOString().split('T')[0],
    termination_reason: '',
    exit_documents: [],
    asset_returns: []
  });
  const [terminationDocuments, setTerminationDocuments] = useState([]);
  const [assetReturnStatus, setAssetReturnStatus] = useState({});
  const [newSalaryAmount, setNewSalaryAmount] = useState('');

  // Şifre Korumalı Silme Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
  const CORRECT_PASSWORD = 'Alyatugba21';

  // Şifre doğrulama fonksiyonu
  const requestPasswordForDelete = (actionType, actionData) => {
    setPendingDeleteAction({ type: actionType, data: actionData });
    setPasswordInput('');
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async () => {
    if (passwordInput !== CORRECT_PASSWORD) {
      toast.error('Yanlış şifre!');
      return;
    }
    setShowPasswordModal(false);
    
    if (pendingDeleteAction) {
      const { type, data } = pendingDeleteAction;
      switch (type) {
        case 'employee':
          await executeDeleteEmployee(data);
          break;
        case 'attendance':
          await executeDeleteAttendance(data);
          break;
        case 'monthly':
          await executeDeleteMonthly(data);
          break;
        case 'monthlyAttendance':
          await executeDeleteMonthlyAttendance(data);
          break;
        case 'overtime':
          await executeDeleteOvertime(data.employeeId, data.recordId);
          break;
        case 'deduction':
          await executeDeleteDeduction(data);
          break;
        case 'salary':
          await executeDeleteSalary(data);
          break;
        case 'salarySheet':
          await executeDeleteSalarySheet(data);
          break;
        case 'leave':
          await executeDeleteLeave(data);
          break;
        case 'yearlyLeaves':
          await executeDeleteYearlyLeaves(data);
          break;
        case 'advance':
          await executeDeleteAdvance(data);
          break;
        default:
          break;
      }
    }
    setPendingDeleteAction(null);
    setPasswordInput('');
  };

  const handleFileUpload = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, [fieldName]: reader.result });
      toast.success('Dosya yüklendi');
    };
    reader.onerror = () => {
      toast.error('Dosya yüklenirken hata oluştu');
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB for documents)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setDocumentForm({ 
        ...documentForm, 
        file_url: reader.result,
        file_name: file.name 
      });
      toast.success('Belge dosyası yüklendi');
    };
    reader.onerror = () => {
      toast.error('Dosya yüklenirken hata oluştu');
    };
    reader.readAsDataURL(file);
  };

  // New card-based document upload handler
  const handleCardDocumentUpload = async (e, docType, customName = null) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB for documents)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/document`, {
          document_type: docType,
          document_name: customName,
          file_url: reader.result,
          file_name: file.name,
          upload_date: new Date().toISOString().split('T')[0]
        });
        
        toast.success(`${customName || documentTypes.find(dt => dt.value === docType)?.label || docType} yüklendi`);
        
        // Refresh documents
        const response = await axios.get(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`);
        setEmployeeDocuments(response.data.documents || []);
        fetchEmployees();
        
        // Reset states
        setActiveDocType(null);
        setShowCustomDocInput(false);
        setCustomDocName('');
      } catch (error) {
        toast.error('Belge yüklenemedi');
      }
    };
    reader.onerror = () => {
      toast.error('Dosya okunurken hata oluştu');
    };
    reader.readAsDataURL(file);
    
    // Reset file input
    e.target.value = '';
  };

  const getDocumentsForType = (docType) => {
    return employeeDocuments.filter(d => d.document_type === docType);
  };

  const triggerFileInput = (docType) => {
    setActiveDocType(docType);
    document.getElementById(`doc-upload-${docType}`).click();
  };

  const handleAddCustomDocSlot = () => {
    if (!customDocName.trim()) {
      toast.error('Belge adı girin');
      return;
    }
    const newCustomDoc = {
      id: `custom-${Date.now()}`,
      name: customDocName.trim(),
      type: 'custom'
    };
    setCustomDocuments([...customDocuments, newCustomDoc]);
    setCustomDocName('');
    setShowCustomDocInput(false);
  };

  const openDocumentPreview = (doc) => {
    setPreviewDoc(doc);
    setShowPreviewModal(true);
  };

  const downloadDocument = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error('Dosya bulunamadı');
      return;
    }

    try {
      // For base64 data URLs, we need to convert to blob
      if (fileUrl.startsWith('data:')) {
        // Extract the base64 content
        const parts = fileUrl.split(',');
        if (parts.length < 2) {
          toast.error('Geçersiz dosya formatı');
          return;
        }
        
        const header = parts[0];
        const base64Content = parts[1];
        const mimeType = header.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
        
        // Convert base64 to binary
        const binaryString = window.atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Create blob
        const blob = new Blob([bytes], { type: mimeType });
        
        // Use saveAs approach for better compatibility
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = blobUrl;
        link.download = fileName || 'document';
        link.setAttribute('download', fileName || 'document');
        
        document.body.appendChild(link);
        link.click();
        
        // Cleanup after a delay to ensure download starts
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 250);
        
        toast.success('Dosya indiriliyor...');
      } else {
        // For regular URLs - open in new tab to trigger download
        window.open(fileUrl, '_blank');
        toast.success('Dosya açılıyor...');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Dosya indirilirken hata oluştu');
    }
  };

  // Export employees to Excel
  const exportToExcel = () => {
    let excelData = [];
    let fileName = '';
    
    // Helper function to get support amount
    const getSupport = (supports, supportName) => {
      if (!supports || supports.length === 0) return null;
      return supports.find(s => 
        (s.name === supportName || s.support_type === supportName) && 
        (s.enabled !== false && s.is_active !== false)
      );
    };
    
    // Helper to get status color text
    const getStatusText = (status) => {
      switch(status) {
        case 'tam_gun': return 'Tam Gün';
        case 'yarim_gun': return 'Yarım Gün';
        case 'izinli': return 'İzinli';
        case 'raporlu': return 'Raporlu';
        case 'yok': return 'Yok';
        default: return status || '-';
      }
    };

    // Export based on active tab
    switch(activeTab) {
      case 'employees':
        if (employees.length === 0) {
          toast.error('İndirilecek çalışan verisi yok');
          return;
        }
        excelData = employees.map((employee, index) => {
          const currentSalary = employee.salary_history && employee.salary_history.length > 0
            ? employee.salary_history[employee.salary_history.length - 1]
            : null;
          
          const yemekKarti = getSupport(employee.supports, 'yemek_karti') || getSupport(employee.supports, 'yemek');
          const yolParasi = getSupport(employee.supports, 'yol_parasi') || getSupport(employee.supports, 'yol');
          const saglikSigortasi = getSupport(employee.supports, 'saglik_sigortasi') || getSupport(employee.supports, 'saglik');

          return {
            'No': index + 1,
            'Ad Soyad': employee.name || '-',
            'TC Kimlik No': employee.tc_no || '-',
            'Telefon': employee.phone || '-',
            'Meslek': employee.profession || '-',
            'Görev': employee.position_name || '-',
            'Şube': employee.branch_name || '-',
            'Maaş': currentSalary && currentSalary.amount ? currentSalary.amount : '-',
            'Yemek Kartı': yemekKarti ? yemekKarti.amount : '-',
            'Yol Parası': yolParasi ? yolParasi.amount : '-',
            'Sağlık Sigortası': saglikSigortasi ? saglikSigortasi.amount : '-',
            'İşe Giriş': employee.hire_date || '-',
            'Durum': employee.employment_status === 'active' ? 'Aktif' : 
                     employee.employment_status === 'on_leave' ? 'İzinli' : 'Ayrılmış'
          };
        });
        fileName = `Calisanlar_Listesi_${new Date().toISOString().split('T')[0]}`;
        break;
        
      case 'attendance':
        const filteredAttendance = filterByCompany(attendanceData);
        if (!filteredAttendance || filteredAttendance.length === 0) {
          toast.error('İndirilecek günlük mesai verisi yok');
          return;
        }
        const getAttendanceStatusText = (status) => {
          switch(status) {
            case 'present': return 'İşe Geldi';
            case 'half_day': return 'Yarım Gün';
            case 'absent': return 'Gelmedi';
            case 'paid_leave': return 'Ücretli İzinli';
            case 'unpaid_leave': return 'Ücretsiz İzinli';
            case 'sick': return 'Raporlu';
            case 'annual': return 'Yıllık İzin';
            case 'holiday': return 'Resmi Tatil';
            case 'weekend': return 'Hafta Sonu';
            case 'holiday_overtime': return 'Tatil Mesaisi';
            default: return status || '-';
          }
        };
        excelData = filteredAttendance.map((emp, index) => {
          return {
            'No': index + 1,
            'Ad Soyad': emp.name || '-',
            'Şube': emp.branch_name || '-',
            'Görev': emp.position_name || '-',
            'TC Kimlik No': emp.tc_no || '-',
            'Telefon': emp.phone || '-',
            'Mesai Durumu': getAttendanceStatusText(emp.status)
          };
        });
        fileName = `Gunluk_Mesai_${attendanceDate}`;
        break;
        
      case 'monthly':
        // Use the same sorted and filtered data as the table
        const sortedFilteredMonthlyData = [...filterByCompany(monthlyData)].sort((a, b) => {
          if (monthlySortColumn === null) return 0;
          if (monthlySortColumn === 'name') {
            return monthlySortDirection === 'asc' 
              ? a.name.localeCompare(b.name)
              : b.name.localeCompare(a.name);
          }
          const statusA = a.attendance?.[monthlySortColumn] || '';
          const statusB = b.attendance?.[monthlySortColumn] || '';
          return monthlySortDirection === 'asc' 
            ? statusA.localeCompare(statusB)
            : statusB.localeCompare(statusA);
        });
        
        if (!sortedFilteredMonthlyData || sortedFilteredMonthlyData.length === 0) {
          toast.error('İndirilecek aylık mesai verisi yok');
          return;
        }
        const monthDate = monthlyDate instanceof Date ? monthlyDate : new Date(monthlyDate);
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();
        const getMonthlyStatusText = (status) => {
          switch(status) {
            case 'present': return 'G';
            case 'half_day': return 'YG';
            case 'absent': return 'X';
            case 'paid_leave': return 'Üİ';
            case 'unpaid_leave': return 'Üsİ';
            case 'sick': return 'R';
            case 'annual': return 'Yİ';
            case 'holiday': return 'T';
            default: return '-';
          }
        };
        excelData = sortedFilteredMonthlyData.map((emp, index) => {
          const row = {};
          // Add columns in correct order
          row['No'] = index + 1;
          row['Ad Soyad'] = emp.name || '-';
          row['Şube'] = emp.branch_name || '-';
          row['Görev'] = emp.position_name || '-';
          // Add each day and count statuses
          const counts = { present: 0, half_day: 0, absent: 0, paid_leave: 0, unpaid_leave: 0, sick: 0, annual: 0, holiday: 0, weekend: 0, holiday_overtime: 0 };
          for (let day = 1; day <= daysInMonth; day++) {
            const attendance = emp.attendance || {};
            const status = attendance[day] || '-';
            row[`${day}`] = getMonthlyStatusText(status);
            if (counts.hasOwnProperty(status)) counts[status]++;
          }
          // Add separate columns for each status count
          row['İşe Geldi'] = counts.present || '-';
          row['Yarım Gün'] = counts.half_day || '-';
          row['Gelmedi'] = counts.absent || '-';
          row['Ücretli İzinli'] = counts.paid_leave || '-';
          row['Ücretsiz İzinli'] = counts.unpaid_leave || '-';
          row['Raporlu'] = counts.sick || '-';
          row['Yıllık İzin'] = counts.annual || '-';
          row['Resmi Tatil'] = counts.holiday || '-';
          row['Hafta Sonu'] = counts.weekend || '-';
          row['Tatil Mesaisi'] = counts.holiday_overtime || '-';
          return row;
        });
        fileName = `Aylik_Mesai_${year}-${String(month).padStart(2, '0')}`;
        break;
        
      case 'leaves':
        if (!yearlyData || yearlyData.length === 0) {
          toast.error('İndirilecek izin verisi yok');
          return;
        }
        const leaveMonthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
        excelData = yearlyData.map((emp, index) => {
          const row = {};
          // Add columns in correct order
          row['No'] = index + 1;
          row['Ad Soyad'] = emp.name || '-';
          row['Şube'] = emp.branch_name || '-';
          row['Görev'] = emp.position_name || '-';
          
          leaveMonthNames.forEach((monthName, idx) => {
            const monthData = emp.months ? emp.months[String(idx + 1)] : null;
            if (monthData) {
              const parts = [];
              if (monthData.present > 0) parts.push(`G:${monthData.present}`);
              if (monthData.absent > 0) parts.push(`X:${monthData.absent}`);
              if (monthData.leave > 0 || monthData.paid_leave > 0) parts.push(`Üİ:${monthData.paid_leave || monthData.leave || 0}`);
              if (monthData.unpaid_leave > 0) parts.push(`Üsİ:${monthData.unpaid_leave}`);
              if (monthData.sick > 0) parts.push(`R:${monthData.sick}`);
              if (monthData.annual > 0) parts.push(`Y:${monthData.annual}`);
              if (monthData.holiday > 0) parts.push(`T:${monthData.holiday}`);
              row[monthName] = parts.join(' | ') || '-';
            } else {
              row[monthName] = '-';
            }
          });
          // Add separate total columns
          row['İşe Geldi'] = emp.totals?.present || '-';
          row['Gelmedi'] = emp.totals?.absent || '-';
          row['Ücretli İzinli'] = emp.totals?.paid_leave || emp.totals?.leave || '-';
          row['Ücretsiz İzinli'] = emp.totals?.unpaid_leave || '-';
          row['Raporlu'] = emp.totals?.sick || '-';
          row['Yıllık İzin'] = emp.totals?.annual || '-';
          row['Resmi Tatil'] = emp.totals?.holiday || '-';
          row['Hafta Sonu'] = emp.totals?.weekend || '-';
          row['Tatil Mesaisi'] = emp.totals?.holiday_overtime || '-';
          return row;
        });
        fileName = `Izinler_${yearlyYear}`;
        break;
        
      case 'advances':
        if (!advancesData || advancesData.length === 0) {
          toast.error('İndirilecek avans verisi yok');
          return;
        }
        const advanceMonthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
        
        // Use correct data field based on sub-tab
        const dataField = advancesSubTab === 'taken' ? 'advances' : 'deductions';
        const totalField = advancesSubTab === 'taken' ? 'total_advances' : 'total_deductions';
        
        excelData = filterByCompany(advancesData).map((emp, index) => {
          const row = {};
          // Add columns in correct order
          row['No'] = index + 1;
          row['Ad Soyad'] = emp.name || '-';
          row['Şube'] = emp.branch_name || '-';
          row['Görev'] = emp.position_name || '-';
          
          let total = 0;
          advanceMonthNames.forEach((monthName, idx) => {
            const monthVal = emp[dataField] ? (emp[dataField][idx + 1] || 0) : 0;
            row[monthName] = monthVal > 0 ? monthVal : '-';
            total += monthVal;
          });
          row['Toplam'] = total > 0 ? total : '-';
          return row;
        });
        fileName = advancesSubTab === 'taken' 
          ? `Cekilen_Avanslar_${advancesYear}` 
          : `Aylik_Avans_Kesintileri_${advancesYear}`;
        break;
        
      case 'overtime':
        const overtimeFiltered = filterByCompany(overtimeData);
        if (!overtimeFiltered || overtimeFiltered.length === 0) {
          toast.error('İndirilecek fazla mesai verisi yok');
          return;
        }
        excelData = overtimeFiltered.map((record, index) => {
          return {
            'No': index + 1,
            'Ad Soyad': record.employee_name || '-',
            'Şube': record.branch_name || '-',
            'Görev': record.position_name || '-',
            'Tarih': record.date || '-',
            'Tip': record.type === 'weekday' ? 'Gün İçi' : 'Tatil',
            'Saat': record.hours || 0,
            'Açıklama': record.description || '-'
          };
        });
        fileName = `Fazla_Mesai_${overtimeYear}-${String(overtimeMonth).padStart(2, '0')}`;
        break;
        
      case 'deductions':
        const deductionsFiltered = filterByCompany(extraDeductionsData);
        if (!deductionsFiltered || deductionsFiltered.length === 0) {
          toast.error('İndirilecek ek kesinti verisi yok');
          return;
        }
        excelData = deductionsFiltered.map((emp, index) => {
          return {
            'No': index + 1,
            'Ad Soyad': emp.name || '-',
            'Şube': emp.branch_name || '-',
            'Görev': emp.position_name || '-',
            'SGK Kesintileri': emp.sgk?.amount || '-',
            'Zarar Kesintileri': emp.damage?.amount || '-',
            'Diğer Kesintiler': emp.other?.amount || '-',
            'Toplam': ((emp.sgk?.amount || 0) + (emp.damage?.amount || 0) + (emp.other?.amount || 0)) || '-'
          };
        });
        fileName = `Ek_Kesintiler_${extraDeductionsYear}-${String(extraDeductionsMonth).padStart(2, '0')}`;
        break;
        
      case 'salary':
        const salaryDataFiltered = filterByCompany(salarySheetData);
        if (!salaryDataFiltered || salaryDataFiltered.length === 0) {
          toast.error('İndirilecek maaş çizelgesi verisi yok');
          return;
        }
        excelData = salaryDataFiltered.map((emp, index) => {
          return {
            'No': index + 1,
            'Ad Soyad': emp.name || '-',
            'Şube': emp.branch_name || '-',
            'Görev': emp.position_name || '-',
            'Aylık Maaş': emp.current_salary ? emp.current_salary.toFixed(2) : '-',
            'Anl. Yemek K.': emp.agreed_meal_card ? emp.agreed_meal_card.toFixed(2) : '-',
            'Anl. Yol P.': emp.agreed_travel ? emp.agreed_travel.toFixed(2) : '-',
            'Anl. Sağlık S.': emp.agreed_health_insurance ? emp.agreed_health_insurance.toFixed(2) : '-',
            'Anlaşılan Toplam': emp.agreed_total ? emp.agreed_total.toFixed(2) : '-',
            'Günlük Maaş': emp.daily_salary ? emp.daily_salary.toFixed(2) : '-',
            'Çalışılan Gün': emp.worked_days ? emp.worked_days.toFixed(1) : '-',
            'Maaş Hakediş': emp.maas_hakedis ? emp.maas_hakedis.toFixed(2) : '-',
            'F.Mesai Normal': emp.fazla_mesai_normal ? emp.fazla_mesai_normal.toFixed(2) : '-',
            'F.Mesai Tatil': emp.fazla_mesai_tatil ? emp.fazla_mesai_tatil.toFixed(2) : '-',
            'Yemek Kartı': emp.yemek_karti ? emp.yemek_karti.toFixed(2) : '-',
            'Yol Parası': emp.yol_parasi ? emp.yol_parasi.toFixed(2) : '-',
            'Sağlık Sig.': emp.saglik_sigortasi ? emp.saglik_sigortasi.toFixed(2) : '-',
            'Avans Kes.': emp.avans_kesintisi ? emp.avans_kesintisi.toFixed(2) : '-',
            'SGK Kesintileri': emp.ek_kesinti_sgk ? emp.ek_kesinti_sgk.toFixed(2) : '-',
            'Zarar Kesintileri': emp.ek_kesinti_zarar ? emp.ek_kesinti_zarar.toFixed(2) : '-',
            'Diğer Kesintiler': emp.ek_kesinti_diger ? emp.ek_kesinti_diger.toFixed(2) : '-',
            'Yatırılacak Maaş': emp.yatirilacak_maas ? emp.yatirilacak_maas.toFixed(2) : '-',
            'Toplam Hakediş': emp.toplam_hakedis ? emp.toplam_hakedis.toFixed(2) : '-',
            'Toplam Kesintiler': emp.toplam_kesintiler ? emp.toplam_kesintiler.toFixed(2) : '-'
          };
        });
        fileName = `Maas_Cizelgesi_${salarySheetYear}-${String(salarySheetMonth).padStart(2, '0')}`;
        break;

      case 'salaryChanges':
        if (employees.length === 0) {
          toast.error('İndirilecek maaş değişikliği verisi yok');
          return;
        }
        
        // Maksimum zam sayısını bul
        const maxRaisesForExcel = Math.max(...(employees.map(emp => {
          const history = emp.salary_history || [];
          return Math.max(0, history.length - 1);
        }) || [0]), 0);
        
        excelData = employees.map((emp, index) => {
          const salaryHistory = (emp.salary_history || []).sort((a, b) => 
            new Date(a.effective_date || a.created_at) - new Date(b.effective_date || b.created_at)
          );
          const startingSalary = salaryHistory[0];
          const raises = salaryHistory.slice(1);
          
          const row = {
            'No': index + 1,
            'Ad Soyad': emp.name || '-',
            'İşe Giriş Tarihi': emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('tr-TR') : '-',
            'Başlangıç Maaşı': startingSalary?.amount || emp.starting_salary || '-'
          };
          
          // Dinamik zam sütunları ekle
          for (let i = 0; i < maxRaisesForExcel; i++) {
            const raise = raises[i];
            row[`${i + 1}. Zam Tarihi`] = raise?.effective_date 
              ? new Date(raise.effective_date).toLocaleDateString('tr-TR') 
              : '-';
            row[`${i + 1}. Zam Miktarı`] = raise?.amount || '-';
          }
          
          row['Güncel Maaş'] = emp.current_salary || '-';
          
          return row;
        });
        fileName = `Maas_Degisiklikleri_${new Date().toISOString().split('T')[0]}`;
        break;
        
      default:
        toast.error('Bu tab için Excel indirme desteklenmiyor');
        return;
    }

    if (excelData.length === 0) {
      toast.error('İndirilecek veri yok');
      return;
    }

    // Convert to CSV format with semicolon separator (better Excel compatibility)
    const headers = Object.keys(excelData[0]);
    const csvContent = [
      headers.join(';'),
      ...excelData.map(row => headers.map(h => {
        const val = row[h];
        // Escape semicolons and quotes in values
        if (typeof val === 'string' && (val.includes(';') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(';'))
    ].join('\n');

    // Add BOM for Turkish character support
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 250);

    toast.success('Excel dosyası indiriliyor...');
  };
  
  const [formData, setFormData] = useState({
    photo_url: '',
    tc_no: '',
    name: '',
    email: '',
    birth_date: '',
    gender: '',
    marital_status: '',
    children_count: 0,
    education_level: '',
    address: '',
    phone: '',
    emergency_contact: '',
    hire_date: '',
    department: '',
    profession: '',
    position_id: '',
    branch_id: '',
    starting_salary: '',
    contract_type: '',
    employment_status: 'active',
    has_bonus: false,
    bonus_type: '',
    bonus_description: '',
    subcontractor_id: null,
    // Sağlık Bilgileri
    blood_type: '',
    chronic_diseases: '',
    allergies: ''
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const documentTypes = [
    { value: 'kimlik', label: 'Kimlik' },
    { value: 'ehliyet', label: 'Ehliyet' },
    { value: 'ikametgah', label: 'İkametgah' },
    { value: 'sabika', label: 'Sabıka Kaydı' },
    { value: 'diploma', label: 'Diploma' },
    { value: 'oda_kaydi', label: 'Oda Kaydı' },
    { value: 'saglik_raporu', label: 'Sağlık Raporu' },
    { value: 'cv', label: 'CV' },
    { value: 'ise_giris_bildirgesi', label: 'İşe Giriş Bildirgesi' },
    { value: 'aile_durum_bildirimi', label: 'Aile Durum Bildirimi' },
    { value: 'is_sozlesmesi', label: 'İş Sözleşmesi' },
    { value: 'kvkk', label: 'KVKK Belgesi' },
    { value: 'aydinlatma_metni', label: 'Çalışan Aydınlatma Metni' },
    { value: 'gorev_tanim', label: 'Görev Tanım Belgesi' },
    { value: 'isg_egitim', label: 'İSG Eğitim Belgeleri' },
    { value: 'izin_belgesi', label: 'İzin Belgesi' },
    { value: 'raporlar', label: 'Raporlar' },
  ];

  useEffect(() => {
    fetchEmployees();
    fetchPositions();
    fetchProfessions();
    fetchBranches();
    fetchSubcontractors();
    fetchArchivedSubcontractors();
  }, []);

  const fetchSubcontractors = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/subcontractors`);
      setSubcontractors(response.data);
    } catch (error) {
      console.error('Subcontractors fetch error:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterBranch) params.branch_id = filterBranch;
      if (filterPosition) params.position_id = filterPosition;
      if (filterStatus) params.employment_status = filterStatus;
      
      // Add current year and month for terminated employee filtering
      const now = new Date();
      params.year = now.getFullYear();
      params.month = now.getMonth() + 1;

      const response = await axios.get(`${backendUrl}/api/hr/employees`, { params });
      
      // Cevap bekleyen belge talebi sayılarını yükle (isim yanı rozetleri için)
      try {
        const cntRes = await axios.get(`${backendUrl}/api/hr/document-requests/pending-counts`);
        setDocReqPendingCounts(cntRes.data || {});
      } catch (e) {
        console.log('Belge talebi sayıları yüklenemedi:', e);
      }
      
      // Filter by company/subcontractor and terminated status
      let filteredEmployees = response.data;
      
      if (selectedCompany === 'fortis') {
        filteredEmployees = filteredEmployees.filter(e => !e.subcontractor_id);
      } else if (selectedCompany !== 'all') {
        filteredEmployees = filteredEmployees.filter(e => e.subcontractor_id === selectedCompany);
      }
      
      // Filter by terminated status
      if (showTerminated) {
        filteredEmployees = filteredEmployees.filter(e => e.employment_status === 'terminated');
      } else {
        filteredEmployees = filteredEmployees.filter(e => e.employment_status !== 'terminated');
      }
      
      setEmployees(filteredEmployees);
    } catch (error) {
      toast.error('Çalışanlar yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/positions`);
      setPositions(response.data);
    } catch (error) {
      console.error('Positions fetch error:', error);
    }
  };

  // Günlük Mesai Fonksiyonları
  
  // Şirket/Taşeron filtreleme helper fonksiyonu
  const filterByCompany = (data) => {
    if (!data || !Array.isArray(data)) return [];
    if (selectedCompany === 'fortis') {
      return data.filter(item => !item.subcontractor_id);
    } else if (selectedCompany !== 'all') {
      return data.filter(item => item.subcontractor_id === selectedCompany);
    }
    return data;
  };
  
  const fetchAttendance = async (date) => {
    setAttendanceLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/hr/attendance/${date}`);
      setAttendanceData(response.data);
    } catch (error) {
      console.error('Attendance fetch error:', error);
      toast.error('Mesai verileri yüklenemedi');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchMissingAttendance = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/hr/attendance/missing?days=7`);
      setMissingAttendance(response.data);
    } catch (error) {
      console.error('Missing attendance fetch error:', error);
    }
  };

  const handleAttendanceChange = async (employeeId, newStatus, hireDate) => {
    // Check if attendance date is before hire date
    if (hireDate && attendanceDate < hireDate) {
      toast.error(`İşe giriş tarihinden (${new Date(hireDate).toLocaleDateString('tr-TR')}) önce mesai kaydı girilemez`);
      return;
    }
    
    try {
      // TOGGLE: Eğer newStatus null ise seçimi kaldır (silme işlemi)
      if (newStatus === null) {
        await axios.delete(`${backendUrl}/api/hr/attendance/${employeeId}/${attendanceDate}`);
        
        // Update local state - status'u temizle
        setAttendanceData(prev => prev.map(item => 
          item.employee_id === employeeId 
            ? { ...item, status: null }
            : item
        ));
        
        toast.success('Mesai kaydı kaldırıldı');
      } else {
        await axios.post(`${backendUrl}/api/hr/attendance`, {
          employee_id: employeeId,
          date: attendanceDate,
          status: newStatus
        });
        
        // Update local state
        setAttendanceData(prev => prev.map(item => 
          item.employee_id === employeeId 
            ? { ...item, status: newStatus }
            : item
        ));
        
        toast.success('Mesai kaydedildi');
      }
      
      // Refresh missing attendance count
      fetchMissingAttendance();
      
      // KURAL 3: Günlük mesai değişikliği aylık tabloya da yansıtılsın
      // Eğer kaydedilen tarih, aylık tabloda görüntülenen ay içindeyse güncelle
      const recordDate = new Date(attendanceDate);
      if (recordDate.getFullYear() === monthlyDate.getFullYear() && 
          recordDate.getMonth() === monthlyDate.getMonth()) {
        // Aylık tabloyu yeniden yükle
        fetchMonthlyAttendance(monthlyDate.getFullYear(), monthlyDate.getMonth() + 1);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Mesai kaydedilemedi'));
    }
  };

  // "İşe Geç Geldi" bağımsız bayrak toggle - ana durumu etkilemez
  const handleLateToggle = async (employeeId, currentIsLate, hireDate) => {
    if (hireDate && attendanceDate < hireDate) {
      toast.error(`İşe giriş tarihinden (${new Date(hireDate).toLocaleDateString('tr-TR')}) önce mesai kaydı girilemez`);
      return;
    }
    const newIsLate = !currentIsLate;
    try {
      await axios.post(`${backendUrl}/api/hr/attendance`, {
        employee_id: employeeId,
        date: attendanceDate,
        is_late: newIsLate
      });
      setAttendanceData(prev => prev.map(item =>
        item.employee_id === employeeId
          ? { ...item, is_late: newIsLate }
          : item
      ));
      toast.success(newIsLate ? 'İşe geç geldi olarak işaretlendi' : 'Geç geldi işareti kaldırıldı');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Geç geldi işareti kaydedilemedi'));
    }
  };
  const fetchMonthlyAttendance = async (year, month) => {
    setMonthlyLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/hr/attendance/monthly/${year}/${month}`);
      setMonthlyData(response.data);
      
      // Aynı ay için onaylı izin taleplerini de yükle
      fetchApprovedLeaves(year, month);
      // Aynı ay için mesai notlarını yükle (kırmızı gösterim)
      fetchMonthlyNotes(year, month);
    } catch (error) {
      console.error('Monthly attendance fetch error:', error);
      toast.error('Aylık mesai verileri yüklenemedi');
    } finally {
      setMonthlyLoading(false);
    }
  };

  // Onaylı izin taleplerini yükle
  const fetchApprovedLeaves = async (year, month) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/approved-leaves/${year}/${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Gün bazında employee_id -> leave_detail map oluştur
      const leaveMap = {};
      response.data.forEach(request => {
        const employeeId = request.employee_id;
        const selectedDays = request.hr_approval?.selected_days || [];
        
        selectedDays.forEach(day => {
          const key = `${employeeId}_${day}`;
          leaveMap[key] = {
            request_id: request.id,
            requester_name: request.requester_name,
            form_data: request.form_data,
            hr_approval: request.hr_approval,
            branch_approval: request.branch_approval,
            created_at: request.created_at
          };
        });
      });
      
      setApprovedLeavesByEmployee(leaveMap);
    } catch (error) {
      console.log('Approved leaves fetch skipped:', error);
    }
  };

  // İzin detayını göster
  const showLeaveDetail = (employeeId, day) => {
    const dateStr = `${monthlyDate.getFullYear()}-${String(monthlyDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const key = `${employeeId}_${dateStr}`;
    const detail = approvedLeavesByEmployee[key];
    
    if (detail) {
      setSelectedLeaveDetail(detail);
      setShowLeaveDetailModal(true);
    }
  };

  // Aya ait mesai notlarını yükle
  const fetchMonthlyNotes = async (year, month) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await axios.get(`${backendUrl}/api/manager/attendance-notes/month?year=${year}&month=${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMonthlyNotes(res.data || {});
    } catch (error) {
      console.log('Mesai notları yüklenemedi:', error);
      setMonthlyNotes({});
    }
  };

  // Bir gün için not metnini al
  const getDayNote = (employeeId, day) => {
    const dateStr = `${monthlyDate.getFullYear()}-${String(monthlyDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return monthlyNotes[`${employeeId}|${dateStr}`]?.note || '';
  };

  // Not modalını aç
  const openNoteModal = (employeeId, name, day) => {
    const dateStr = `${monthlyDate.getFullYear()}-${String(monthlyDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setNoteModalEmp({ employee_id: employeeId, name });
    setNoteModalDate(dateStr);
    setNoteModalText(monthlyNotes[`${employeeId}|${dateStr}`]?.note || '');
    setNoteModalOpen(true);
  };

  // Notu kaydet (İK)
  const saveNoteModal = async () => {
    if (!noteModalEmp) return;
    setNoteModalSaving(true);
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.put(`${backendUrl}/api/manager/attendance-note`, {
        employee_id: noteModalEmp.employee_id,
        employee_name: noteModalEmp.name,
        date: noteModalDate,
        note: noteModalText
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      toast.success(noteModalText.trim() ? 'Not kaydedildi' : 'Not silindi');
      setNoteModalOpen(false);
      setNoteModalEmp(null);
      setNoteModalText('');
      fetchMonthlyNotes(monthlyDate.getFullYear(), monthlyDate.getMonth() + 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Not kaydedilemedi');
    }
    setNoteModalSaving(false);
  };

  // Gün için onaylı izin var mı kontrol et (aylık mesai için)
  const hasApprovedLeave = (employeeId, day) => {
    const dateStr = `${monthlyDate.getFullYear()}-${String(monthlyDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const key = `${employeeId}_${dateStr}`;
    return !!approvedLeavesByEmployee[key];
  };

  // Günlük mesai için onaylı izin var mı kontrol et
  const hasApprovedLeaveForDate = (employeeId, dateStr) => {
    const key = `${employeeId}_${dateStr}`;
    return !!approvedLeavesByEmployee[key];
  };

  // Günlük mesai için izin detayını al
  const getApprovedLeaveDetailForDate = (employeeId, dateStr) => {
    const key = `${employeeId}_${dateStr}`;
    return approvedLeavesByEmployee[key] || null;
  };

  // İzin durumuna göre Türkçe isim al
  const getLeaveStatusName = (status) => {
    switch(status) {
      case 'annual': return 'Yıllık İzin';
      case 'sick': return 'Raporlu';
      case 'unpaid_leave': return 'Ücretsiz İzin';
      case 'paid_leave': return 'Ücretli İzin';
      default: return 'İzinli';
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getMonthName = (month) => {
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                   'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return months[month];
  };

  const getStatusShort = (status) => {
    switch(status) {
      case 'present': return 'G';
      case 'half_day': return 'YG';
      case 'absent': return 'X';
      case 'paid_leave': return 'Üİ';
      case 'unpaid_leave': return 'Üsİ';
      case 'sick': return 'R';
      case 'annual': return 'Y';
      case 'holiday': return 'T';
      case 'weekend': return 'HS';
      case 'holiday_overtime': return 'TM';
      default: return '-';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'present': return 'bg-green-100 text-green-800';
      case 'half_day': return 'bg-lime-100 text-lime-800';
      case 'absent': return 'bg-red-100 text-red-800';
      case 'paid_leave': return 'bg-blue-100 text-blue-800';
      case 'unpaid_leave': return 'bg-cyan-100 text-cyan-800';
      case 'sick': return 'bg-purple-100 text-purple-800';
      case 'annual': return 'bg-yellow-100 text-yellow-800';
      case 'holiday': return 'bg-orange-100 text-orange-800';
      case 'weekend': return 'bg-pink-100 text-pink-800';
      case 'holiday_overtime': return 'bg-rose-100 text-rose-800';
      default: return 'bg-gray-100 text-gray-400';
    }
  };

  const handleMonthlySort = (day) => {
    if (monthlySortColumn === day) {
      setMonthlySortDirection(monthlySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setMonthlySortColumn(day);
      setMonthlySortDirection('asc');
    }
  };

  // Aylık mesai verilerini şirkete göre filtrele ve sırala
  const sortedMonthlyData = [...filterByCompany(monthlyData)].sort((a, b) => {
    if (monthlySortColumn === null) return 0;
    if (monthlySortColumn === 'name') {
      return monthlySortDirection === 'asc' 
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    // Sort by specific day's status
    const statusA = a.attendance?.[monthlySortColumn] || '';
    const statusB = b.attendance?.[monthlySortColumn] || '';
    return monthlySortDirection === 'asc' 
      ? statusA.localeCompare(statusB)
      : statusB.localeCompare(statusA);
  });

  // Çalışanlar tablosu sıralama handler
  const handleEmployeeSort = (column) => {
    if (employeeSortColumn === column) {
      setEmployeeSortDirection(employeeSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setEmployeeSortColumn(column);
      setEmployeeSortDirection('asc');
    }
  };

  // Çalışanlar tablosunu sırala
  const sortedEmployees = [...employees].sort((a, b) => {
    if (employeeSortColumn === null) return 0;
    
    let valA, valB;
    
    switch (employeeSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'tc_no':
        valA = a.tc_no || '';
        valB = b.tc_no || '';
        break;
      case 'phone':
        valA = a.phone || '';
        valB = b.phone || '';
        break;
      case 'profession':
        valA = a.profession || '';
        valB = b.profession || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'salary':
        const salaryA = a.salary_history && a.salary_history.length > 0 
          ? parseFloat(a.salary_history[a.salary_history.length - 1].amount) || 0 
          : 0;
        const salaryB = b.salary_history && b.salary_history.length > 0 
          ? parseFloat(b.salary_history[b.salary_history.length - 1].amount) || 0 
          : 0;
        return employeeSortDirection === 'asc' ? salaryA - salaryB : salaryB - salaryA;
      case 'yemek':
        const getSupportAmount = (emp, supportName) => {
          if (!emp.supports || emp.supports.length === 0) return 0;
          const support = emp.supports.find(s => 
            (s.name === supportName || s.support_type === supportName) && 
            (s.enabled !== false && s.is_active !== false)
          );
          return support ? parseFloat(support.amount) || 0 : 0;
        };
        const yemekA = getSupportAmount(a, 'yemek_karti') || getSupportAmount(a, 'yemek');
        const yemekB = getSupportAmount(b, 'yemek_karti') || getSupportAmount(b, 'yemek');
        return employeeSortDirection === 'asc' ? yemekA - yemekB : yemekB - yemekA;
      case 'yol':
        const getYolSupport = (emp) => {
          if (!emp.supports || emp.supports.length === 0) return 0;
          const support = emp.supports.find(s => 
            (s.name === 'yol_parasi' || s.support_type === 'yol_parasi' || s.name === 'yol' || s.support_type === 'yol') && 
            (s.enabled !== false && s.is_active !== false)
          );
          return support ? parseFloat(support.amount) || 0 : 0;
        };
        const yolA = getYolSupport(a);
        const yolB = getYolSupport(b);
        return employeeSortDirection === 'asc' ? yolA - yolB : yolB - yolA;
      case 'saglik':
        const getSaglikSupport = (emp) => {
          if (!emp.supports || emp.supports.length === 0) return 0;
          const support = emp.supports.find(s => 
            (s.name === 'saglik_sigortasi' || s.support_type === 'saglik_sigortasi' || s.name === 'saglik' || s.support_type === 'saglik') && 
            (s.enabled !== false && s.is_active !== false)
          );
          return support ? parseFloat(support.amount) || 0 : 0;
        };
        const saglikA = getSaglikSupport(a);
        const saglikB = getSaglikSupport(b);
        return employeeSortDirection === 'asc' ? saglikA - saglikB : saglikB - saglikA;
      case 'hire_date':
        valA = a.hire_date || '';
        valB = b.hire_date || '';
        break;
      case 'termination_date':
        valA = a.termination_details?.date || a.termination_date || '';
        valB = b.termination_details?.date || b.termination_date || '';
        break;
      case 'status':
        valA = a.employment_status || '';
        valB = b.employment_status || '';
        break;
      default:
        return 0;
    }
    
    // String comparison for non-numeric columns
    if (typeof valA === 'string' && typeof valB === 'string') {
      return employeeSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    
    return 0;
  });

  // Sıralama ikonu bileşeni
  const SortIcon = ({ column }) => {
    if (employeeSortColumn !== column) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return employeeSortDirection === 'asc' 
      ? <span className="ml-1 text-[hsl(var(--brand-700))]">↑</span>
      : <span className="ml-1 text-[hsl(var(--brand-700))]">↓</span>;
  };

  // Generic sort icon component for all tables
  const GenericSortIcon = ({ column, sortColumn, sortDirection, color = 'hsl(var(--brand-700))' }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-4 w-4 text-gray-400 inline" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className={`ml-1 h-4 w-4 inline`} style={{ color }} />
      : <ArrowDown className={`ml-1 h-4 w-4 inline`} style={{ color }} />;
  };

  // Günlük Mesai sıralama handler
  const handleAttendanceSort = (column) => {
    if (attendanceSortColumn === column) {
      setAttendanceSortDirection(attendanceSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setAttendanceSortColumn(column);
      setAttendanceSortDirection('asc');
    }
  };

  // Günlük Mesai verilerini sırala
  const sortedAttendanceData = [...filterByCompany(attendanceData)].sort((a, b) => {
    if (attendanceSortColumn === null) return 0;
    let valA, valB;
    switch (attendanceSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'tc_no':
        valA = a.tc_no || '';
        valB = b.tc_no || '';
        break;
      case 'phone':
        valA = a.phone || '';
        valB = b.phone || '';
        break;
      case 'status':
        valA = a.status || '';
        valB = b.status || '';
        break;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return attendanceSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Fazla Mesai sıralama handler
  const handleOvertimeSort = (column) => {
    if (overtimeSortColumn === column) {
      setOvertimeSortDirection(overtimeSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOvertimeSortColumn(column);
      setOvertimeSortDirection('asc');
    }
  };

  // Fazla Mesai verilerini sırala
  const sortedOvertimeData = [...filterByCompany(overtimeData)].sort((a, b) => {
    if (overtimeSortColumn === null) return 0;
    let valA, valB;
    switch (overtimeSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'weekday_total':
        valA = a.weekday_total || 0;
        valB = b.weekday_total || 0;
        return overtimeSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'holiday_total':
        valA = a.holiday_total || 0;
        valB = b.holiday_total || 0;
        return overtimeSortDirection === 'asc' ? valA - valB : valB - valA;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return overtimeSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Ek Kesintiler sıralama handler
  const handleDeductionsSort = (column) => {
    if (deductionsSortColumn === column) {
      setDeductionsSortDirection(deductionsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDeductionsSortColumn(column);
      setDeductionsSortDirection('asc');
    }
  };

  // Ek Kesintiler verilerini sırala
  const sortedDeductionsData = [...filterByCompany(extraDeductionsData)].sort((a, b) => {
    if (deductionsSortColumn === null) return 0;
    let valA, valB;
    switch (deductionsSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'icra':
        valA = parseFloat(a.extra_deductions?.icra) || 0;
        valB = parseFloat(b.extra_deductions?.icra) || 0;
        return deductionsSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'nafaka':
        valA = parseFloat(a.extra_deductions?.nafaka) || 0;
        valB = parseFloat(b.extra_deductions?.nafaka) || 0;
        return deductionsSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'avans':
        valA = parseFloat(a.extra_deductions?.avans) || 0;
        valB = parseFloat(b.extra_deductions?.avans) || 0;
        return deductionsSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'diger':
        valA = parseFloat(a.extra_deductions?.diger) || 0;
        valB = parseFloat(b.extra_deductions?.diger) || 0;
        return deductionsSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'total':
        const totalA = (parseFloat(a.extra_deductions?.icra) || 0) + 
                       (parseFloat(a.extra_deductions?.nafaka) || 0) + 
                       (parseFloat(a.extra_deductions?.avans) || 0) + 
                       (parseFloat(a.extra_deductions?.diger) || 0);
        const totalB = (parseFloat(b.extra_deductions?.icra) || 0) + 
                       (parseFloat(b.extra_deductions?.nafaka) || 0) + 
                       (parseFloat(b.extra_deductions?.avans) || 0) + 
                       (parseFloat(b.extra_deductions?.diger) || 0);
        return deductionsSortDirection === 'asc' ? totalA - totalB : totalB - totalA;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return deductionsSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Maaş Çizelgesi sıralama handler
  const handleSalarySort = (column) => {
    if (salarySortColumn === column) {
      setSalarySortDirection(salarySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSalarySortColumn(column);
      setSalarySortDirection('asc');
    }
  };

  // Maaş Çizelgesi verilerini sırala
  const sortedSalaryData = [...filterByCompany(salarySheetData)].sort((a, b) => {
    if (salarySortColumn === null) return 0;
    let valA, valB;
    switch (salarySortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'base_salary':
        valA = parseFloat(a.base_salary) || 0;
        valB = parseFloat(b.base_salary) || 0;
        return salarySortDirection === 'asc' ? valA - valB : valB - valA;
      case 'overtime_pay':
        valA = parseFloat(a.overtime_pay) || 0;
        valB = parseFloat(b.overtime_pay) || 0;
        return salarySortDirection === 'asc' ? valA - valB : valB - valA;
      case 'total_supports':
        valA = parseFloat(a.total_supports) || 0;
        valB = parseFloat(b.total_supports) || 0;
        return salarySortDirection === 'asc' ? valA - valB : valB - valA;
      case 'total_deductions':
        valA = parseFloat(a.total_deductions) || 0;
        valB = parseFloat(b.total_deductions) || 0;
        return salarySortDirection === 'asc' ? valA - valB : valB - valA;
      case 'net_salary':
        valA = parseFloat(a.net_salary) || 0;
        valB = parseFloat(b.net_salary) || 0;
        return salarySortDirection === 'asc' ? valA - valB : valB - valA;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return salarySortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // İzinler sıralama handler
  const handleLeavesSort = (column) => {
    if (leavesSortColumn === column) {
      setLeavesSortDirection(leavesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLeavesSortColumn(column);
      setLeavesSortDirection('asc');
    }
  };

  // İzinler verilerini sırala
  const sortedLeavesData = [...filterByCompany(yearlyData)].sort((a, b) => {
    if (leavesSortColumn === null) return 0;
    let valA, valB;
    switch (leavesSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'yillik_izin':
        valA = parseInt(a.leave_summary?.yillik_izin) || 0;
        valB = parseInt(b.leave_summary?.yillik_izin) || 0;
        return leavesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'mazeret_izni':
        valA = parseInt(a.leave_summary?.mazeret_izni) || 0;
        valB = parseInt(b.leave_summary?.mazeret_izni) || 0;
        return leavesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'ucretsiz_izin':
        valA = parseInt(a.leave_summary?.ucretsiz_izin) || 0;
        valB = parseInt(b.leave_summary?.ucretsiz_izin) || 0;
        return leavesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'raporlu':
        valA = parseInt(a.leave_summary?.raporlu) || 0;
        valB = parseInt(b.leave_summary?.raporlu) || 0;
        return leavesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'total':
        const totalA = (parseInt(a.leave_summary?.yillik_izin) || 0) + 
                       (parseInt(a.leave_summary?.mazeret_izni) || 0) + 
                       (parseInt(a.leave_summary?.ucretsiz_izin) || 0) + 
                       (parseInt(a.leave_summary?.raporlu) || 0);
        const totalB = (parseInt(b.leave_summary?.yillik_izin) || 0) + 
                       (parseInt(b.leave_summary?.mazeret_izni) || 0) + 
                       (parseInt(b.leave_summary?.ucretsiz_izin) || 0) + 
                       (parseInt(b.leave_summary?.raporlu) || 0);
        return leavesSortDirection === 'asc' ? totalA - totalB : totalB - totalA;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return leavesSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Avanslar sıralama handler
  const handleAdvancesSort = (column) => {
    if (advancesSortColumn === column) {
      setAdvancesSortDirection(advancesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setAdvancesSortColumn(column);
      setAdvancesSortDirection('asc');
    }
  };

  // Avanslar verilerini sırala
  const sortedAdvancesData = [...filterByCompany(advancesData)].sort((a, b) => {
    if (advancesSortColumn === null) return 0;
    let valA, valB;
    switch (advancesSortColumn) {
      case 'name':
        valA = a.name || '';
        valB = b.name || '';
        break;
      case 'branch':
        valA = a.branch_name || '';
        valB = b.branch_name || '';
        break;
      case 'position':
        valA = a.position_name || '';
        valB = b.position_name || '';
        break;
      case 'total_advance':
        valA = parseFloat(a.advance_summary?.total_advance) || 0;
        valB = parseFloat(b.advance_summary?.total_advance) || 0;
        return advancesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'total_paid':
        valA = parseFloat(a.advance_summary?.total_paid) || 0;
        valB = parseFloat(b.advance_summary?.total_paid) || 0;
        return advancesSortDirection === 'asc' ? valA - valB : valB - valA;
      case 'remaining':
        valA = parseFloat(a.advance_summary?.remaining) || 0;
        valB = parseFloat(b.advance_summary?.remaining) || 0;
        return advancesSortDirection === 'asc' ? valA - valB : valB - valA;
      default:
        return 0;
    }
    if (typeof valA === 'string' && typeof valB === 'string') {
      return advancesSortDirection === 'asc' 
        ? valA.localeCompare(valB, 'tr') 
        : valB.localeCompare(valA, 'tr');
    }
    return 0;
  });

  // Fetch attendance when date changes or tab switches
  useEffect(() => {
    if (activeTab === 'attendance') {
      fetchAttendance(attendanceDate);
      // Günlük mesai için de o ayın izin verilerini yükle
      const date = new Date(attendanceDate);
      fetchApprovedLeaves(date.getFullYear(), date.getMonth() + 1);
    }
  }, [activeTab, attendanceDate]);

  // Fetch missing attendance on component mount and tab changes
  useEffect(() => {
    fetchMissingAttendance();
  }, []);

  // Fetch monthly attendance when month changes or tab switches
  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyAttendance(monthlyDate.getFullYear(), monthlyDate.getMonth() + 1);
    }
  }, [activeTab, monthlyDate]);

  // Fetch yearly attendance when year changes or tab switches
  useEffect(() => {
    if (activeTab === 'leaves') {
      fetchYearlyAttendance(yearlyYear);
    }
  }, [activeTab, yearlyYear]);

  // İzinler (Yıllık Özet) Fonksiyonları
  const fetchYearlyAttendance = async (year) => {
    setYearlyLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/hr/attendance/yearly/${year}`);
      setYearlyData(response.data);
    } catch (error) {
      console.error('Yearly attendance fetch error:', error);
      toast.error('Yıllık izin verileri yüklenemedi');
    } finally {
      setYearlyLoading(false);
    }
  };

  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

  const statusLabels = {
    present: 'İşe Geldi',
    half_day: 'Yarım Gün',
    absent: 'Gelmedi',
    paid_leave: 'Ücretli İzinli',
    unpaid_leave: 'Ücretsiz İzinli',
    sick: 'Raporlu',
    annual: 'Yıllık İzin',
    holiday: 'Resmi Tatil',
    weekend: 'Hafta Sonu',
    holiday_overtime: 'Tatil Mesaisi'
  };

  const statusColors = {
    present: 'text-green-600',
    half_day: 'text-lime-600',
    absent: 'text-red-600',
    paid_leave: 'text-blue-600',
    unpaid_leave: 'text-cyan-600',
    sick: 'text-purple-600',
    annual: 'text-yellow-600',
    holiday: 'text-orange-600',
    weekend: 'text-pink-600',
    holiday_overtime: 'text-rose-600'
  };

  // Avanslar Fonksiyonları
  const fetchAdvances = async (year) => {
    setAdvancesLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/hr/advances/yearly/${year}`);
      setAdvancesData(response.data);
    } catch (error) {
      console.error('Advances fetch error:', error);
      // Veri yoksa boş array kullan
      setAdvancesData([]);
    } finally {
      setAdvancesLoading(false);
    }
  };

  // Fetch advances when year changes or tab switches
  useEffect(() => {
    if (activeTab === 'advances') {
      fetchAdvances(advancesYear);
    }
  }, [activeTab, advancesYear]);

  // Avans detaylarını getir
  const fetchAdvanceDetails = async (employeeId, year, month) => {
    setAdvanceDetailLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/advances/details/${employeeId}/${year}/${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAdvanceDetailData(response.data);
      setShowAdvanceDetailModal(true);
    } catch (error) {
      console.error('Advance details fetch error:', error);
      toast.error('Avans detayları yüklenemedi');
    } finally {
      setAdvanceDetailLoading(false);
    }
  };

  // Maaş Çizelgesi fonksiyonu
  const fetchSalarySheet = async (year, month) => {
    setSalarySheetLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Aylık mesai verisini al
      const attendanceResponse = await axios.get(`${backendUrl}/api/hr/attendance/monthly/${year}/${month}`);
      const monthlyAttendance = attendanceResponse.data;
      
      // Avans verilerini al
      const advancesResponse = await axios.get(`${backendUrl}/api/hr/advances/yearly/${year}`);
      const advancesYearData = advancesResponse.data;
      
      // Fazla mesai verilerini al
      let overtimeMap = {};
      try {
        const overtimeResponse = await axios.get(`${backendUrl}/api/hr/overtime/${year}/${month}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Her çalışan için toplam fazla mesai saatlerini hesapla
        overtimeResponse.data.forEach(record => {
          if (!overtimeMap[record.employee_id]) {
            overtimeMap[record.employee_id] = { weekday_hours: 0, holiday_hours: 0 };
          }
          if (record.type === 'weekday') {
            overtimeMap[record.employee_id].weekday_hours += record.hours || 0;
          } else if (record.type === 'holiday') {
            overtimeMap[record.employee_id].holiday_hours += record.hours || 0;
          }
        });
      } catch (err) {
        console.log('Overtime fetch skipped:', err);
      }
      
      // Ek kesintiler verisini al
      let extraDeductionsMap = {};
      let extraDeductionsBreakdown = {};
      try {
        const extraDeductionsResponse = await axios.get(`${backendUrl}/api/hr/extra-deductions/${year}/${month}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        extraDeductionsResponse.data.forEach(emp => {
          const sgkAmount = emp.sgk?.amount || 0;
          const damageAmount = emp.damage?.amount || 0;
          const otherAmount = emp.other?.amount || 0;
          extraDeductionsMap[emp.employee_id] = sgkAmount + damageAmount + otherAmount;
          extraDeductionsBreakdown[emp.employee_id] = { sgk: sgkAmount, damage: damageAmount, other: otherAmount };
        });
      } catch (err) {
        console.log('Extra deductions fetch skipped:', err);
      }
      
      // Ayın gün sayısını hesapla
      const daysInMonth = new Date(year, month, 0).getDate(); // month: 1-12, bu fonksiyon bir önceki ayın son gününü verir
      
      // Maaş çizelgesi verisi oluştur
      const salaryData = monthlyAttendance.map(emp => computeMonthlySalaryRow(emp, year, month, advancesYearData, overtimeMap, extraDeductionsBreakdown));
      
      setSalarySheetData(salaryData);
    } catch (error) {
      console.error('Salary sheet fetch error:', error);
      setSalarySheetData([]);
    } finally {
      setSalarySheetLoading(false);
    }
  };

  // Fetch salary sheet when month/year changes or tab switches
  useEffect(() => {
    if (activeTab === 'salary') {
      fetchSalarySheet(salarySheetYear, salarySheetMonth);
    }
  }, [activeTab, salarySheetYear, salarySheetMonth]);

  // Ek Kesintiler fetch
  useEffect(() => {
    if (activeTab === 'deductions') {
      fetchExtraDeductions(extraDeductionsYear, extraDeductionsMonth);
    }
  }, [activeTab, extraDeductionsYear, extraDeductionsMonth]);

  // Fazla mesai verilerini yükle
  useEffect(() => {
    if (activeTab === 'overtime') {
      fetchOvertimeRecords(overtimeYear, overtimeMonth);
    }
  }, [activeTab, overtimeYear, overtimeMonth]);

  const fetchExtraDeductions = async (year, month) => {
    setExtraDeductionsLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/extra-deductions/${year}/${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setExtraDeductionsData(response.data);
    } catch (error) {
      console.error('Extra deductions fetch error:', error);
      toast.error('Ek kesintiler yüklenemedi');
    } finally {
      setExtraDeductionsLoading(false);
    }
  };

  const handleAddDeduction = async () => {
    if (!selectedDeductionEmployee || !selectedDeductionType || !newDeduction.amount) {
      toast.error('Miktar zorunludur');
      return;
    }
    
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Add deduction
      await axios.post(`${backendUrl}/api/hr/extra-deductions`, {
        employee_id: selectedDeductionEmployee.employee_id,
        deduction_type: selectedDeductionType,
        amount: parseFloat(newDeduction.amount),
        description: newDeduction.description,
        year: extraDeductionsYear,
        month: extraDeductionsMonth
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Upload file if exists
      if (deductionFile) {
        const formData = new FormData();
        formData.append('file', deductionFile);
        formData.append('deduction_type', selectedDeductionType);
        formData.append('year', extraDeductionsYear);
        formData.append('month', extraDeductionsMonth);
        
        await axios.post(`${backendUrl}/api/hr/extra-deductions/${selectedDeductionEmployee.employee_id}/upload`, formData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Kesinti eklendi');
      setShowAddDeductionModal(false);
      setNewDeduction({ amount: '', description: '' });
      setDeductionFile(null);
      fetchExtraDeductions(extraDeductionsYear, extraDeductionsMonth);
    } catch (error) {
      console.error('Add deduction error:', error);
      toast.error('Kesinti eklenemedi');
    }
  };

  const fetchDeductionDetail = async (employeeId, deductionType) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(
        `${backendUrl}/api/hr/extra-deductions/detail/${employeeId}/${extraDeductionsYear}/${extraDeductionsMonth}/${deductionType}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDeductionDetailData(response.data);
      setShowDeductionDetailModal(true);
    } catch (error) {
      console.error('Deduction detail fetch error:', error);
      toast.error('Detaylar yüklenemedi');
    }
  };

  const openAddDeductionModal = (employee, deductionType) => {
    setSelectedDeductionEmployee(employee);
    setSelectedDeductionType(deductionType);
    setNewDeduction({ amount: '', description: '' });
    setDeductionFile(null);
    setShowAddDeductionModal(true);
  };

  // Fazla Mesai Fonksiyonları
  const fetchOvertimeRecords = async (year, month) => {
    setOvertimeLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/overtime/${year}/${month}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOvertimeData(response.data);
    } catch (error) {
      console.error('Overtime fetch error:', error);
      setOvertimeData([]);
    } finally {
      setOvertimeLoading(false);
    }
  };

  const handleAddOvertime = async () => {
    if (!newOvertime.employee_id || !newOvertime.date || !newOvertime.hours) {
      toast.error('Çalışan, tarih ve saat zorunludur');
      return;
    }
    
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Önce fazla mesai kaydını oluştur
      const response = await axios.post(`${backendUrl}/api/hr/overtime`, {
        employee_id: newOvertime.employee_id,
        overtime_date: newOvertime.date,
        overtime_type: newOvertime.type,
        hours: parseFloat(newOvertime.hours),
        description: newOvertime.description
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Belgeler varsa yükle
      if (newOvertime.documents.length > 0) {
        const formData = new FormData();
        newOvertime.documents.forEach(file => {
          formData.append('files', file);
        });
        formData.append('record_id', response.data.id);
        formData.append('record_type', 'overtime');
        
        await axios.post(`${backendUrl}/api/hr/overtime/documents`, formData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      
      toast.success('Fazla mesai kaydedildi');
      setShowAddOvertimeModal(false);
      setNewOvertime({ employee_id: '', date: '', type: 'weekday', hours: '', description: '', documents: [] });
      fetchOvertimeRecords(overtimeYear, overtimeMonth);
    } catch (error) {
      console.error('Add overtime error:', error);
      toast.error('Fazla mesai kaydedilemedi');
    }
  };

  // Avans Ekleme Fonksiyonu
  const handleAddAdvance = async () => {
    if (!newAdvance.employee_id || !newAdvance.amount || !newAdvance.given_date || !newAdvance.deduction_start_date) {
      toast.error('Çalışan, miktar, avans veriliş tarihi ve kesinti başlama tarihi zorunludur');
      return;
    }
    
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.post(`${backendUrl}/api/hr/advances`, {
        employee_id: newAdvance.employee_id,
        amount: parseFloat(newAdvance.amount),
        installments: parseInt(newAdvance.installments),
        given_date: newAdvance.given_date,               // Avans veriliş tarihi
        deduction_start_date: newAdvance.deduction_start_date,  // Kesinti başlama tarihi
        description: newAdvance.description
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Upload documents if any
      if (newAdvance.documents.length > 0 && response.data.id) {
        const formData = new FormData();
        newAdvance.documents.forEach(file => {
          formData.append('files', file);
        });
        formData.append('advance_id', response.data.id);
        
        try {
          await axios.post(`${backendUrl}/api/hr/advances/documents`, formData, {
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (docError) {
          console.error('Document upload error:', docError);
          toast.warning('Avans kaydedildi ancak belgeler yüklenemedi');
        }
      }
      
      toast.success('Avans kaydedildi');
      setShowAddAdvanceModal(false);
      setNewAdvance({ employee_id: '', amount: '', installments: 1, given_date: '', deduction_start_date: '', description: '', documents: [] });
      fetchAdvances(advancesYear);
    } catch (error) {
      console.error('Add advance error:', error);
      toast.error(error.response?.data?.detail || 'Avans kaydedilemedi');
    }
  };

  // İzin Ekle Fonksiyonu
  const handleAddLeave = async () => {
    if (!newLeave.employee_id) {
      toast.error('Çalışan seçiniz');
      return;
    }
    if (!newLeave.izinTuru) {
      toast.error('İzin türü seçiniz');
      return;
    }
    if (!newLeave.izinBaslangic) {
      toast.error('İzin başlangıç tarihi giriniz');
      return;
    }
    if (!newLeave.izinBitis) {
      toast.error('İzin bitiş tarihi giriniz');
      return;
    }
    if (newLeave.izinBitis < newLeave.izinBaslangic) {
      toast.error('Bitiş tarihi başlangıç tarihinden önce olamaz');
      return;
    }
    
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Belgeleri base64'e çevir
      const belgelerBase64 = [];
      for (const file of newLeave.belgeler) {
        const base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
        belgelerBase64.push({
          name: file.name,
          type: file.type,
          data: base64
        });
      }
      
      await axios.post(`${backendUrl}/api/hr/leaves`, {
        employee_id: newLeave.employee_id,
        leave_type: newLeave.izinTuru,
        start_date: newLeave.izinBaslangic,
        end_date: newLeave.izinBitis,
        description: newLeave.aciklama,
        documents: belgelerBase64
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('İzin kaydedildi');
      setShowAddLeaveModal(false);
      setNewLeave({ employee_id: '', izinTuru: '', izinBaslangic: '', izinBitis: '', aciklama: '', belgeler: [] });
      fetchYearlyAttendance(yearlyYear);
    } catch (error) {
      console.error('Add leave error:', error);
      toast.error(error.response?.data?.detail || 'İzin kaydedilemedi');
    }
  };

  const handleDeleteOvertime = (employeeId, recordId) => {
    // Şifre doğrulaması iste
    requestPasswordForDelete('overtime', { employeeId, recordId });
  };

  const executeDeleteOvertime = async (employeeId, recordId) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/hr/overtime/${employeeId}/${recordId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Fazla mesai kaydı silindi');
      fetchOvertimeRecords(overtimeYear, overtimeMonth);
    } catch (error) {
      console.error('Delete overtime error:', error);
      toast.error('Kayıt silinemedi');
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/professions`);
      setProfessions(response.data);
    } catch (error) {
      console.error('Professions fetch error:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/branches`);
      setBranches(response.data);
    } catch (error) {
      console.error('Branches fetch error:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmployees();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filterBranch, filterPosition, filterStatus, selectedCompany, showTerminated]);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      // Build supports array from selectedSupports state
      const supports = [];
      const today = new Date().toISOString().split('T')[0];
      
      if (selectedSupports.yemek_karti.enabled && parseFloat(selectedSupports.yemek_karti.amount) > 0) {
        supports.push({
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + '_yemek',
          support_type: 'yemek_karti',
          name: 'yemek_karti',
          description: 'Yemek Kartı',
          amount: parseFloat(selectedSupports.yemek_karti.amount),
          start_date: today,
          effective_date: today,
          is_active: true,
          enabled: true,
          history: [],
          created_at: today
        });
      }
      
      if (selectedSupports.yol_parasi.enabled && parseFloat(selectedSupports.yol_parasi.amount) > 0) {
        supports.push({
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + '_yol',
          support_type: 'yol_parasi',
          name: 'yol_parasi',
          description: 'Yol Parası',
          amount: parseFloat(selectedSupports.yol_parasi.amount),
          start_date: today,
          effective_date: today,
          is_active: true,
          enabled: true,
          history: [],
          created_at: today
        });
      }
      
      if (selectedSupports.saglik_sigortasi.enabled && parseFloat(selectedSupports.saglik_sigortasi.amount) > 0) {
        supports.push({
          id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + '_saglik',
          support_type: 'saglik_sigortasi',
          name: 'saglik_sigortasi',
          description: 'Sağlık Sigortası',
          amount: parseFloat(selectedSupports.saglik_sigortasi.amount),
          start_date: today,
          effective_date: today,
          is_active: true,
          enabled: true,
          history: [],
          created_at: today
        });
      }
      
      // Add subcontractor_id based on selected company
      const employeeData = {
        ...formData,
        supports: supports,
        subcontractor_id: selectedCompany === 'fortis' ? null : selectedCompany
      };
      await axios.post(`${backendUrl}/api/hr/employees`, employeeData);
      toast.success('Çalışan başarıyla eklendi');
      setShowAddModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Çalışan eklenirken hata oluştu'));
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    try {
      // Get existing supports to preserve history
      const existingSupports = selectedEmployee.supports || [];
      
      // Helper function to update or add support with history
      const updateSupport = (supportType, newAmount, enabled) => {
        const existing = existingSupports.find(s => 
          s.support_type === supportType || s.name === supportType || s.type === supportType
        );
        
        const today = new Date().toISOString().split('T')[0];
        
        // Calculate next month's first day for effective_date
        const now = new Date();
        let nextMonth;
        if (now.getMonth() === 11) {
          nextMonth = new Date(now.getFullYear() + 1, 0, 1);
        } else {
          nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        }
        const effectiveDate = nextMonth.toISOString().split('T')[0];
        
        if (existing) {
          // Update existing support - add to history
          const history = existing.history || [];
          const currentAmount = existing.amount || 0;
          
          // Only add to history if amount changed
          if (currentAmount !== newAmount && enabled) {
            history.push({
              amount: currentAmount,
              start_date: existing.start_date || existing.effective_date || today,
              end_date: today,
              changed_at: today
            });
          }
          
          return {
            ...existing,
            amount: enabled ? newAmount : 0,
            effective_date: effectiveDate,
            is_active: enabled,
            enabled: enabled,
            history: history,
            updated_at: today
          };
        } else if (enabled && newAmount > 0) {
          // Create new support
          return {
            id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
            support_type: supportType,
            name: supportType,
            description: supportType === 'yemek_karti' ? 'Yemek Kartı' : 
                        supportType === 'yol_parasi' ? 'Yol Parası' : 'Sağlık Sigortası',
            amount: newAmount,
            start_date: today,
            effective_date: effectiveDate,
            is_active: true,
            enabled: true,
            history: [],
            created_at: today
          };
        }
        return null;
      };
      
      // Build supports array preserving history
      const supports = [];
      
      const yemekKarti = updateSupport(
        'yemek_karti', 
        parseFloat(selectedSupports.yemek_karti.amount) || 0,
        selectedSupports.yemek_karti.enabled
      );
      if (yemekKarti) supports.push(yemekKarti);
      
      const yolParasi = updateSupport(
        'yol_parasi',
        parseFloat(selectedSupports.yol_parasi.amount) || 0,
        selectedSupports.yol_parasi.enabled
      );
      if (yolParasi) supports.push(yolParasi);
      
      const saglikSig = updateSupport(
        'saglik_sigortasi',
        parseFloat(selectedSupports.saglik_sigortasi.amount) || 0,
        selectedSupports.saglik_sigortasi.enabled
      );
      if (saglikSig) supports.push(saglikSig);
      
      // Keep any other supports that weren't modified
      existingSupports.forEach(s => {
        const sType = s.support_type || s.name || s.type;
        if (!['yemek_karti', 'yol_parasi', 'saglik_sigortasi', 'yemek', 'yol', 'saglik'].includes(sType)) {
          supports.push(s);
        }
      });
      
      // Send formData with supports
      const updateData = { ...formData, supports };
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, updateData);
      toast.success('Çalışan başarıyla güncellendi');
      setShowEditModal(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Çalışan güncellenirken hata oluştu'));
    }
  };

  const handleDeleteEmployee = async () => {
    // Şifre doğrulaması iste
    requestPasswordForDelete('employee', selectedEmployee.id);
    setShowDeleteDialog(false);
  };

  const executeDeleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${backendUrl}/api/hr/employees/${employeeId}`);
      toast.success('Çalışan başarıyla silindi');
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Çalışan silinirken hata oluştu'));
    }
  };

  // Günlük Mesai Silme
  const executeDeleteAttendance = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/hr/attendance/${data.employeeId}/${data.date}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Günlük mesai kaydı silindi');
      fetchAttendance(attendanceDate);
    } catch (error) {
      toast.error('Kayıt silinemedi');
    }
  };

  // Aylık Mesai Silme (tüm ay kayıtlarını siler)
  const executeDeleteMonthly = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      // Aylık mesai için o ayın tüm günlük kayıtlarını silmek yerine sadece bilgi verelim
      toast.info('Aylık mesai özeti hesaplanan bir değerdir. Günlük mesai kayıtlarını silebilirsiniz.');
    } catch (error) {
      toast.error('İşlem yapılamadı');
    }
  };

  // Ek Kesinti Silme
  const executeDeleteDeduction = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/hr/extra-deductions/${data.employeeId}/${data.year}/${data.month}/${data.deductionType}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Ek kesinti silindi');
      fetchExtraDeductions(extraDeductionsYear, extraDeductionsMonth);
    } catch (error) {
      toast.error('Kesinti silinemedi');
    }
  };

  // Maaş Çizelgesi Silme (hesaplanan değerdir)
  const executeDeleteSalary = async (data) => {
    toast.info('Maaş çizelgesi hesaplanan bir değerdir. İlgili kayıtları (mesai, kesinti vb.) silebilirsiniz.');
  };

  // İzin Silme
  const executeDeleteLeave = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/hr/leaves/${data.requestId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('İzin kaydı silindi');
      fetchYearlyLeaves(yearlyYear);
    } catch (error) {
      toast.error('İzin silinemedi');
    }
  };

  // Avanslar - Çalışanı Sil
  const executeDeleteAdvance = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      // Çalışanı tamamen sil
      await axios.delete(`${backendUrl}/api/hr/employees/${data.employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Çalışan silindi');
      fetchAdvances(advancesYear);
      fetchEmployees();
    } catch (error) {
      toast.error('Çalışan silinemedi');
    }
  };

  // Aylık Mesai Kayıtlarını Silme (çalışanı sil)
  const executeDeleteMonthlyAttendance = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      // Çalışanı tamamen sil
      await axios.delete(`${backendUrl}/api/hr/employees/${data.employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Çalışan silindi');
      fetchMonthlyAttendance(data.year, data.month);
      fetchEmployees(); // Ana listeyi de güncelle
    } catch (error) {
      toast.error('Çalışan silinemedi');
    }
  };

  // Maaş Çizelgesi - Çalışanı Sil
  const executeDeleteSalarySheet = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      // Çalışanı tamamen sil
      await axios.delete(`${backendUrl}/api/hr/employees/${data.employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Çalışan silindi');
      fetchSalarySheet(salarySheetYear, salarySheetMonth);
      fetchEmployees();
    } catch (error) {
      toast.error('Çalışan silinemedi');
    }
  };

  // Yıllık İzinler - Çalışanı Sil
  const executeDeleteYearlyLeaves = async (data) => {
    try {
      const token = localStorage.getItem('fortis_token');
      // Çalışanı tamamen sil
      await axios.delete(`${backendUrl}/api/hr/employees/${data.employeeId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Çalışan silindi');
      fetchYearlyLeaves(yearlyYear);
      fetchEmployees();
    } catch (error) {
      toast.error('Çalışan silinemedi');
    }
  };

  // Maaş Güncelleme
  const handleSalaryUpdate = async () => {
    if (!newSalaryAmount || !selectedEmployee) {
      toast.error('Lütfen yeni maaş tutarını girin');
      return;
    }
    
    try {
      await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/salary`, {
        amount: parseFloat(newSalaryAmount),
        effective_date: new Date().toISOString().split('T')[0],
        notes: 'Maaş güncellendi'
      });
      
      toast.success('Maaş başarıyla güncellendi');
      setNewSalaryAmount('');
      
      // Refresh employee data
      const response = await axios.get(`${backendUrl}/api/hr/employees`);
      setEmployees(response.data);
      
      // Update selectedEmployee with new data
      const updatedEmployee = response.data.find(e => e.id === selectedEmployee.id);
      if (updatedEmployee) {
        setSelectedEmployee(updatedEmployee);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Maaş güncellenirken hata oluştu'));
    }
  };

  const openEditModal = (employee, viewOnly = false) => {
    setEditViewOnly(viewOnly);
    setSelectedEmployee(employee);
    setFormData({
      photo_url: employee.photo_url || '',
      tc_no: employee.tc_no,
      name: employee.name,
      email: employee.email || '',
      birth_date: employee.birth_date || '',
      gender: employee.gender || '',
      marital_status: employee.marital_status || '',
      children_count: employee.children_count || 0,
      education_level: employee.education_level || '',
      address: employee.address || '',
      phone: employee.phone,
      emergency_contact: employee.emergency_contact || '',
      hire_date: employee.hire_date,
      department: employee.department || '',
      profession: employee.profession || '',
      position_id: employee.position_id,
      branch_id: employee.branch_id,
      starting_salary: employee.starting_salary || '',
      current_salary: employee.current_salary || '',
      contract_type: employee.contract_type || '',
      employment_status: employee.employment_status,
      has_bonus: employee.has_bonus || false,
      bonus_type: employee.bonus_type || '',
      bonus_description: employee.bonus_description || '',
      // Sağlık Bilgileri
      blood_type: employee.blood_type || '',
      chronic_diseases: employee.chronic_diseases || '',
      allergies: employee.allergies || '',
      // Banka ve Sigorta Bilgileri
      bank_name: employee.bank_name || '',
      iban: employee.iban || '',
      insurance_number: employee.insurance_number || ''
    });
    
    // Load existing supports data into selectedSupports state
    const getExistingSupport = (supportName) => {
      if (!employee.supports || employee.supports.length === 0) return null;
      return employee.supports.find(s => 
        (s.name === supportName || s.support_type === supportName || s.type === supportName) && 
        (s.enabled !== false && s.is_active !== false)
      );
    };
    
    const yemekKarti = getExistingSupport('yemek_karti') || getExistingSupport('yemek');
    const yolParasi = getExistingSupport('yol_parasi') || getExistingSupport('yol');
    const saglikSigortasi = getExistingSupport('saglik_sigortasi') || getExistingSupport('saglik');
    
    setSelectedSupports({
      yemek_karti: { 
        enabled: !!yemekKarti, 
        amount: yemekKarti?.amount || '' 
      },
      yol_parasi: { 
        enabled: !!yolParasi, 
        amount: yolParasi?.amount || '' 
      },
      saglik_sigortasi: { 
        enabled: !!saglikSigortasi, 
        amount: saglikSigortasi?.amount || '' 
      }
    });
    
    setNewSalaryAmount('');
    setDetailTab('genel');
    setHistoryData(null);
    setShowEditModal(true);
    if (viewOnly) {
      fetchEmployeeHistory(employee);
    }
  };

  // İşten ayrılan çalışanın tüm çalışma dönemi geçmişini toplar (mesai, izin, avans, maaş)
  const fetchEmployeeHistory = async (employee) => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const empId = employee.id;

      // Çalışma dönemi: işe giriş -> çıkış (yoksa bugün)
      const startStr = (employee.start_date || employee.hire_date || '').slice(0, 10);
      const endStr = (employee.termination_date || new Date().toISOString()).slice(0, 10);
      const start = startStr ? new Date(startStr) : new Date();
      const end = new Date(endStr);
      const months = [];
      let cur = new Date(start.getFullYear(), start.getMonth(), 1);
      const last = new Date(end.getFullYear(), end.getMonth(), 1);
      while (cur <= last && months.length < 240) {
        months.push({ year: cur.getFullYear(), month: cur.getMonth() + 1 });
        cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      }
      const years = [...new Set(months.map(m => m.year))];

      // Yıl bazlı çağrılar (izin + avans)
      const yearlyAttByYear = {};
      const advancesByYear = {};
      await Promise.all(years.map(async (y) => {
        try {
          const r = await axios.get(`${backendUrl}/api/hr/attendance/yearly/${y}`, auth);
          yearlyAttByYear[y] = (r.data || []).find(e => e.employee_id === empId) || null;
        } catch (e) { yearlyAttByYear[y] = null; }
        try {
          const r = await axios.get(`${backendUrl}/api/hr/advances/yearly/${y}`, auth);
          advancesByYear[y] = r.data || [];
        } catch (e) { advancesByYear[y] = []; }
      }));

      // Ay bazlı çağrılar (mesai + fazla mesai + ek kesinti + maaş)
      const monthly = [];
      await Promise.all(months.map(async ({ year, month }) => {
        let empMonth = null, overtimeMap = {}, extraBreakdown = {};
        try {
          const r = await axios.get(`${backendUrl}/api/hr/attendance/monthly/${year}/${month}`, auth);
          empMonth = (r.data || []).find(e => e.employee_id === empId) || null;
        } catch (e) { /* skip */ }
        try {
          const r = await axios.get(`${backendUrl}/api/hr/overtime/${year}/${month}`, auth);
          (r.data || []).forEach(rec => {
            if (rec.employee_id !== empId) return;
            if (!overtimeMap[empId]) overtimeMap[empId] = { weekday_hours: 0, holiday_hours: 0 };
            if (rec.type === 'weekday') overtimeMap[empId].weekday_hours += rec.hours || 0;
            else if (rec.type === 'holiday') overtimeMap[empId].holiday_hours += rec.hours || 0;
          });
        } catch (e) { /* skip */ }
        try {
          const r = await axios.get(`${backendUrl}/api/hr/extra-deductions/${year}/${month}`, auth);
          (r.data || []).forEach(emp => {
            if (emp.employee_id !== empId) return;
            extraBreakdown[empId] = { sgk: emp.sgk?.amount || 0, damage: emp.damage?.amount || 0, other: emp.other?.amount || 0 };
          });
        } catch (e) { /* skip */ }

        const attendance = empMonth?.attendance || {};
        const counts = {};
        Object.keys(ATT_STATUS_LABELS).forEach(k => { counts[k] = 0; });
        Object.values(attendance).forEach(s => { if (counts[s] !== undefined) counts[s]++; });

        let salaryRow = null;
        if (empMonth) {
          salaryRow = computeMonthlySalaryRow(empMonth, year, month, advancesByYear[year] || [], overtimeMap, extraBreakdown);
        }
        const empAdv = (advancesByYear[year] || []).find(a => a.employee_id === empId);
        monthly.push({
          year, month,
          attendance,
          counts,
          overtime: overtimeMap[empId] || { weekday_hours: 0, holiday_hours: 0 },
          extra: extraBreakdown[empId] || { sgk: 0, damage: 0, other: 0 },
          advance_taken: empAdv?.advances?.[month] || 0,
          advance_deduction: empAdv?.deductions?.[month] || 0,
          salary: salaryRow
        });
      }));
      monthly.sort((a, b) => (a.year - b.year) || (a.month - b.month));

      // Maaş geçmişi (değişiklikler)
      let salaryHistory = [];
      try {
        const r = await axios.get(`${backendUrl}/api/hr/employees/${empId}/salary-history`, auth);
        salaryHistory = Array.isArray(r.data) ? r.data : (r.data?.salary_history || []);
      } catch (e) { /* skip */ }

      setHistoryData({
        employee, startStr, endStr, months, years,
        monthly, yearlyAttByYear, salaryHistory
      });
    } catch (error) {
      console.error('History fetch error:', error);
      toast.error('Geçmiş verileri yüklenemedi');
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Geçmiş sekmeleri Excel (CSV) dökümü
  const exportHistorySection = (section) => {
    if (!historyData) return;
    const nm = (historyData.employee.name || 'calisan').replace(/\s+/g, '_');
    const monthLabel = (m) => `${TR_MONTHS[m.month - 1]} ${m.year}`;
    if (section === 'mesai') {
      const rows = historyData.monthly.map(m => ({
        'Dönem': monthLabel(m),
        'Geldi': m.counts.present, 'Yarım Gün': m.counts.half_day, 'Gelmedi': m.counts.absent,
        'Ücretli İzin': m.counts.paid_leave, 'Ücretsiz İzin': m.counts.unpaid_leave,
        'Raporlu': m.counts.sick, 'Yıllık İzin': m.counts.annual, 'Resmi Tatil': m.counts.holiday,
        'Hafta Sonu': m.counts.weekend, 'Tatil Mesaisi': m.counts.holiday_overtime
      }));
      downloadCsv(rows, `${nm}_Mesai_Ozeti`);
    } else if (section === 'izinler') {
      const rows = historyData.years.map(y => {
        const e = historyData.yearlyAttByYear[y];
        const t = e?.totals || {};
        return {
          'Yıl': y, 'Yıllık İzin (kullanılan)': t.annual || 0, 'Ücretli İzin': t.paid_leave || 0,
          'Ücretsiz İzin': t.unpaid_leave || 0, 'Raporlu': t.sick || 0,
          'Yıllık İzin Hakkı': e?.annual_leave_entitlement ?? '-', 'Kalan Yıllık İzin': e?.remaining_annual_leave ?? '-'
        };
      });
      downloadCsv(rows, `${nm}_Izinler`);
    } else if (section === 'avanslar') {
      const rows = historyData.monthly.map(m => ({
        'Dönem': monthLabel(m), 'Alınan Avans': m.advance_taken, 'Avans Kesintisi': m.advance_deduction
      }));
      downloadCsv(rows, `${nm}_Avanslar`);
    } else if (section === 'maas') {
      const rows = historyData.monthly.filter(m => m.salary).map(m => {
        const s = m.salary;
        return {
          'Dönem': monthLabel(m),
          'Aylık Maaş': s.current_salary?.toFixed(2),
          'Anl. Yemek K.': s.agreed_meal_card?.toFixed(2),
          'Anl. Yol P.': s.agreed_travel?.toFixed(2),
          'Anl. Sağlık S.': s.agreed_health_insurance?.toFixed(2),
          'Anlaşılan Toplam': s.agreed_total?.toFixed(2),
          'Günlük Maaş': s.daily_salary?.toFixed(2),
          'Çalışılan Gün': s.worked_days?.toFixed(1),
          'Maaş Hakediş': s.maas_hakedis?.toFixed(2),
          'F.Mesai Normal': s.fazla_mesai_normal?.toFixed(2),
          'F.Mesai Tatil': s.fazla_mesai_tatil?.toFixed(2),
          'Yemek Kartı': s.yemek_karti?.toFixed(2),
          'Yol Parası': s.yol_parasi?.toFixed(2),
          'Sağlık Sig.': s.saglik_sigortasi?.toFixed(2),
          'Avans Kes.': s.avans_kesintisi?.toFixed(2),
          'SGK Kesintileri': s.ek_kesinti_sgk?.toFixed(2),
          'Zarar Kesintileri': s.ek_kesinti_zarar?.toFixed(2),
          'Diğer Kesintiler': s.ek_kesinti_diger?.toFixed(2),
          'Yatırılacak Maaş': s.yatirilacak_maas?.toFixed(2),
          'Toplam Hakediş': s.toplam_hakedis?.toFixed(2),
          'Toplam Kesintiler': s.toplam_kesintiler?.toFixed(2)
        };
      });
      downloadCsv(rows, `${nm}_Maas_Detaylari`);
    }
  };

  const fmtTL = (v) => (v || v === 0) ? `₺${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-';
  const monthName = (m) => `${TR_MONTHS[m.month - 1]} ${m.year}`;
  const statusBadgeColor = (s) => ({
    present: 'bg-green-100 text-green-800', half_day: 'bg-amber-100 text-amber-800',
    absent: 'bg-red-100 text-red-800', paid_leave: 'bg-blue-100 text-blue-800',
    unpaid_leave: 'bg-orange-100 text-orange-800', sick: 'bg-purple-100 text-purple-800',
    annual: 'bg-cyan-100 text-cyan-800', holiday: 'bg-slate-100 text-slate-700',
    weekend: 'bg-gray-100 text-gray-500', holiday_overtime: 'bg-rose-100 text-rose-800'
  }[s] || 'bg-gray-100 text-gray-600');

  const renderDetailHistory = () => {
    if (!historyData) return null;
    const { employee, startStr, endStr, monthly, years, yearlyAttByYear, salaryHistory } = historyData;

    const SummaryBar = () => (
      <div className="flex flex-wrap gap-4 items-center bg-[hsl(var(--muted))] rounded-lg px-4 py-3 mb-4 text-sm">
        <div><span className="text-[hsl(var(--muted-foreground))]">Şube: </span><b>{employee.branch_name || '-'}</b></div>
        <div><span className="text-[hsl(var(--muted-foreground))]">Görev: </span><b>{employee.position_name || '-'}</b></div>
        <div><span className="text-[hsl(var(--muted-foreground))]">İşe Giriş: </span><b>{startStr ? new Date(startStr).toLocaleDateString('tr-TR') : '-'}</b></div>
        <div><span className="text-[hsl(var(--muted-foreground))]">İşten Çıkış: </span><b className="text-red-600">{employee.termination_date ? new Date(employee.termination_date).toLocaleDateString('tr-TR') : '-'}</b></div>
        <div><span className="text-[hsl(var(--muted-foreground))]">Toplam Dönem: </span><b>{monthly.length} ay</b></div>
      </div>
    );

    if (detailTab === 'mesai') {
      return (
        <div data-testid="history-mesai">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Aylık Mesai Özeti</h3>
            <Button type="button" size="sm" variant="outline" onClick={() => exportHistorySection('mesai')} data-testid="history-export-mesai"><Download className="h-4 w-4 mr-1" />Excel İndir</Button>
          </div>
          <SummaryBar />
          <div className="overflow-x-auto border rounded-lg mb-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Dönem</TableHead><TableHead className="text-center">Geldi</TableHead>
                <TableHead className="text-center">Yarım</TableHead><TableHead className="text-center">Gelmedi</TableHead>
                <TableHead className="text-center">Ü.İzin</TableHead><TableHead className="text-center">Üs.İzin</TableHead>
                <TableHead className="text-center">Raporlu</TableHead><TableHead className="text-center">Yıllık</TableHead>
                <TableHead className="text-center">Tatil</TableHead><TableHead className="text-center">Hafta Sonu</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {monthly.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium whitespace-nowrap">{monthName(m)}</TableCell>
                    <TableCell className="text-center">{m.counts.present || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.half_day || '-'}</TableCell>
                    <TableCell className="text-center text-red-600">{m.counts.absent || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.paid_leave || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.unpaid_leave || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.sick || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.annual || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.holiday || '-'}</TableCell>
                    <TableCell className="text-center">{m.counts.weekend || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <h3 className="text-lg font-semibold mb-2">Günlük Mesai Dökümü</h3>
          <div className="space-y-3">
            {monthly.filter(m => Object.keys(m.attendance).length > 0).map((m, i) => (
              <div key={i} className="border rounded-lg p-3">
                <div className="font-medium mb-2">{monthName(m)}</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(m.attendance).sort((a, b) => new Date(a[0]) - new Date(b[0])).map(([day, st]) => (
                    <span key={day} className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${statusBadgeColor(st)}`} title={ATT_STATUS_LABELS[st] || st}>
                      {new Date(day).getDate()}: {ATT_STATUS_LABELS[st] || st}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {monthly.every(m => Object.keys(m.attendance).length === 0) && (
              <div className="text-center text-[hsl(var(--muted-foreground))] py-6">Günlük mesai kaydı bulunamadı.</div>
            )}
          </div>
        </div>
      );
    }

    if (detailTab === 'izinler') {
      return (
        <div data-testid="history-izinler">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">İzinler (Yıllık Özet)</h3>
            <Button type="button" size="sm" variant="outline" onClick={() => exportHistorySection('izinler')} data-testid="history-export-izinler"><Download className="h-4 w-4 mr-1" />Excel İndir</Button>
          </div>
          <SummaryBar />
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Yıl</TableHead><TableHead className="text-center">Yıllık İzin (Kullanılan)</TableHead>
                <TableHead className="text-center">Ücretli İzin</TableHead><TableHead className="text-center">Ücretsiz İzin</TableHead>
                <TableHead className="text-center">Raporlu</TableHead><TableHead className="text-center">Yıllık İzin Hakkı</TableHead>
                <TableHead className="text-center">Kalan Yıllık İzin</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {years.map((y) => {
                  const e = yearlyAttByYear[y]; const t = e?.totals || {};
                  return (
                    <TableRow key={y}>
                      <TableCell className="font-medium">{y}</TableCell>
                      <TableCell className="text-center text-cyan-700">{t.annual || 0}</TableCell>
                      <TableCell className="text-center">{t.paid_leave || 0}</TableCell>
                      <TableCell className="text-center">{t.unpaid_leave || 0}</TableCell>
                      <TableCell className="text-center">{t.sick || 0}</TableCell>
                      <TableCell className="text-center">{e?.annual_leave_entitlement ?? '-'}</TableCell>
                      <TableCell className="text-center font-medium">{e?.remaining_annual_leave ?? '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      );
    }

    if (detailTab === 'avanslar') {
      const totalTaken = monthly.reduce((s, m) => s + (m.advance_taken || 0), 0);
      const totalDeduct = monthly.reduce((s, m) => s + (m.advance_deduction || 0), 0);
      return (
        <div data-testid="history-avanslar">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Avanslar (Aylık)</h3>
            <Button type="button" size="sm" variant="outline" onClick={() => exportHistorySection('avanslar')} data-testid="history-export-avanslar"><Download className="h-4 w-4 mr-1" />Excel İndir</Button>
          </div>
          <SummaryBar />
          <div className="flex gap-4 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-sm"><span className="text-[hsl(var(--muted-foreground))]">Toplam Alınan Avans: </span><b className="text-blue-700">{fmtTL(totalTaken)}</b></div>
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm"><span className="text-[hsl(var(--muted-foreground))]">Toplam Kesinti: </span><b className="text-green-700">{fmtTL(totalDeduct)}</b></div>
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Dönem</TableHead><TableHead className="text-right">Alınan Avans</TableHead><TableHead className="text-right">Avans Kesintisi</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {monthly.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium whitespace-nowrap">{monthName(m)}</TableCell>
                    <TableCell className="text-right text-blue-700">{m.advance_taken ? fmtTL(m.advance_taken) : '-'}</TableCell>
                    <TableCell className="text-right text-green-700">{m.advance_deduction ? fmtTL(m.advance_deduction) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      );
    }

    if (detailTab === 'maas') {
      const sortedHistory = [...(salaryHistory || [])].sort((a, b) => new Date(a.effective_date || a.created_at) - new Date(b.effective_date || b.created_at));
      return (
        <div data-testid="history-maas">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">Maaş Detayları</h3>
            <Button type="button" size="sm" variant="outline" onClick={() => exportHistorySection('maas')} data-testid="history-export-maas"><Download className="h-4 w-4 mr-1" />Excel İndir</Button>
          </div>
          <SummaryBar />
          <h4 className="font-medium mb-2">Maaş Geçmişi / Değişiklikleri</h4>
          <div className="overflow-x-auto border rounded-lg mb-6">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Geçerlilik Tarihi</TableHead><TableHead className="text-right">Maaş</TableHead><TableHead>Açıklama</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {sortedHistory.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-[hsl(var(--muted-foreground))]">Maaş geçmişi yok.</TableCell></TableRow>
                ) : sortedHistory.map((h, i) => (
                  <TableRow key={i}>
                    <TableCell>{(h.effective_date || h.created_at) ? new Date(h.effective_date || h.created_at).toLocaleDateString('tr-TR') : '-'}</TableCell>
                    <TableCell className="text-right font-medium">{fmtTL(h.amount)}</TableCell>
                    <TableCell>{h.reason || (i === 0 ? 'Başlangıç Maaşı' : 'Zam')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <h4 className="font-medium mb-2">Aylık Maaş Çizelgesi</h4>
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="bg-emerald-100 font-bold min-w-[110px] whitespace-nowrap">Dönem</TableHead>
                <TableHead className="font-bold text-right min-w-[100px] whitespace-nowrap">Aylık Maaş</TableHead>
                <TableHead className="font-bold text-right min-w-[90px] bg-orange-50 whitespace-nowrap">Anl. Yemek K.</TableHead>
                <TableHead className="font-bold text-right min-w-[80px] bg-orange-50 whitespace-nowrap">Anl. Yol P.</TableHead>
                <TableHead className="font-bold text-right min-w-[90px] bg-orange-50 whitespace-nowrap">Anl. Sağlık S.</TableHead>
                <TableHead className="font-bold text-right min-w-[110px] bg-purple-100 whitespace-nowrap">Anlaşılan Toplam</TableHead>
                <TableHead className="font-bold text-right min-w-[90px] whitespace-nowrap">Günlük Maaş</TableHead>
                <TableHead className="font-bold text-center min-w-[90px] bg-green-100 whitespace-nowrap">Çalışılan Gün</TableHead>
                <TableHead className="font-bold text-right min-w-[100px] bg-green-200 whitespace-nowrap">Maaş Hakediş</TableHead>
                <TableHead className="font-bold text-right min-w-[100px] bg-blue-100 whitespace-nowrap">F.Mesai Normal</TableHead>
                <TableHead className="font-bold text-right min-w-[100px] bg-blue-100 whitespace-nowrap">F.Mesai Tatil</TableHead>
                <TableHead className="font-bold text-right min-w-[80px] bg-yellow-100 whitespace-nowrap">Yemek Kartı</TableHead>
                <TableHead className="font-bold text-right min-w-[80px] bg-yellow-100 whitespace-nowrap">Yol Parası</TableHead>
                <TableHead className="font-bold text-right min-w-[80px] bg-purple-100 whitespace-nowrap">Sağlık Sig.</TableHead>
                <TableHead className="font-bold text-right min-w-[90px] bg-red-100 whitespace-nowrap">Avans Kes.</TableHead>
                <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">SGK Kesintileri</TableHead>
                <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">Zarar Kesintileri</TableHead>
                <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">Diğer Kesintiler</TableHead>
                <TableHead className="font-bold text-right min-w-[110px] bg-cyan-100 whitespace-nowrap">Yatırılacak Maaş</TableHead>
                <TableHead className="font-bold text-right min-w-[130px] bg-emerald-200 whitespace-nowrap">Toplam Hakediş</TableHead>
                <TableHead className="font-bold text-right min-w-[130px] bg-red-200 whitespace-nowrap">Toplam Kesintiler</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {monthly.filter(m => m.salary).map((m, i) => {
                  const s = m.salary;
                  return (
                  <TableRow key={i}>
                    <TableCell className="font-medium whitespace-nowrap bg-emerald-50">{monthName(m)}</TableCell>
                    <TableCell className="text-right">{fmtTL(s.current_salary)}</TableCell>
                    <TableCell className="text-right bg-orange-50">{fmtTL(s.agreed_meal_card)}</TableCell>
                    <TableCell className="text-right bg-orange-50">{fmtTL(s.agreed_travel)}</TableCell>
                    <TableCell className="text-right bg-orange-50">{fmtTL(s.agreed_health_insurance)}</TableCell>
                    <TableCell className="text-right bg-purple-50 font-medium">{fmtTL(s.agreed_total)}</TableCell>
                    <TableCell className="text-right">{fmtTL(s.daily_salary)}</TableCell>
                    <TableCell className="text-center bg-green-50">{s.worked_days?.toFixed(1)}</TableCell>
                    <TableCell className="text-right bg-green-100 font-medium">{fmtTL(s.maas_hakedis)}</TableCell>
                    <TableCell className="text-right bg-blue-50">{fmtTL(s.fazla_mesai_normal)}</TableCell>
                    <TableCell className="text-right bg-blue-50">{fmtTL(s.fazla_mesai_tatil)}</TableCell>
                    <TableCell className="text-right bg-yellow-50">{fmtTL(s.yemek_karti)}</TableCell>
                    <TableCell className="text-right bg-yellow-50">{fmtTL(s.yol_parasi)}</TableCell>
                    <TableCell className="text-right bg-purple-50">{fmtTL(s.saglik_sigortasi)}</TableCell>
                    <TableCell className="text-right text-red-600 bg-red-50">{s.avans_kesintisi ? `-${fmtTL(s.avans_kesintisi)}` : '-'}</TableCell>
                    <TableCell className="text-right text-red-600 bg-red-50">{s.ek_kesinti_sgk ? `-${fmtTL(s.ek_kesinti_sgk)}` : '-'}</TableCell>
                    <TableCell className="text-right text-red-600 bg-red-50">{s.ek_kesinti_zarar ? `-${fmtTL(s.ek_kesinti_zarar)}` : '-'}</TableCell>
                    <TableCell className="text-right text-red-600 bg-red-50">{s.ek_kesinti_diger ? `-${fmtTL(s.ek_kesinti_diger)}` : '-'}</TableCell>
                    <TableCell className="text-right font-bold bg-cyan-50 text-cyan-700">{fmtTL(s.yatirilacak_maas)}</TableCell>
                    <TableCell className="text-right font-bold bg-emerald-50 text-emerald-700">{fmtTL(s.toplam_hakedis)}</TableCell>
                    <TableCell className="text-right font-bold bg-red-50 text-red-700">{fmtTL(s.toplam_kesintiler)}</TableCell>
                  </TableRow>
                  );
                })}
                {monthly.filter(m => m.salary).length === 0 && (
                  <TableRow><TableCell colSpan={21} className="text-center text-[hsl(var(--muted-foreground))]">Maaş çizelgesi verisi yok.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      );
    }
    return null;
  };


  const openDeleteDialog = (employee) => {
    setSelectedEmployee(employee);
    setShowDeleteDialog(true);
  };

  const resetForm = () => {
    setFormData({
      photo_url: '',
      tc_no: '',
      name: '',
      email: '',
      birth_date: '',
      gender: '',
      marital_status: '',
      children_count: 0,
      education_level: '',
      address: '',
      phone: '',
      emergency_contact: '',
      hire_date: '',
      department: '',
      profession: '',
      position_id: '',
      branch_id: '',
      starting_salary: '',
      contract_type: '',
      employment_status: 'active',
      has_bonus: false,
      bonus_type: '',
      bonus_description: '',
      // Sağlık Bilgileri
      blood_type: '',
      chronic_diseases: '',
      allergies: '',
      // Banka ve Sigorta Bilgileri
      iban: '',
      bank_name: '',
      insurance_number: ''
    });
    // Reset selectedSupports to default state
    setSelectedSupports({
      yemek_karti: { enabled: false, amount: '' },
      yol_parasi: { enabled: false, amount: '' },
      saglik_sigortasi: { enabled: false, amount: '' }
    });
    setSelectedEmployee(null);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterBranch('');
    setFilterPosition('');
    setFilterStatus('');
  };

  const openSalaryHistory = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const response = await axios.get(`${backendUrl}/api/hr/employees/${employee.id}/salary-history`);
      setSalaryHistory(response.data.salary_history || []);
      setShowSalaryHistoryModal(true);
    } catch (error) {
      toast.error('Maaş geçmişi yüklenemedi');
    }
  };

  const handleAddSalary = async () => {
    if (!newSalary.amount || !newSalary.effective_date) {
      toast.error('Maaş ve tarih gereklidir');
      return;
    }
    
    try {
      await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/salary`, newSalary);
      toast.success('Yeni maaş eklendi');
      setNewSalary({ amount: '', effective_date: '', reason: '' });
      // Refresh salary history
      const response = await axios.get(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/salary-history`);
      setSalaryHistory(response.data.salary_history || []);
      fetchEmployees();
    } catch (error) {
      toast.error('Maaş eklenemedi');
    }
  };

  const openDocumentsModal = async (employee) => {
    setSelectedEmployee(employee);
    // Fetch fresh employee data from API to get latest documents
    try {
      const response = await axios.get(`${backendUrl}/api/hr/employees/${employee.id}`);
      const freshDocs = response.data.documents || [];
      setEmployeeDocuments(freshDocs);
    } catch (error) {
      // Fallback to cached data if API fails
      const docs = employee.documents || [];
      setEmployeeDocuments(docs);
    }
    setShowDocumentsModal(true);
  };

  const handleAddDocument = async () => {
    if (!documentForm.file_url) {
      toast.error('Lütfen dosya seçiniz');
      return;
    }
    
    if (documentForm.type === 'custom' && !documentForm.custom_name) {
      toast.error('Özel belge için isim gereklidir');
      return;
    }
    
    try {
      await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/document`, {
        document_type: documentForm.type,
        document_name: documentForm.type === 'custom' ? documentForm.custom_name : null,
        file_url: documentForm.file_url,
        file_name: documentForm.file_name || 'document',
        upload_date: new Date().toISOString().split('T')[0]
      });
      
      toast.success('Belge eklendi');
      setDocumentForm({ type: '', custom_name: '', file_url: '', file_name: '' });
      
      // Refresh documents
      const response = await axios.get(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`);
      setEmployeeDocuments(response.data.documents || []);
      fetchEmployees();
    } catch (error) {
      toast.error('Belge eklenemedi');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/document/${docId}`);
      toast.success('Belge silindi');
      
      // Refresh documents
      const response = await axios.get(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`);
      setEmployeeDocuments(response.data.documents || []);
      fetchEmployees();
    } catch (error) {
      toast.error('Belge silinemedi');
    }
  };

  const getDocumentCount = (employee, docType) => {
    const docs = employee.documents || [];
    return docs.filter(d => d.document_type === docType).length;
  };

  // Zimmet (Asset) functions
  const openAssetsModal = async (employee) => {
    setSelectedEmployee(employee);
    setShowAssetsModal(true);
    setEmployeeAssetsArchive([]);
    
    // API'den zimmetleri yükle (depot_zimmetler + employee.assets)
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/employees/${employee.id}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeAssets(response.data || []);
    } catch (error) {
      console.error('Zimmetler yüklenirken hata:', error);
      // Fallback olarak employee.assets kullan
      setEmployeeAssets(employee.assets || []);
    }
    // Zimmet arşivini yükle (iade/red/geri alma geçmişi)
    try {
      const token = localStorage.getItem('fortis_token');
      const arsivRes = await axios.get(`${backendUrl}/api/hr/employees/${employee.id}/assets/archive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeAssetsArchive(arsivRes.data || []);
    } catch (error) {
      console.error('Zimmet arşivi yüklenirken hata:', error);
      setEmployeeAssetsArchive([]);
    }
  };

  const handleAddAsset = async () => {
    if (!newAsset.asset_name.trim()) {
      toast.error('Ürün adı gerekli');
      return;
    }
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/assets`, {
        ...newAsset,
        assignment_date: new Date().toISOString().split('T')[0]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeAssets([...employeeAssets, response.data]);
      setNewAsset({ asset_name: '', asset_description: '', serial_number: '', notes: '' });
      toast.success('Zimmet eklendi');
      fetchEmployees();
    } catch (error) {
      toast.error('Zimmet eklenemedi');
    }
  };

  const handleReturnAsset = async (assetId) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/assets/${assetId}`, {
        return_date: new Date().toISOString().split('T')[0],
        status: 'returned'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeAssets(employeeAssets.map(a => 
        a.id === assetId ? { ...a, status: 'returned', return_date: new Date().toISOString().split('T')[0] } : a
      ));
      toast.success('Zimmet iade edildi');
      fetchEmployees();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      const token = localStorage.getItem('fortis_token');
      await axios.delete(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeAssets(employeeAssets.filter(a => a.id !== assetId));
      toast.success('Zimmet silindi');
      fetchEmployees();
    } catch (error) {
      toast.error('Zimmet silinemedi');
    }
  };

  // Subcontractor functions
  const handleSaveSubcontractor = async () => {
    if (!newSubcontractor.name.trim()) {
      toast.error('Taşeron adı gerekli');
      return;
    }
    if (!newSubcontractor.branch_id) {
      toast.error('Şube seçimi gerekli');
      return;
    }
    try {
      if (newSubcontractor.id) {
        // Düzenleme
        const response = await axios.put(`${backendUrl}/api/subcontractors/${newSubcontractor.id}`, newSubcontractor);
        setSubcontractors(subcontractors.map(s => s.id === newSubcontractor.id ? response.data : s));
        toast.success(`Taşeron "${newSubcontractor.name}" güncellendi`);
        
        // Eğer detay modalı açıksa onu da güncelle
        if (selectedSubDetail && selectedSubDetail.id === newSubcontractor.id) {
          setSelectedSubDetail(response.data);
        }
      } else {
        // Yeni ekleme
        const response = await axios.post(`${backendUrl}/api/subcontractors`, newSubcontractor);
        setSubcontractors([...subcontractors, response.data]);
        const branchName = branches.find(b => b.id === newSubcontractor.branch_id)?.name || '';
        toast.success(`Taşeron "${newSubcontractor.name}" eklendi ve ${branchName} şubesine atandı`);
      }
      
      setNewSubcontractor({ 
        name: '', tax_number: '', phone: '', email: '', contact_person: '', 
        contacts: [{ name: '', role: '', phone: '', email: '' }], 
        branch_id: '', start_date: '', end_date: '', provided_service: '', custom_fields: [] 
      });
      setShowSubcontractorModal(false);
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteSubcontractor = async (subcontractorId) => {
    try {
      await axios.delete(`${backendUrl}/api/subcontractors/${subcontractorId}`);
      // Taşeronu listeden çıkar
      const deletedSub = subcontractors.find(s => s.id === subcontractorId);
      setSubcontractors(subcontractors.filter(s => s.id !== subcontractorId));
      // Silinen taşeronu arşive ekle
      if (deletedSub) {
        setArchivedSubcontractors([...archivedSubcontractors, {...deletedSub, is_deleted: true}]);
      }
      // Eğer silinen şirket seçiliyse, FORTIS'e dön
      if (selectedCompany === subcontractorId) {
        setSelectedCompany('fortis');
      }
      toast.success('Taşeron arşive taşındı');
    } catch (error) {
      toast.error('Taşeron arşivlenemedi');
    }
  };

  const handleRestoreSubcontractor = async (subcontractorId) => {
    try {
      await axios.put(`${backendUrl}/api/subcontractors/${subcontractorId}/restore`);
      // Taşeronu arşivden çıkar
      const restoredSub = archivedSubcontractors.find(s => s.id === subcontractorId);
      setArchivedSubcontractors(archivedSubcontractors.filter(s => s.id !== subcontractorId));
      // Aktif listeye ekle
      if (restoredSub) {
        const {is_deleted, deleted_at, deleted_by, ...cleanSub} = restoredSub;
        setSubcontractors([...subcontractors, cleanSub]);
      }
      toast.success('Taşeron geri alındı');
    } catch (error) {
      toast.error('Taşeron geri alınamadı');
    }
  };

  const fetchArchivedSubcontractors = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/subcontractors/archived`);
      setArchivedSubcontractors(response.data);
    } catch (error) {
      console.error('Arşivli taşeronlar yüklenemedi:', error);
    }
  };

  const getAssetCount = (employee) => {
    const assets = employee.assets || [];
    return assets.filter(a => a.status === 'assigned').length;
  };

  // Bordrolar Fonksiyonları
  const openPayrollsModal = (employee) => {
    setSelectedEmployee(employee);
    setPayrolls(employee.payrolls || []);
    setNewPayroll({ month: '', year: new Date().getFullYear(), file_name: '', file_url: '' });
    setShowPayrollsModal(true);
  };

  const handleAddPayroll = async () => {
    if (!newPayroll.month || !newPayroll.file_url || !newPayroll.file_name) {
      toast.error('Lütfen ay, dosya adı ve dosya seçin');
      return;
    }
    const updatedPayrolls = [...payrolls, { 
      id: Date.now().toString(), 
      ...newPayroll,
      uploaded_at: new Date().toISOString()
    }];
    try {
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, {
        payrolls: updatedPayrolls
      });
      setPayrolls(updatedPayrolls);
      setNewPayroll({ month: '', year: new Date().getFullYear(), file_name: '', file_url: '' });
      toast.success('Bordro eklendi');
      fetchEmployees();
    } catch (error) {
      toast.error('Bordro eklenemedi');
    }
  };

  const handleDeletePayroll = async (payrollId) => {
    const updatedPayrolls = payrolls.filter(p => p.id !== payrollId);
    try {
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, {
        payrolls: updatedPayrolls
      });
      setPayrolls(updatedPayrolls);
      toast.success('Bordro silindi');
      fetchEmployees();
    } catch (error) {
      toast.error('Bordro silinemedi');
    }
  };

  // Evraklar Fonksiyonları
  const documentCategories = [
    { key: 'izin_talepleri', label: 'İzin Talepleri' },
    { key: 'raporlar', label: 'Raporlar' },
    { key: 'savunmalar', label: 'Savunmalar' },
    { key: 'sikayetler', label: 'Şikayetler' },
    { key: 'tutanaklar', label: 'Tutanaklar' },
    { key: 'avanslar', label: 'Avanslar' },
    { key: 'kesintiler', label: 'Kesintiler' },
    { key: 'fazla_mesai', label: 'Fazla Mesai' },
    { key: 'ik_belge_talepleri', label: 'İK Belge Talepleri' }
  ];

  const openDocumentsDetailModal = async (employee) => {
    setSelectedEmployee(employee);
    // Kesinti belgelerini yükle
    const kesintiBelgeleri = [];
    const extraDeductions = employee.extra_deductions || {};
    Object.entries(extraDeductions).forEach(([year, yearData]) => {
      Object.entries(yearData).forEach(([month, monthData]) => {
        Object.entries(monthData).forEach(([type, typeData]) => {
          if (typeData.documents && typeData.documents.length > 0) {
            typeData.documents.forEach(doc => {
              kesintiBelgeleri.push({
                id: `${year}-${month}-${type}-${doc.uploaded_at}`,
                name: doc.filename,
                file_url: doc.file_url || (doc.data ? `data:${doc.content_type};base64,${doc.data}` : null),
                uploaded_at: doc.uploaded_at,
                deduction_type: type === 'sgk' ? 'SGK Kesintisi' : type === 'damage' ? 'Zarar Kesintisi' : 'Diğer Kesinti',
                amount: typeData.amount,
                month: month,
                year: year
              });
            });
          }
        });
      });
    });
    
    // Fazla mesai taleplerini yükle (onaylanmış olanlar)
    let fazlaMesaiTalepleri = [];
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await axios.get(`${backendUrl}/api/hr/employee-overtime-requests/${employee.user_id || employee.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fazlaMesaiTalepleri = response.data || [];
    } catch (err) {
      console.log('Fazla mesai talepleri yüklenemedi:', err);
    }
    
    // İK belge talepleri (tutanak/savunma vb. - istek + cevap arşivi)
    let ikBelgeTalepleri = [];
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await axios.get(`${backendUrl}/api/hr/employees/${employee.id}/document-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      ikBelgeTalepleri = res.data || [];
    } catch (err) {
      console.log('İK belge talepleri yüklenemedi:', err);
    }
    
    // Taşınmış (gerçek taşıma ile başka taba alınmış) kayıtların id'leri
    const movedOut = new Set(employee.documents_detail?._moved_out || []);

    setDocumentsDetail({
      ...(employee.documents_detail || {}),
      izin_talepleri: employee.documents_detail?.izin_talepleri || [],
      raporlar: employee.documents_detail?.raporlar || [],
      savunmalar: employee.documents_detail?.savunmalar || [],
      sikayetler: employee.documents_detail?.sikayetler || [],
      tutanaklar: employee.documents_detail?.tutanaklar || [],
      avanslar: employee.documents_detail?.avanslar || [],
      kesintiler: kesintiBelgeleri.filter(d => !movedOut.has(d.id)),
      fazla_mesai: fazlaMesaiTalepleri.filter(d => !movedOut.has(d.id)),
      ik_belge_talepleri: ikBelgeTalepleri.filter(d => !movedOut.has(d.id)),
      _moved_out: employee.documents_detail?._moved_out || []
    });
    setActiveDocCategory('izin_talepleri');
    setNewDocumentDetail({ name: '', file_url: '' });
    setShowDocumentsDetailModal(true);
    // Load document reminders
    loadDocumentReminders(employee.id);
  };

  const handleAddDocumentDetail = async () => {
    if (!newDocumentDetail.name || !newDocumentDetail.file_url) {
      toast.error('Lütfen belge adı ve dosya seçin');
      return;
    }
    const updatedDocs = {
      ...documentsDetail,
      [activeDocCategory]: [
        ...(documentsDetail[activeDocCategory] || []),
        { 
          id: Date.now().toString(), 
          ...newDocumentDetail,
          uploaded_at: new Date().toISOString()
        }
      ]
    };
    try {
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, {
        documents_detail: updatedDocs
      });
      setDocumentsDetail(updatedDocs);
      setNewDocumentDetail({ name: '', file_url: '' });
      toast.success('Belge eklendi');
      fetchEmployees();
    } catch (error) {
      toast.error('Belge eklenemedi');
    }
  };

  const handleDeleteDocumentDetail = async (docId) => {
    const updatedDocs = {
      ...documentsDetail,
      [activeDocCategory]: documentsDetail[activeDocCategory].filter(d => d.id !== docId)
    };
    try {
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, {
        documents_detail: updatedDocs
      });
      setDocumentsDetail(updatedDocs);
      toast.success('Belge silindi');
      fetchEmployees();
    } catch (error) {
      toast.error('Belge silinemedi');
    }
  };

  // ===== Belge Taşıma (gerçek taşıma) =====
  const MOVE_BACKED_CATEGORIES = ['izin_talepleri', 'raporlar', 'savunmalar', 'sikayetler', 'tutanaklar', 'avanslar'];
  const MOVE_TARGET_CATEGORIES = MOVE_BACKED_CATEGORIES; // hedef yalnızca belge-kategorileri (sistem üretimli tablar hedef olamaz)

  const openMoveDialog = (item, sourceKey) => setMoveDialog({ open: true, item, sourceKey });

  const buildMovedEntry = (item, sourceKey, targetKey) => {
    const label = documentCategories.find(c => c.key === sourceKey)?.label || sourceKey;
    let docs = [];
    if (sourceKey === 'ik_belge_talepleri') {
      docs = [
        ...((item.documents || []).map(d => ({ name: d.name, data: d.data }))),
        ...(((item.my_response?.documents) || []).map(d => ({ name: d.name, data: d.data })))
      ];
    } else if (sourceKey === 'fazla_mesai') {
      const fd = item.form_data?.form_data || item.form_data || {};
      if (fd.mesaiFormDosya) {
        const url = fd.mesaiFormDosya.startsWith('data:') ? fd.mesaiFormDosya : (fd.mesaiFormDosya.startsWith('/') ? fd.mesaiFormDosya : `data:application/octet-stream;base64,${fd.mesaiFormDosya}`);
        docs = [{ name: fd.mesaiFormDosyaAdi || 'Mesai Form Dosyası', data: url }];
      }
    } else if (item.documents?.length) {
      docs = item.documents.map(d => ({ name: d.name || d.filename, data: d.data || d.file_url || d.url }));
    } else if (item.file_url) {
      docs = [{ name: item.name, data: item.file_url }];
    }
    docs = docs.filter(d => d.data);
    return {
      id: `moved-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: item.title || item.name || (sourceKey === 'fazla_mesai' ? 'Fazla Mesai Belgesi' : 'Taşınan Belge'),
      uploaded_at: new Date().toISOString(),
      moved_from: label,
      description: item.description || item.my_response?.message || item.deduction_type || '',
      documents: docs,
      file_url: docs.length === 1 ? docs[0].data : undefined
    };
  };

  const handleMoveDocument = async (targetKey) => {
    const { item, sourceKey } = moveDialog;
    if (!item || !sourceKey || !targetKey) return;
    const entry = buildMovedEntry(item, sourceKey, targetKey);

    const updated = { ...documentsDetail };
    updated[targetKey] = [...(updated[targetKey] || []), entry];

    if (MOVE_BACKED_CATEGORIES.includes(sourceKey)) {
      updated[sourceKey] = (updated[sourceKey] || []).filter(d => d.id !== item.id);
    } else {
      // Sistem üretimli tablar: id'yi taşınmış listesine ekle + anlık UI'dan çıkar
      const movedOut = new Set(updated._moved_out || []);
      movedOut.add(item.id);
      updated._moved_out = [...movedOut];
      updated[sourceKey] = (updated[sourceKey] || []).filter(d => d.id !== item.id);
    }

    // PUT için sistem üretimli (hesaplanan) tabları çıkar — DB'ye base64 kopyalanmasın
    const toSave = { ...updated };
    delete toSave.kesintiler;
    delete toSave.fazla_mesai;
    delete toSave.ik_belge_talepleri;

    try {
      await axios.put(`${backendUrl}/api/hr/employees/${selectedEmployee.id}`, { documents_detail: toSave });
      setDocumentsDetail(updated);
      setMoveDialog({ open: false, item: null, sourceKey: null });
      const targetLabel = documentCategories.find(c => c.key === targetKey)?.label || targetKey;
      toast.success(`Belge "${targetLabel}" tabına taşındı`);
      fetchEmployees();
    } catch (error) {
      toast.error('Belge taşınamadı');
    }
  };

  // Belge Hatırlatıcı/Alarm Fonksiyonları
  const loadDocumentReminders = async (employeeId) => {
    try {
      const res = await axios.get(`${backendUrl}/api/hr/employees/${employeeId}/document-reminders`);
      setDocumentReminders(res.data || []);
    } catch (error) {
      console.error('Error loading reminders:', error);
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.document_name || !newReminder.due_date) {
      toast.error('Belge adı ve tarih zorunludur');
      return;
    }
    try {
      await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/document-reminders`, null, {
        params: {
          document_type: newReminder.document_type || 'diger',
          document_name: newReminder.document_name,
          due_date: newReminder.due_date,
          notes: newReminder.notes
        }
      });
      toast.success('Belge hatırlatıcısı oluşturuldu');
      setShowReminderModal(false);
      setNewReminder({ document_type: '', document_name: '', due_date: '', notes: '' });
      loadDocumentReminders(selectedEmployee.id);
    } catch (error) {
      toast.error('Hatırlatıcı oluşturulamadı');
    }
  };

  const handleCompleteReminder = async (reminderId, fileUrl) => {
    try {
      await axios.put(`${backendUrl}/api/hr/document-reminders/${reminderId}/complete`, null, {
        params: { file_url: fileUrl }
      });
      toast.success('Belge hatırlatıcısı tamamlandı');
      loadDocumentReminders(selectedEmployee.id);
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm('Bu hatırlatıcıyı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${backendUrl}/api/hr/document-reminders/${reminderId}`);
      toast.success('Hatırlatıcı silindi');
      loadDocumentReminders(selectedEmployee.id);
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  // İşten Ayrılma Fonksiyonları
  const handleRehire = (employee) => {
    if (window.confirm(`${employee.name} adlı çalışanı tekrar işe almak istediğinizden emin misiniz? (Tüm eski özlük belgeleri ve kişisel bilgiler yeni bir kayıt olarak aktarılacaktır)`)) {
      setFormData({
        photo_url: employee.photo_url || '',
        tc_no: employee.tc_no || '',
        name: employee.name || '',
        birth_date: employee.birth_date || '',
        email: employee.email || '',
        phone: employee.phone || '',
        address: employee.address || '',
        blood_type: employee.blood_type || '',
        emergency_contact_name: employee.emergency_contact_name || '',
        emergency_contact_phone: employee.emergency_contact_phone || '',
        profession_id: employee.profession_id || '',
        position_id: employee.position_id || '',
        starting_salary: '', // Yeni işe giriş olduğu için maaş sıfırlanır
        start_date: new Date().toISOString().split('T')[0], // Bugünün tarihi
        documents_detail: employee.documents_detail || {}, // Eski belgeleri aktar
      });
      
      setSelectedCompany(employee.subcontractor_id || 'fortis');
      
      setSelectedSupports({
        yemek_karti: { enabled: false, amount: '' },
        yol_parasi: { enabled: false, amount: '' },
        saglik_sigortasi: { enabled: false, amount: '' }
      });
      
      setShowAddModal(true);
    }
  };

  const openTerminationModal = async (employee) => {
    setSelectedEmployee(employee);
    // Çalışanın mevcut zimmetlerini yükle
    const assets = employee.assets || [];
    const assignedAssets = assets.filter(a => a.status === 'assigned');
    
    // Her zimmet için iade durumu state'ini oluştur
    const initialReturnStatus = {};
    assignedAssets.forEach(asset => {
      initialReturnStatus[asset.id] = {
        returned: false,
        condition: 'iyi',
        notes: ''
      };
    });
    setAssetReturnStatus(initialReturnStatus);
    
    setTerminationData({
      termination_date: new Date().toISOString().split('T')[0],
      termination_reason: '',
      termination_reason_other: '',
      exit_documents: [],
      asset_returns: []
    });
    setTerminationDocuments([]);
    setShowTerminationModal(true);
  };

  const handleTerminationDocUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const newDoc = {
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        file_name: file.name,
        file_url: reader.result,
        file_type: file.type,
        uploaded_at: new Date().toISOString()
      };
      setTerminationDocuments([...terminationDocuments, newDoc]);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeTerminationDoc = (docId) => {
    setTerminationDocuments(terminationDocuments.filter(d => d.id !== docId));
  };

  const handleTerminateEmployee = async () => {
    if (!terminationData.termination_date) {
      toast.error('İşten çıkış tarihi gerekli');
      return;
    }
    
    try {
      // Zimmet iade bilgilerini hazırla
      const assetReturns = Object.entries(assetReturnStatus).map(([assetId, status]) => ({
        asset_id: assetId,
        returned: status.returned,
        condition: status.condition,
        return_notes: status.notes,
        return_date: status.returned ? new Date().toISOString().split('T')[0] : null
      }));

      // Backend'e gönder
      await axios.post(`${backendUrl}/api/hr/employees/${selectedEmployee.id}/terminate`, {
        termination_date: terminationData.termination_date,
        termination_reason: terminationData.termination_reason,
        termination_reason_other: terminationData.termination_reason_other,
        exit_documents: terminationDocuments,
        asset_returns: assetReturns
      });
      
      toast.success('Çalışan işten ayrıldı olarak işaretlendi');
      setShowTerminationModal(false);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'İşlem sırasında hata oluştu'));
    }
  };

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="employee-list">
      {/* Company/Subcontractor Selection Bar */}
      <div className="flex items-center gap-4 p-4 bg-[hsl(var(--muted))] rounded-lg">
        <span className="text-sm font-medium">Şirket:</span>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCompany === 'fortis' && !showArchivedSubcontractors ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setSelectedCompany('fortis');
              setShowArchivedSubcontractors(false);
            }}
            className={selectedCompany === 'fortis' && !showArchivedSubcontractors ? 'bg-[hsl(var(--brand-700))]' : ''}
          >
            FORTİS
          </Button>
          {subcontractors.map(sub => (
            <div key={sub.id} className="relative group">
              <Button
                variant={selectedCompany === sub.id && !showArchivedSubcontractors ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setSelectedCompany(sub.id);
                  setShowArchivedSubcontractors(false);
                }}
                className={`pr-8 ${selectedCompany === sub.id && !showArchivedSubcontractors ? 'bg-[hsl(var(--brand-700))]' : ''}`}
              >
                {sub.name}
              </Button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`"${sub.name}" taşeron şirketini arşive taşımak istediğinizden emin misiniz?`)) {
                    handleDeleteSubcontractor(sub.id);
                  }
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Taşeronu Arşive Taşı"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          {!viewOnly && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSubcontractorModal(true)}
                className="border-dashed"
              >
                <Plus className="h-3 w-3 mr-1" />
                Taşeron Ekle
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSubcontractorListModal(true)}
              >
                <ClipboardList className="h-3 w-3 mr-1" />
                Taşeron Listesi
              </Button>
            </div>
          )}
          {/* Arşiv - Silinen Taşeronlar */}
          {archivedSubcontractors.length > 0 && (
            <Button
              variant={showArchivedSubcontractors ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowArchivedSubcontractors(!showArchivedSubcontractors)}
              className={showArchivedSubcontractors ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-600'}
            >
              <Archive className="h-3 w-3 mr-1" />
              Arşiv ({archivedSubcontractors.length})
            </Button>
          )}
        </div>
      </div>

      {/* Arşivlenmiş Taşeronlar Listesi */}
      {showArchivedSubcontractors && (
        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
          <h3 className="text-lg font-semibold text-amber-800 mb-4 flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Silinen Taşeronlar
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {archivedSubcontractors.map(sub => (
              <div key={sub.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                <div>
                  <span className="font-medium">{sub.name}</span>
                  {sub.deleted_at && (
                    <p className="text-xs text-gray-500">
                      Silindi: {new Date(sub.deleted_at).toLocaleDateString('tr-TR')}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRestoreSubcontractor(sub.id)}
                  className="border-green-300 text-green-600 hover:bg-green-50"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Geri Al
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">
            {showTerminated ? 'İşten Ayrılanlar' : 'Çalışanlar'}
          </h2>
          <p className="text-[hsl(var(--muted-foreground))]">
            {selectedCompany === 'fortis' ? 'FORTİS' : subcontractors.find(s => s.id === selectedCompany)?.name || ''} 
            {showTerminated ? ' - İşten çıkış yapan personeller' : ' çalışanlarını yönetin'}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex rounded-lg border border-[hsl(var(--border))] overflow-hidden">
            <Button
              onClick={() => setShowTerminated(false)}
              variant="ghost"
              className={`rounded-none border-0 ${!showTerminated ? 'bg-[hsl(var(--brand-700))] text-white hover:bg-[hsl(var(--brand-800))]' : 'hover:bg-[hsl(var(--muted))]'}`}
              data-testid="show-active-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              Çalışanlar
            </Button>
            <Button
              onClick={() => { setShowTerminated(true); setActiveTab('employees'); }}
              variant="ghost"
              className={`rounded-none border-0 border-l border-[hsl(var(--border))] ${showTerminated ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-600 hover:bg-red-50'}`}
              data-testid="show-terminated-btn"
            >
              <Users className="h-4 w-4 mr-2" />
              İşten Ayrılanlar
            </Button>
          </div>
          <Button
            onClick={exportToExcel}
            variant="outline"
            className="border-[hsl(var(--brand-700))] text-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-50))]"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Excel İndir
          </Button>
          {!viewOnly && (
            <Button
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="add-employee-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Yeni Çalışan
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-white rounded-lg border border-[hsl(var(--border))]">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
            <Input
              placeholder="Ad veya TC No ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              data-testid="employee-search"
            />
          </div>
        </div>
        <Select value={filterBranch} onValueChange={setFilterBranch}>
          <SelectTrigger className="w-[180px]" data-testid="filter-branch">
            <SelectValue placeholder="Şube Filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Şubeler</SelectItem>
            {branches.map((branch) => (
              <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterPosition} onValueChange={setFilterPosition}>
          <SelectTrigger className="w-[180px]" data-testid="filter-position">
            <SelectValue placeholder="Görev Filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Görevler</SelectItem>
            {positions.map((pos) => (
              <SelectItem key={pos.id} value={pos.id}>{pos.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]" data-testid="filter-status">
            <SelectValue placeholder="Durum Filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="on_leave">İzinli</SelectItem>
            <SelectItem value="terminated">Ayrılmış</SelectItem>
          </SelectContent>
        </Select>
        {(searchTerm || filterBranch || filterPosition || filterStatus) && (
          <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters">
            Filtreleri Temizle
          </Button>
        )}
      </div>

      {/* Eksik Mesai Uyarısı */}
      {!showTerminated && missingAttendance.count > 0 && showMissingWarning && (
        <Alert variant="destructive" className="mt-4 bg-amber-50 border-amber-300">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <AlertTitle className="text-amber-800 font-bold">Eksik Mesai Kaydı Uyarısı</AlertTitle>
          <AlertDescription className="text-amber-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Son 7 günde {missingAttendance.count} adet eksik mesai kaydı bulundu!</p>
                <p className="text-sm mt-1">
                  {missingAttendance.records.slice(0, 3).map((r, i) => (
                    <span key={i}>
                      {r.employee_name} ({new Date(r.date).toLocaleDateString('tr-TR')})
                      {i < Math.min(missingAttendance.records.length, 3) - 1 ? ', ' : ''}
                    </span>
                  ))}
                  {missingAttendance.count > 3 && <span> ve {missingAttendance.count - 3} kişi daha...</span>}
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => setActiveTab('attendance')}
                >
                  Mesai Girişine Git
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  className="text-amber-600 hover:bg-amber-100"
                  onClick={() => setShowMissingWarning(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Tab Seçimi - Çalışanlar / Günlük Mesai / Aylık Mesai (sadece aktif çalışanlar görünümünde) */}
      {!showTerminated && (
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-thin">
          <Button
            variant={activeTab === 'employees' ? 'default' : 'outline'}
            onClick={() => setActiveTab('employees')}
            className={`${activeTab === 'employees' ? 'bg-[hsl(var(--brand-700))]' : ''} shrink-0`}
            data-testid="tab-employees"
          >
            <Users className="h-4 w-4 mr-2" />
            Çalışanlar
          </Button>
          {!viewOnly && (
            <Button
              variant={activeTab === 'attendance' ? 'default' : 'outline'}
              onClick={() => setActiveTab('attendance')}
              className={`${activeTab === 'attendance' ? 'bg-orange-600 hover:bg-orange-700' : 'border-orange-300 text-orange-600'} shrink-0`}
              data-testid="tab-attendance"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Günlük Mesai
            </Button>
          )}
          <Button
            variant={activeTab === 'monthly' ? 'default' : 'outline'}
            onClick={() => setActiveTab('monthly')}
            className={`${activeTab === 'monthly' ? 'bg-indigo-600 hover:bg-indigo-700' : 'border-indigo-300 text-indigo-600'} shrink-0`}
            data-testid="tab-monthly"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Aylık Mesai
          </Button>
          <Button
            variant={activeTab === 'overtime' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overtime')}
            className={`${activeTab === 'overtime' ? 'bg-amber-600 hover:bg-amber-700' : 'border-amber-300 text-amber-600'} shrink-0`}
            data-testid="tab-overtime"
          >
            <Clock className="h-4 w-4 mr-2" />
            Fazla Mesai
          </Button>
          <Button
            variant={activeTab === 'deductions' ? 'default' : 'outline'}
            onClick={() => setActiveTab('deductions')}
            className={`${activeTab === 'deductions' ? 'bg-red-600 hover:bg-red-700' : 'border-red-300 text-red-600'} shrink-0`}
            data-testid="tab-deductions"
          >
            <FileText className="h-4 w-4 mr-2" />
            Ek Kesintiler
          </Button>
          <Button
            variant={activeTab === 'salary' ? 'default' : 'outline'}
            onClick={() => setActiveTab('salary')}
            className={`${activeTab === 'salary' ? 'bg-emerald-600 hover:bg-emerald-700' : 'border-emerald-300 text-emerald-600'} shrink-0`}
            data-testid="tab-salary"
          >
            <FileText className="h-4 w-4 mr-2" />
            Maaş Çizelgesi
          </Button>
          <Button
            variant={activeTab === 'leaves' ? 'default' : 'outline'}
            onClick={() => setActiveTab('leaves')}
            className={`${activeTab === 'leaves' ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-cyan-300 text-cyan-600'} shrink-0`}
            data-testid="tab-leaves"
          >
            <Calendar className="h-4 w-4 mr-2" />
            İzinler
          </Button>
          <Button
            variant={activeTab === 'advances' ? 'default' : 'outline'}
            onClick={() => setActiveTab('advances')}
            className={`${activeTab === 'advances' ? 'bg-pink-600 hover:bg-pink-700' : 'border-pink-300 text-pink-600'} shrink-0`}
            data-testid="tab-advances"
          >
            <FileText className="h-4 w-4 mr-2" />
            Avanslar
          </Button>
          <Button
            variant={activeTab === 'salaryChanges' ? 'default' : 'outline'}
            onClick={() => setActiveTab('salaryChanges')}
            className={`${activeTab === 'salaryChanges' ? 'bg-violet-600 hover:bg-violet-700' : 'border-violet-300 text-violet-600'} shrink-0`}
            data-testid="tab-salary-changes"
          >
            <FileText className="h-4 w-4 mr-2" />
            Maaş Değişiklikleri
          </Button>
        </div>
      )}

      {/* Günlük Mesai Tab İçeriği (sadece aktif çalışanlar görünümünde) */}
      {!showTerminated && activeTab === 'attendance' && (
        <div className="mt-4 space-y-4">
          {/* Tarih Seçimi */}
          <div className="flex items-center gap-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-orange-600" />
              <span className="font-medium text-orange-800">Tarih:</span>
            </div>
            <Input
              type="date"
              value={attendanceDate}
              onChange={(e) => setAttendanceDate(e.target.value)}
              className="w-48"
              data-testid="attendance-date"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = new Date(attendanceDate);
                  d.setDate(d.getDate() - 1);
                  setAttendanceDate(d.toISOString().split('T')[0]);
                }}
              >
                ← Önceki Gün
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAttendanceDate(new Date().toISOString().split('T')[0])}
              >
                Bugün
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const d = new Date(attendanceDate);
                  d.setDate(d.getDate() + 1);
                  setAttendanceDate(d.toISOString().split('T')[0]);
                }}
              >
                Sonraki Gün →
              </Button>
            </div>
            <div className="ml-auto text-lg font-bold text-orange-700">
              {new Date(attendanceDate).toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          {/* Mesai Tablosu */}
          <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-orange-100">
                  <TableHead className="w-12 text-center font-bold">No</TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('name')}
                  >
                    Ad Soyad
                    <GenericSortIcon column="name" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('branch')}
                  >
                    Şube
                    <GenericSortIcon column="branch" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('position')}
                  >
                    Görev
                    <GenericSortIcon column="position" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('tc_no')}
                  >
                    TC Kimlik No
                    <GenericSortIcon column="tc_no" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                  <TableHead 
                    className="font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('phone')}
                  >
                    Telefon
                    <GenericSortIcon column="phone" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                  <TableHead 
                    className="text-center font-bold cursor-pointer hover:bg-orange-200 select-none"
                    onClick={() => handleAttendanceSort('status')}
                  >
                    Mesai Durumu
                    <GenericSortIcon column="status" sortColumn={attendanceSortColumn} sortDirection={attendanceSortDirection} color="#ea580c" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attendanceLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedAttendanceData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Bu şirkette aktif çalışan bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAttendanceData.map((emp, idx) => {
                    const isBeforeHireDate = emp.hire_date && attendanceDate < emp.hire_date;
                    const isAfterTerminationDate = emp.termination_date && attendanceDate > emp.termination_date;
                    const isTerminated = emp.employment_status === 'terminated' || emp.termination_date;
                    const isDisabled = isBeforeHireDate || isAfterTerminationDate;
                    
                    // Günlük mesai için onaylı izin kontrolü
                    const hasLeave = hasApprovedLeaveForDate(emp.employee_id, attendanceDate);
                    const leaveDetail = hasLeave ? getApprovedLeaveDetailForDate(emp.employee_id, attendanceDate) : null;
                    const leaveStatus = leaveDetail?.hr_approval?.attendance_status || null;
                    
                    return (
                    <TableRow 
                      key={emp.employee_id} 
                      className={`hover:bg-orange-50 ${isDisabled ? 'opacity-50 bg-gray-100' : ''} ${isTerminated && !isAfterTerminationDate ? 'bg-gray-50' : ''} ${hasLeave ? 'bg-emerald-50 border-l-4 border-l-emerald-500' : ''}`}
                    >
                      <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {emp.name}
                          {isBeforeHireDate && (
                            <span className="text-xs text-red-500" title={`İşe giriş: ${emp.hire_date}`}>
                              (İşe giriş: {new Date(emp.hire_date).toLocaleDateString('tr-TR')})
                            </span>
                          )}
                          {isTerminated && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                              Ayrıldı {emp.termination_date && `(${new Date(emp.termination_date).toLocaleDateString('tr-TR')})`}
                            </Badge>
                          )}
                          {/* Onaylı izin göstergesi */}
                          {hasLeave && (
                            <Badge className="bg-emerald-500 text-white text-[10px] px-1.5 py-0 h-5 flex items-center gap-1">
                              <Check className="h-3 w-3" />
                              {getLeaveStatusName(leaveStatus)}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{emp.branch_name || '-'}</TableCell>
                      <TableCell>{emp.position_name || '-'}</TableCell>
                      <TableCell>{emp.tc_no}</TableCell>
                      <TableCell>{emp.phone}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-center gap-1">
                          {/* İşe Geç Geldi - bağımsız bilgi bayrağı */}
                          <button
                            onClick={() => handleLateToggle(emp.employee_id, emp.is_late, emp.hire_date)}
                            disabled={isDisabled}
                            className={`px-2 py-1 text-xs rounded border-2 transition-all flex items-center gap-1 ${
                              isDisabled
                                ? 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                                : emp.is_late
                                ? 'bg-orange-500 border-orange-500 text-white font-bold shadow-md'
                                : 'bg-white border-orange-300 text-orange-600 hover:bg-orange-50'
                            }`}
                            title={
                              isDisabled ? 'Bu tarihte kayıt girilemez'
                              : emp.is_late ? 'İşe Geç Geldi - Kaldırmak için tekrar tıklayın'
                              : 'İşe Geç Geldi olarak işaretle (diğer durumlarla birlikte seçilebilir)'
                            }
                            data-testid={`attendance-late-${emp.employee_id}`}
                          >
                            <Clock className="h-3 w-3" />
                            İşe Geç Geldi
                          </button>
                          {[
                            { value: 'present', label: 'İşe Geldi', color: 'green' },
                            { value: 'half_day', label: 'Yarım Gün', color: 'lime' },
                            { value: 'absent', label: 'Gelmedi', color: 'red' },
                            { value: 'paid_leave', label: 'Ücretli İzinli', color: 'blue' },
                            { value: 'unpaid_leave', label: 'Ücretsiz İzinli', color: 'cyan' },
                            { value: 'sick', label: 'Raporlu', color: 'purple' },
                            { value: 'annual', label: 'Yıllık İzin', color: 'yellow' },
                            { value: 'holiday', label: 'Resmi Tatil', color: 'orange' },
                            { value: 'weekend', label: 'Hafta Sonu', color: 'pink' },
                            { value: 'holiday_overtime', label: 'Tatil Mesaisi', color: 'rose' }
                          ].map((status) => (
                            <button
                              key={status.value}
                              onClick={() => {
                                // KURAL 2: Eğer otomatik giriş (onaylı izin) varsa manuel girişe izin verme
                                if (hasLeave) {
                                  toast.error(`⚠️ Bu tarihte otomatik "${getLeaveStatusName(leaveStatus)}" kaydı var. Manuel değişiklik yapılamaz.`);
                                  return;
                                }
                                
                                // KURAL 1: Toggle özelliği - aynı seçeneğe tıklanırsa seçimi kaldır
                                if (emp.status === status.value) {
                                  handleAttendanceChange(emp.employee_id, null, emp.hire_date);
                                } else {
                                  handleAttendanceChange(emp.employee_id, status.value, emp.hire_date);
                                }
                              }}
                              disabled={isDisabled || (hasLeave && leaveStatus !== status.value)}
                              className={`px-2 py-1 text-xs rounded border-2 transition-all ${
                                isDisabled || (hasLeave && leaveStatus !== status.value)
                                  ? 'bg-gray-200 border-gray-300 text-gray-400 cursor-not-allowed'
                                  : emp.status === status.value
                                  ? 'bg-red-500 border-red-500 text-white font-bold shadow-md'
                                  : hasLeave && leaveStatus === status.value
                                  ? 'bg-emerald-500 border-emerald-500 text-white font-bold shadow-md ring-2 ring-emerald-300 cursor-not-allowed'
                                  : `bg-white border-gray-300 text-gray-600 hover:border-${status.color}-400 hover:bg-${status.color}-50`
                              }`}
                              title={
                                isBeforeHireDate ? 'İşe giriş tarihinden önce kayıt girilemez' 
                                : isAfterTerminationDate ? 'İşten çıkış tarihinden sonra kayıt girilemez' 
                                : hasLeave ? `🔒 Otomatik giriş: ${getLeaveStatusName(leaveStatus)} (değiştirilemez)`
                                : emp.status === status.value ? `${status.label} - Kaldırmak için tekrar tıklayın`
                                : status.label
                              }
                              data-testid={`attendance-${status.value}-${emp.employee_id}`}
                            >
                              {status.label}
                              {hasLeave && leaveStatus === status.value && (
                                <Lock className="inline-block ml-1 h-3 w-3" />
                              )}
                            </button>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )})
                )}
              </TableBody>
            </Table>
          </div>

          {/* Özet */}
          {sortedAttendanceData.length > 0 && (
            <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                <span className="text-sm">İşe Geç Geldi: {filterByCompany(attendanceData).filter(e => e.is_late).length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                <span className="text-sm">İşe Geldi: {filterByCompany(attendanceData).filter(e => e.status === 'present').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-lime-500 rounded-full"></span>
                <span className="text-sm">Yarım Gün: {filterByCompany(attendanceData).filter(e => e.status === 'half_day').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                <span className="text-sm">Gelmedi: {filterByCompany(attendanceData).filter(e => e.status === 'absent').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                <span className="text-sm">Ücretli İzinli: {filterByCompany(attendanceData).filter(e => e.status === 'paid_leave').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-cyan-500 rounded-full"></span>
                <span className="text-sm">Ücretsiz İzinli: {filterByCompany(attendanceData).filter(e => e.status === 'unpaid_leave').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                <span className="text-sm">Raporlu: {filterByCompany(attendanceData).filter(e => e.status === 'sick').length}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                <span className="text-sm">Yıllık İzin: {filterByCompany(attendanceData).filter(e => e.status === 'annual').length}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-orange-50 rounded">
                <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                <span className="text-sm">Resmi Tatil: {filterByCompany(attendanceData).filter(e => e.status === 'holiday').length}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 rounded">
                <span className="w-2 h-2 rounded-full bg-pink-500"></span>
                <span className="text-sm">Hafta Sonu: {filterByCompany(attendanceData).filter(e => e.status === 'weekend').length}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-rose-50 rounded">
                <span className="w-2 h-2 rounded-full bg-rose-500"></span>
                <span className="text-sm">Tatil Mesaisi: {filterByCompany(attendanceData).filter(e => e.status === 'holiday_overtime').length}</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="w-3 h-3 bg-gray-300 rounded-full"></span>
                <span className="text-sm">Kayıt Yok: {filterByCompany(attendanceData).filter(e => !e.status).length}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Aylık Mesai Tab İçeriği */}
      {!showTerminated && activeTab === 'monthly' && (
        <div className="mt-4 space-y-4">
          {/* Ay Seçimi */}
          <div className="flex items-center gap-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              <span className="font-medium text-indigo-800">Ay:</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(monthlyDate);
                  newDate.setMonth(newDate.getMonth() - 1);
                  setMonthlyDate(newDate);
                }}
              >
                ← Önceki Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMonthlyDate(new Date())}
              >
                Bu Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newDate = new Date(monthlyDate);
                  newDate.setMonth(newDate.getMonth() + 1);
                  setMonthlyDate(newDate);
                }}
              >
                Sonraki Ay →
              </Button>
            </div>
            <div className="ml-auto text-lg font-bold text-indigo-700">
              {getMonthName(monthlyDate.getMonth())} {monthlyDate.getFullYear()}
            </div>
          </div>

          {/* Aylık Mesai Tablosu */}
          <div className="w-full overflow-hidden">
            <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
              <Table className="min-w-max">
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-indigo-100">
                  <TableHead className="sticky left-0 bg-indigo-100 z-20 min-w-[50px] text-center font-bold border-r whitespace-nowrap">No</TableHead>
                  <TableHead 
                    className="sticky left-[50px] bg-indigo-100 z-20 min-w-[150px] font-bold cursor-pointer hover:bg-indigo-200 border-r whitespace-nowrap"
                    onClick={() => handleMonthlySort('name')}
                  >
                    Ad Soyad {monthlySortColumn === 'name' && (monthlySortDirection === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  <TableHead className="bg-indigo-100 font-bold min-w-[150px] border-r whitespace-nowrap">Şube</TableHead>
                  <TableHead className="bg-indigo-100 font-bold min-w-[120px] border-r whitespace-nowrap">Görev</TableHead>
                  {Array.from({ length: getDaysInMonth(monthlyDate.getFullYear(), monthlyDate.getMonth()) }, (_, i) => i + 1).map(day => (
                    <TableHead 
                      key={day} 
                      className="text-center font-bold min-w-[40px] cursor-pointer hover:bg-indigo-200"
                      onClick={() => handleMonthlySort(day)}
                    >
                      {day}
                      {monthlySortColumn === day && (monthlySortDirection === 'asc' ? '↑' : '↓')}
                    </TableHead>
                  ))}
                  <TableHead className="bg-green-100 font-bold text-center min-w-[90px]">İşe Geldi</TableHead>
                  <TableHead className="bg-lime-100 font-bold text-center min-w-[80px]">Yarım Gün</TableHead>
                  <TableHead className="bg-red-100 font-bold text-center min-w-[80px]">Gelmedi</TableHead>
                  <TableHead className="bg-blue-100 font-bold text-center min-w-[100px]">Ücretli İzinli</TableHead>
                  <TableHead className="bg-cyan-100 font-bold text-center min-w-[110px]">Ücretsiz İzinli</TableHead>
                  <TableHead className="bg-purple-100 font-bold text-center min-w-[80px]">Raporlu</TableHead>
                  <TableHead className="bg-yellow-100 font-bold text-center min-w-[90px]">Yıllık İzin</TableHead>
                  <TableHead className="bg-orange-100 font-bold text-center min-w-[100px]">Resmi Tatil</TableHead>
                  <TableHead className="bg-pink-100 font-bold text-center min-w-[90px]">Hafta Sonu</TableHead>
                  <TableHead className="bg-rose-100 font-bold text-center min-w-[100px]">Tatil Mesaisi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyLoading ? (
                  <TableRow>
                    <TableCell colSpan={getDaysInMonth(monthlyDate.getFullYear(), monthlyDate.getMonth()) + 12} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-indigo-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedMonthlyData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={getDaysInMonth(monthlyDate.getFullYear(), monthlyDate.getMonth()) + 12} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Aktif çalışan bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedMonthlyData.map((emp, idx) => (
                    <TableRow key={emp.employee_id} className={`hover:bg-indigo-50 ${emp.termination_date ? 'bg-red-50/30' : ''}`}>
                      <TableCell className="sticky left-0 bg-white z-20 min-w-[50px] text-center font-medium border-r">{idx + 1}</TableCell>
                      <TableCell className="sticky left-[50px] bg-white z-20 min-w-[150px] font-medium border-r">
                        <div className="flex items-center gap-2 whitespace-nowrap">
                          {emp.name}
                          {emp.termination_date && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                              Ayrıldı
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="bg-white min-w-[150px] border-r whitespace-nowrap">{emp.branch_name || '-'}</TableCell>
                      <TableCell className="bg-white min-w-[120px] border-r whitespace-nowrap">{emp.position_name || '-'}</TableCell>
                      {Array.from({ length: getDaysInMonth(monthlyDate.getFullYear(), monthlyDate.getMonth()) }, (_, i) => i + 1).map(day => {
                        const status = emp.attendance?.[day];
                        const hasLeave = hasApprovedLeave(emp.employee_id, day);
                        const isLeaveStatus = ['paid_leave', 'unpaid_leave', 'annual'].includes(status);
                        const dayNote = getDayNote(emp.employee_id, day);
                        
                        return (
                          <TableCell
                            key={day}
                            className={`text-center p-1 relative group cursor-pointer ${dayNote ? 'bg-red-100 ring-1 ring-red-300' : 'hover:bg-gray-50'}`}
                            title={dayNote ? `Not: ${dayNote}` : 'Not eklemek için tıklayın'}
                            onClick={() => openNoteModal(emp.employee_id, emp.name, day)}
                            data-testid={`hr-monthly-cell-${emp.employee_id}-${day}`}
                          >
                            <div className="flex flex-col items-center">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor(status)}`}>
                                {getStatusShort(status)}
                              </span>
                              {/* Eye icon for approved leave */}
                              {isLeaveStatus && hasLeave && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); showLeaveDetail(emp.employee_id, day); }}
                                  className="mt-0.5 p-0.5 rounded hover:bg-cyan-100 transition-colors"
                                  title="Onaylı izin formunu görüntüle"
                                >
                                  <Eye className="h-3 w-3 text-cyan-600" />
                                </button>
                              )}
                              {dayNote && (
                                <AlertCircle className="h-3 w-3 text-red-600 mt-0.5" />
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                      {(() => {
                        const att = emp.attendance || {};
                        const counts = {
                          present: Object.values(att).filter(s => s === 'present').length,
                          half_day: Object.values(att).filter(s => s === 'half_day').length,
                          absent: Object.values(att).filter(s => s === 'absent').length,
                          paid_leave: Object.values(att).filter(s => s === 'paid_leave').length,
                          unpaid_leave: Object.values(att).filter(s => s === 'unpaid_leave').length,
                          sick: Object.values(att).filter(s => s === 'sick').length,
                          annual: Object.values(att).filter(s => s === 'annual').length,
                          holiday: Object.values(att).filter(s => s === 'holiday').length,
                          weekend: Object.values(att).filter(s => s === 'weekend').length,
                          holiday_overtime: Object.values(att).filter(s => s === 'holiday_overtime').length
                        };
                        return (
                          <>
                            <TableCell className="text-center font-bold bg-green-50 text-green-700">{counts.present || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-lime-50 text-lime-700">{counts.half_day || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-red-50 text-red-700">{counts.absent || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-blue-50 text-blue-700">{counts.paid_leave || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-cyan-50 text-cyan-700">{counts.unpaid_leave || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-purple-50 text-purple-700">{counts.sick || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-yellow-50 text-yellow-700">{counts.annual || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-orange-50 text-orange-700">{counts.holiday || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-pink-50 text-pink-700">{counts.weekend || '-'}</TableCell>
                            <TableCell className="text-center font-bold bg-rose-50 text-rose-700">{counts.holiday_overtime || '-'}</TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>

          {/* Lejant */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('present')}`}>G</span>
              <span className="text-sm">İşe Geldi</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('half_day')}`}>YG</span>
              <span className="text-sm">Yarım Gün</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('absent')}`}>X</span>
              <span className="text-sm">Gelmedi</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('paid_leave')}`}>Üİ</span>
              <span className="text-sm">Ücretli İzinli</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('unpaid_leave')}`}>Üsİ</span>
              <span className="text-sm">Ücretsiz İzinli</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('sick')}`}>R</span>
              <span className="text-sm">Raporlu</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('annual')}`}>Y</span>
              <span className="text-sm">Yıllık İzin</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor('holiday')}`}>T</span>
              <span className="text-sm">Resmi Tatil</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor(null)}`}>-</span>
              <span className="text-sm">Kayıt Yok</span>
            </div>
          </div>
        </div>
      )}

      {/* Fazla Mesai Tab İçeriği */}
      {!showTerminated && activeTab === 'overtime' && (
        <div className="mt-4 space-y-4">
          {/* Ay/Yıl Seçimi ve Mesai Ekle Butonu */}
          <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-800">Dönem:</span>
              </div>
              <Select
                value={String(overtimeMonth)}
                onValueChange={(value) => setOvertimeMonth(parseInt(value))}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Ay seçin" />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((month, idx) => (
                    <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOvertimeYear(overtimeYear - 1)}
                >
                  ← Önceki Yıl
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setOvertimeYear(new Date().getFullYear());
                    setOvertimeMonth(new Date().getMonth() + 1);
                  }}
                >
                  {overtimeYear}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOvertimeYear(overtimeYear + 1)}
                >
                  Sonraki Yıl →
                </Button>
              </div>
            </div>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => setShowAddOvertimeModal(true)}
            >
              <Plus className="h-4 w-4 mr-2" /> Fazla Mesai Ekle
            </Button>
          </div>

          {/* Fazla Mesai Tablosu */}
          {overtimeLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-100">
                    <TableHead className="font-bold text-center w-[60px]">No</TableHead>
                    <TableHead 
                      className="font-bold cursor-pointer hover:bg-amber-200 select-none"
                      onClick={() => handleOvertimeSort('name')}
                    >
                      Ad Soyad
                      <GenericSortIcon column="name" sortColumn={overtimeSortColumn} sortDirection={overtimeSortDirection} color="#d97706" />
                    </TableHead>
                    <TableHead 
                      className="font-bold cursor-pointer hover:bg-amber-200 select-none"
                      onClick={() => handleOvertimeSort('branch')}
                    >
                      Şube
                      <GenericSortIcon column="branch" sortColumn={overtimeSortColumn} sortDirection={overtimeSortDirection} color="#d97706" />
                    </TableHead>
                    <TableHead 
                      className="font-bold cursor-pointer hover:bg-amber-200 select-none"
                      onClick={() => handleOvertimeSort('position')}
                    >
                      Görev
                      <GenericSortIcon column="position" sortColumn={overtimeSortColumn} sortDirection={overtimeSortDirection} color="#d97706" />
                    </TableHead>
                    <TableHead className="font-bold text-center">Mesai Günü</TableHead>
                    <TableHead className="font-bold text-center bg-blue-100">Gün İçi Mesai (Saat)</TableHead>
                    <TableHead className="font-bold text-center bg-orange-100">Tatil Mesaisi (Saat)</TableHead>
                    <TableHead 
                      className="font-bold text-center bg-green-100 cursor-pointer hover:bg-green-200 select-none"
                      onClick={() => handleOvertimeSort('weekday_total')}
                    >
                      Toplam Gün İçi
                      <GenericSortIcon column="weekday_total" sortColumn={overtimeSortColumn} sortDirection={overtimeSortDirection} color="#16a34a" />
                    </TableHead>
                    <TableHead 
                      className="font-bold text-center bg-red-100 cursor-pointer hover:bg-red-200 select-none"
                      onClick={() => handleOvertimeSort('holiday_total')}
                    >
                      Toplam Tatil
                      <GenericSortIcon column="holiday_total" sortColumn={overtimeSortColumn} sortDirection={overtimeSortDirection} color="#dc2626" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOvertimeData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <Clock className="h-12 w-12 mx-auto mb-4 text-amber-300" />
                        <p className="text-gray-500">Bu şirket için bu dönemde fazla mesai kaydı yok</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (() => {
                      // Çalışan bazında grupla
                      const groupedData = {};
                      sortedOvertimeData.forEach((record, idx) => {
                        if (!groupedData[record.employee_id]) {
                          groupedData[record.employee_id] = {
                            name: record.name,
                            branch_name: record.branch_name,
                            position: record.position,
                            records: [],
                            total_weekday: 0,
                            total_holiday: 0
                          };
                        }
                        groupedData[record.employee_id].records.push(record);
                        if (record.type === 'weekday') {
                          groupedData[record.employee_id].total_weekday += record.hours;
                        } else {
                          groupedData[record.employee_id].total_holiday += record.hours;
                        }
                      });
                      
                      let rowNum = 0;
                      return Object.entries(groupedData).flatMap(([empId, empData], empIdx) => {
                        rowNum++;
                        const recordCount = empData.records.length;
                        
                        return empData.records.map((record, recIdx) => {
                          const isFirstRow = recIdx === 0;
                          const isLastRow = recIdx === empData.records.length - 1;
                          
                          return (
                            <TableRow 
                              key={`${empId}-${record.id || recIdx}`}
                              className={`${isLastRow ? 'border-b-2 border-amber-300' : ''} ${empIdx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}`}
                            >
                              {isFirstRow && (
                                <TableCell className="text-center font-medium align-middle" rowSpan={recordCount}>{rowNum}</TableCell>
                              )}
                              {isFirstRow && (
                                <TableCell className="font-medium align-middle" rowSpan={recordCount}>{empData.name}</TableCell>
                              )}
                              {isFirstRow && (
                                <TableCell className="align-middle" rowSpan={recordCount}>{empData.branch_name}</TableCell>
                              )}
                              {isFirstRow && (
                                <TableCell className="align-middle" rowSpan={recordCount}>{empData.position}</TableCell>
                              )}
                              <TableCell className="text-center">
                                {new Date(record.date).toLocaleDateString('tr-TR')}
                              </TableCell>
                              <TableCell className="text-center bg-blue-50">
                                {record.type === 'weekday' ? (
                                  <span className="font-medium text-blue-700">{record.hours} saat</span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center bg-orange-50">
                                {record.type === 'holiday' ? (
                                  <span className="font-medium text-orange-700">{record.hours} saat</span>
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </TableCell>
                              {isFirstRow && (
                                <TableCell className="text-center bg-green-50 align-middle" rowSpan={recordCount}>
                                  <span className="font-bold text-green-700">{empData.total_weekday} saat</span>
                                </TableCell>
                              )}
                              {isFirstRow && (
                                <TableCell className="text-center bg-red-50 align-middle" rowSpan={recordCount}>
                                  <span className="font-bold text-red-700">{empData.total_holiday} saat</span>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        });
                      });
                    })()
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Ek Kesintiler Tab İçeriği */}
      {!showTerminated && activeTab === 'deductions' && (
        <div className="mt-4 space-y-4">
          {/* Ay/Yıl Seçimi */}
          <div className="flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-red-600" />
              <span className="font-medium text-red-800">Dönem:</span>
            </div>
            <Select
              value={String(extraDeductionsMonth)}
              onValueChange={(value) => setExtraDeductionsMonth(parseInt(value))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Ay seçin" />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((month, idx) => (
                  <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExtraDeductionsYear(extraDeductionsYear - 1)}
              >
                ← Önceki Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setExtraDeductionsYear(new Date().getFullYear());
                  setExtraDeductionsMonth(new Date().getMonth() + 1);
                }}
              >
                Bu Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExtraDeductionsYear(extraDeductionsYear + 1)}
              >
                Sonraki Yıl →
              </Button>
            </div>
            <div className="ml-auto text-lg font-bold text-red-700">
              {monthNames[extraDeductionsMonth - 1]} {extraDeductionsYear}
            </div>
          </div>

          {/* Ek Kesintiler Tablosu */}
          <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-red-100">
                  <TableHead className="font-bold text-center w-[50px]">No</TableHead>
                  <TableHead 
                    className="font-bold min-w-[150px] cursor-pointer hover:bg-red-200 select-none"
                    onClick={() => handleDeductionsSort('name')}
                  >
                    Ad Soyad
                    <GenericSortIcon column="name" sortColumn={deductionsSortColumn} sortDirection={deductionsSortDirection} color="#dc2626" />
                  </TableHead>
                  <TableHead 
                    className="font-bold min-w-[120px] cursor-pointer hover:bg-red-200 select-none"
                    onClick={() => handleDeductionsSort('branch')}
                  >
                    Şube
                    <GenericSortIcon column="branch" sortColumn={deductionsSortColumn} sortDirection={deductionsSortDirection} color="#dc2626" />
                  </TableHead>
                  <TableHead 
                    className="font-bold min-w-[120px] cursor-pointer hover:bg-red-200 select-none"
                    onClick={() => handleDeductionsSort('position')}
                  >
                    Görev
                    <GenericSortIcon column="position" sortColumn={deductionsSortColumn} sortDirection={deductionsSortDirection} color="#dc2626" />
                  </TableHead>
                  <TableHead className="font-bold text-center min-w-[180px] bg-blue-100">SGK Kesintileri</TableHead>
                  <TableHead className="font-bold text-center min-w-[180px] bg-orange-100">Zarar Kesintileri</TableHead>
                  <TableHead className="font-bold text-center min-w-[180px] bg-purple-100">Diğer Kesintiler</TableHead>
                  <TableHead 
                    className="font-bold text-center min-w-[120px] bg-red-200 cursor-pointer hover:bg-red-300 select-none"
                    onClick={() => handleDeductionsSort('total')}
                  >
                    Toplam
                    <GenericSortIcon column="total" sortColumn={deductionsSortColumn} sortDirection={deductionsSortDirection} color="#dc2626" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extraDeductionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-red-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedDeductionsData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Bu şirkette çalışan bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedDeductionsData.map((emp, idx) => (
                    <TableRow key={emp.employee_id} className="hover:bg-red-50">
                      <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell>{emp.branch_name}</TableCell>
                      <TableCell>{emp.position_name || emp.position || '-'}</TableCell>
                      
                      {/* SGK Kesintileri */}
                      <TableCell className="text-center bg-blue-50">
                        <div className="flex items-center justify-center gap-2">
                          {emp.sgk?.amount > 0 ? (
                            <>
                              <span className="font-medium text-blue-700">{emp.sgk.amount.toLocaleString('tr-TR')} ₺</span>
                              {emp.sgk?.documents?.length > 0 && (
                                <FileText className="h-4 w-4 text-blue-500" />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                                onClick={() => fetchDeductionDetail(emp.employee_id, 'sgk')}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 text-blue-600 border-blue-300 hover:bg-blue-100"
                            onClick={() => openAddDeductionModal(emp, 'sgk')}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      
                      {/* Zarar Kesintileri */}
                      <TableCell className="text-center bg-orange-50">
                        <div className="flex items-center justify-center gap-2">
                          {emp.damage?.amount > 0 ? (
                            <>
                              <span className="font-medium text-orange-700">{emp.damage.amount.toLocaleString('tr-TR')} ₺</span>
                              {emp.damage?.documents?.length > 0 && (
                                <FileText className="h-4 w-4 text-orange-500" />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-orange-600 hover:text-orange-800"
                                onClick={() => fetchDeductionDetail(emp.employee_id, 'damage')}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 text-orange-600 border-orange-300 hover:bg-orange-100"
                            onClick={() => openAddDeductionModal(emp, 'damage')}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      
                      {/* Diğer Kesintiler */}
                      <TableCell className="text-center bg-purple-50">
                        <div className="flex items-center justify-center gap-2">
                          {emp.other?.amount > 0 ? (
                            <>
                              <span className="font-medium text-purple-700">{emp.other.amount.toLocaleString('tr-TR')} ₺</span>
                              {emp.other?.documents?.length > 0 && (
                                <FileText className="h-4 w-4 text-purple-500" />
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-purple-600 hover:text-purple-800"
                                onClick={() => fetchDeductionDetail(emp.employee_id, 'other')}
                              >
                                <Eye className="h-3 w-3" />
                              </Button>
                            </>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 w-6 p-0 text-purple-600 border-purple-300 hover:bg-purple-100"
                            onClick={() => openAddDeductionModal(emp, 'other')}
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                      
                      {/* Toplam */}
                      <TableCell className="text-center bg-red-100">
                        <span className="font-bold text-red-700">
                          {((emp.sgk?.amount || 0) + (emp.damage?.amount || 0) + (emp.other?.amount || 0)).toLocaleString('tr-TR')} ₺
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Maaş Çizelgesi Tab İçeriği */}
      {!showTerminated && activeTab === 'salary' && (
        <div className="mt-4 space-y-4">
          {/* Ay/Yıl Seçimi */}
          <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-600" />
              <span className="font-medium text-emerald-800">Dönem:</span>
            </div>
            <Select
              value={String(salarySheetMonth)}
              onValueChange={(value) => setSalarySheetMonth(parseInt(value))}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Ay seçin" />
              </SelectTrigger>
              <SelectContent>
                {monthNames.map((month, idx) => (
                  <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSalarySheetYear(salarySheetYear - 1)}
              >
                ← Önceki Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSalarySheetYear(new Date().getFullYear());
                  setSalarySheetMonth(new Date().getMonth() + 1);
                }}
              >
                Bu Ay
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSalarySheetYear(salarySheetYear + 1)}
              >
                Sonraki Yıl →
              </Button>
            </div>
            <div className="ml-auto text-lg font-bold text-emerald-700">
              {monthNames[salarySheetMonth - 1]} {salarySheetYear}
            </div>
          </div>

          {/* Maaş Çizelgesi Tablosu */}
          <div className="w-full overflow-hidden">
            <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
              <Table className="min-w-max">
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-emerald-100">
                  <TableHead className="sticky left-0 bg-emerald-100 z-20 font-bold text-center min-w-[50px] border-r whitespace-nowrap">No</TableHead>
                  <TableHead 
                    className="sticky left-[50px] bg-emerald-100 z-20 font-bold min-w-[150px] border-r whitespace-nowrap cursor-pointer hover:bg-emerald-200 select-none"
                    onClick={() => handleSalarySort('name')}
                  >
                    Ad Soyad
                    <GenericSortIcon column="name" sortColumn={salarySortColumn} sortDirection={salarySortDirection} color="#059669" />
                  </TableHead>
                  <TableHead className="bg-emerald-100 font-bold min-w-[100px] border-r whitespace-nowrap">
                    İşe Giriş
                  </TableHead>
                  <TableHead 
                    className="bg-emerald-100 font-bold min-w-[150px] border-r whitespace-nowrap cursor-pointer hover:bg-emerald-200 select-none"
                    onClick={() => handleSalarySort('branch')}
                  >
                    Şube
                    <GenericSortIcon column="branch" sortColumn={salarySortColumn} sortDirection={salarySortDirection} color="#059669" />
                  </TableHead>
                  <TableHead 
                    className="bg-emerald-100 font-bold min-w-[120px] border-r whitespace-nowrap cursor-pointer hover:bg-emerald-200 select-none"
                    onClick={() => handleSalarySort('position')}
                  >
                    Görev
                    <GenericSortIcon column="position" sortColumn={salarySortColumn} sortDirection={salarySortDirection} color="#059669" />
                  </TableHead>
                  <TableHead 
                    className="font-bold text-right min-w-[100px] whitespace-nowrap cursor-pointer hover:bg-emerald-200 select-none"
                    onClick={() => handleSalarySort('base_salary')}
                  >
                    Aylık Maaş
                    <GenericSortIcon column="base_salary" sortColumn={salarySortColumn} sortDirection={salarySortDirection} color="#059669" />
                  </TableHead>
                  <TableHead className="font-bold text-right min-w-[90px] bg-orange-50 whitespace-nowrap">Anl. Yemek K.</TableHead>
                  <TableHead className="font-bold text-right min-w-[80px] bg-orange-50 whitespace-nowrap">Anl. Yol P.</TableHead>
                  <TableHead className="font-bold text-right min-w-[90px] bg-orange-50 whitespace-nowrap">Anl. Sağlık S.</TableHead>
                  <TableHead className="font-bold text-right min-w-[110px] bg-purple-100 whitespace-nowrap">Anlaşılan Toplam</TableHead>
                  <TableHead className="font-bold text-right min-w-[90px] whitespace-nowrap">Günlük Maaş</TableHead>
                  <TableHead className="font-bold text-center min-w-[90px] bg-green-100 whitespace-nowrap">Çalışılan Gün</TableHead>
                  <TableHead className="font-bold text-right min-w-[100px] bg-green-200 whitespace-nowrap">Maaş Hakediş</TableHead>
                  <TableHead className="font-bold text-right min-w-[100px] bg-blue-100 whitespace-nowrap">F.Mesai Normal</TableHead>
                  <TableHead className="font-bold text-right min-w-[100px] bg-blue-100 whitespace-nowrap">F.Mesai Tatil</TableHead>
                  <TableHead className="font-bold text-right min-w-[80px] bg-yellow-100 whitespace-nowrap">Yemek Kartı</TableHead>
                  <TableHead className="font-bold text-right min-w-[80px] bg-yellow-100 whitespace-nowrap">Yol Parası</TableHead>
                  <TableHead className="font-bold text-right min-w-[80px] bg-purple-100 whitespace-nowrap">Sağlık Sig.</TableHead>
                  <TableHead className="font-bold text-right min-w-[90px] bg-red-100 whitespace-nowrap">Avans Kes.</TableHead>
                  <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">SGK Kesintileri</TableHead>
                  <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">Zarar Kesintileri</TableHead>
                  <TableHead className="font-bold text-right min-w-[110px] bg-red-100 whitespace-nowrap">Diğer Kesintiler</TableHead>
                  <TableHead 
                    className="font-bold text-right min-w-[110px] bg-cyan-100 whitespace-nowrap cursor-pointer hover:bg-cyan-200 select-none"
                    onClick={() => handleSalarySort('net_salary')}
                  >
                    Yatırılacak Maaş
                    <GenericSortIcon column="net_salary" sortColumn={salarySortColumn} sortDirection={salarySortDirection} color="#0891b2" />
                  </TableHead>
                  <TableHead className="font-bold text-right min-w-[130px] bg-emerald-200">Toplam Hakediş</TableHead>
                  <TableHead className="font-bold text-right min-w-[130px] bg-red-200">Toplam Kesintiler</TableHead>
                  <TableHead className="font-bold text-center min-w-[70px] bg-gray-100">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {salarySheetLoading ? (
                  <TableRow>
                    <TableCell colSpan={26} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-emerald-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedSalaryData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={26} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Bu şirket için bu dönem veri bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSalaryData.map((emp, idx) => (
                    <TableRow key={emp.employee_id} className={`hover:bg-emerald-50 ${emp.termination_date ? 'bg-red-50/30' : ''}`}>
                      <TableCell className="sticky left-0 bg-white z-20 text-center font-medium border-r min-w-[50px]">{idx + 1}</TableCell>
                      <TableCell className="sticky left-[50px] bg-white z-20 font-medium border-r min-w-[150px]">
                        <div className="flex flex-col whitespace-nowrap">
                          <span>{emp.name}</span>
                          {emp.termination_date && (
                            <span className="text-xs text-red-600 font-normal">
                              Ayrıldı - {new Date(emp.termination_date).toLocaleDateString('tr-TR')}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="bg-white border-r min-w-[100px] whitespace-nowrap text-sm">
                        {emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('tr-TR') : '-'}
                      </TableCell>
                      <TableCell className="bg-white border-r min-w-[150px] whitespace-nowrap">{emp.branch_name || '-'}</TableCell>
                      <TableCell className="bg-white border-r min-w-[120px] whitespace-nowrap">{emp.position_name || '-'}</TableCell>
                      <TableCell className="text-right border-r font-medium whitespace-nowrap">{emp.current_salary?.toLocaleString('tr-TR')} ₺</TableCell>
                      <TableCell className="text-right border-r bg-orange-50/50 whitespace-nowrap">{emp.agreed_meal_card?.toLocaleString('tr-TR') || '-'} ₺</TableCell>
                      <TableCell className="text-right border-r bg-orange-50/50 whitespace-nowrap">{emp.agreed_travel?.toLocaleString('tr-TR') || '-'} ₺</TableCell>
                      <TableCell className="text-right border-r bg-orange-50/50 whitespace-nowrap">{emp.agreed_health_insurance?.toLocaleString('tr-TR') || '-'} ₺</TableCell>
                      <TableCell className="text-right border-r bg-purple-100/50 font-bold text-purple-700 whitespace-nowrap">{emp.agreed_total?.toLocaleString('tr-TR') || '-'} ₺</TableCell>
                      <TableCell className="text-right border-r whitespace-nowrap">{emp.daily_salary?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                      <TableCell className="text-center border-r bg-green-50">
                        <div className="text-sm whitespace-nowrap">
                          <span className="font-bold">{emp.worked_days}</span>
                          <div className="text-xs text-gray-500">
                            (G:{emp.present_days} YG:{emp.half_days} Üİ:{emp.paid_leave_days} R:{emp.sick_days} Y:{emp.annual_days} T:{emp.holiday_days})
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right border-r bg-green-100 font-bold text-green-700 whitespace-nowrap">
                        {emp.maas_hakedis?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺
                      </TableCell>
                      <TableCell className="text-right border-r bg-blue-50">
                        <div className="text-sm">
                          <span className="font-medium">{emp.fazla_mesai_normal?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</span>
                          {emp.fazla_mesai_saat_normal > 0 && (
                            <div className="text-xs text-blue-600">({emp.fazla_mesai_saat_normal} saat)</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right border-r bg-blue-50">
                        <div className="text-sm">
                          <span className="font-medium">{emp.fazla_mesai_tatil?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</span>
                          {emp.fazla_mesai_saat_tatil > 0 && (
                            <div className="text-xs text-blue-600">({emp.fazla_mesai_saat_tatil} saat)</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right border-r bg-yellow-50">{emp.yemek_karti?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                      <TableCell className="text-right border-r bg-yellow-50">{emp.yol_parasi?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                      <TableCell className="text-right border-r bg-purple-50">{emp.saglik_sigortasi?.toLocaleString('tr-TR')} ₺</TableCell>
                      <TableCell className="text-right border-r bg-red-50 text-red-600">-{emp.avans_kesintisi?.toLocaleString('tr-TR')} ₺</TableCell>
                      <TableCell className="text-right border-r bg-red-50 text-red-600">{emp.ek_kesinti_sgk ? `-${emp.ek_kesinti_sgk.toLocaleString('tr-TR')} ₺` : '-'}</TableCell>
                      <TableCell className="text-right border-r bg-red-50 text-red-600">{emp.ek_kesinti_zarar ? `-${emp.ek_kesinti_zarar.toLocaleString('tr-TR')} ₺` : '-'}</TableCell>
                      <TableCell className="text-right border-r bg-red-50 text-red-600">{emp.ek_kesinti_diger ? `-${emp.ek_kesinti_diger.toLocaleString('tr-TR')} ₺` : '-'}</TableCell>
                      <TableCell className="text-right border-r bg-cyan-50 font-medium">{emp.yatirilacak_maas?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                      <TableCell className="text-right border-r bg-emerald-100 font-bold text-emerald-700">{emp.toplam_hakedis?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                      <TableCell className="text-right border-r bg-red-100 font-bold text-red-700">{emp.toplam_kesintiler?.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} ₺</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>

          {/* Alt Bilgi */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Hesaplama Formülleri:</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-green-100 rounded">Günlük Maaş = Aylık Maaş / 30</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-100 rounded">F.Mesai Normal = (Günlük/7.5) × Saat × 1.5</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-blue-100 rounded">F.Mesai Tatil = (Günlük/7.5) × Saat × 2</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-yellow-100 rounded">Yemek = Aylık / 30 × Çalışma Günü</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-yellow-100 rounded">Yol = Aylık / 30 × Çalışma Günü</span>
            </div>
          </div>
        </div>
      )}

      {/* İzinler Tab İçeriği */}
      {!showTerminated && activeTab === 'leaves' && (
        <div className="mt-4 space-y-4">
          {/* Yıl Seçimi ve İzin Ekle Butonu */}
          <div className="flex items-center gap-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-600" />
              <span className="font-medium text-cyan-800">Yıl:</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setYearlyYear(yearlyYear - 1)}
              >
                ← Önceki Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setYearlyYear(new Date().getFullYear())}
              >
                Bu Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setYearlyYear(yearlyYear + 1)}
              >
                Sonraki Yıl →
              </Button>
            </div>
            <div className="ml-auto flex items-center gap-4">
              <span className="text-lg font-bold text-cyan-700">{yearlyYear}</span>
              <Button
                onClick={() => setShowAddLeaveModal(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="add-leave-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                İzin Ekle
              </Button>
            </div>
          </div>

          {/* Yıllık Özet Tablosu */}
          <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-cyan-100">
                  <TableHead 
                    className="sticky left-0 bg-cyan-100 z-10 font-bold cursor-pointer hover:bg-cyan-200 select-none"
                    onClick={() => handleLeavesSort('name')}
                  >
                    Çalışan
                    <GenericSortIcon column="name" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#0891b2" />
                  </TableHead>
                  {monthNames.map((month, idx) => (
                    <TableHead key={idx} className="text-center font-bold min-w-[100px]">
                      {month}
                    </TableHead>
                  ))}
                  <TableHead 
                    className="text-center font-bold bg-red-100 min-w-[70px] cursor-pointer hover:bg-red-200 select-none"
                    onClick={() => handleLeavesSort('absent')}
                  >
                    Gelmedi
                    <GenericSortIcon column="absent" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#dc2626" />
                  </TableHead>
                  <TableHead 
                    className="text-center font-bold bg-blue-100 min-w-[90px] cursor-pointer hover:bg-blue-200 select-none"
                    onClick={() => handleLeavesSort('mazeret_izni')}
                  >
                    Ücretli İzinli
                    <GenericSortIcon column="mazeret_izni" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#2563eb" />
                  </TableHead>
                  <TableHead 
                    className="text-center font-bold bg-cyan-100 min-w-[100px] cursor-pointer hover:bg-cyan-200 select-none"
                    onClick={() => handleLeavesSort('ucretsiz_izin')}
                  >
                    Ücretsiz İzinli
                    <GenericSortIcon column="ucretsiz_izin" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#0891b2" />
                  </TableHead>
                  <TableHead 
                    className="text-center font-bold bg-purple-100 min-w-[70px] cursor-pointer hover:bg-purple-200 select-none"
                    onClick={() => handleLeavesSort('raporlu')}
                  >
                    Raporlu
                    <GenericSortIcon column="raporlu" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#9333ea" />
                  </TableHead>
                  <TableHead 
                    className="text-center font-bold bg-yellow-100 min-w-[80px] cursor-pointer hover:bg-yellow-200 select-none"
                    onClick={() => handleLeavesSort('yillik_izin')}
                  >
                    Yıllık İzin
                    <GenericSortIcon column="yillik_izin" sortColumn={leavesSortColumn} sortDirection={leavesSortDirection} color="#ca8a04" />
                  </TableHead>
                  <TableHead className="text-center font-bold bg-green-100 min-w-[120px]">Kalan Y. İzin Hakkı</TableHead>
                  <TableHead className="text-center font-bold bg-gray-100 min-w-[70px]">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {yearlyLoading ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedLeavesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Bu şirkette aktif çalışan bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedLeavesData.map((emp) => (
                    <TableRow key={emp.employee_id} className={`hover:bg-cyan-50 ${emp.termination_date ? 'bg-red-50/30' : ''}`}>
                      <TableCell className="sticky left-0 bg-white z-10 font-medium border-r">
                        <div className="flex items-center gap-2">
                          {emp.name}
                          {emp.termination_date && (
                            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                              Ayrıldı
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                        const monthData = emp.months?.[month] || {};
                        const hasData = Object.values(monthData).some(v => v > 0);
                        return (
                          <TableCell key={month} className="text-center p-2">
                            {hasData ? (
                              <div className="text-xs space-y-0.5">
                                {monthData.annual > 0 && (
                                  <div className="text-yellow-600">Y.İzin: {monthData.annual}</div>
                                )}
                                {monthData.sick > 0 && (
                                  <div className="text-purple-600">Rapor: {monthData.sick}</div>
                                )}
                                {monthData.leave > 0 && (
                                  <div className="text-blue-600">İzin: {monthData.leave}</div>
                                )}
                                {monthData.unpaid_leave > 0 && (
                                  <div className="text-cyan-600">Üsİz: {monthData.unpaid_leave}</div>
                                )}
                                {monthData.absent > 0 && (
                                  <div className="text-red-600">Gelmedi: {monthData.absent}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-bold bg-red-50 text-red-700">{emp.totals?.absent || '-'}</TableCell>
                      <TableCell className="text-center font-bold bg-blue-50 text-blue-700">{emp.totals?.paid_leave || emp.totals?.leave || '-'}</TableCell>
                      <TableCell className="text-center font-bold bg-cyan-50 text-cyan-700">{emp.totals?.unpaid_leave || '-'}</TableCell>
                      <TableCell className="text-center font-bold bg-purple-50 text-purple-700">{emp.totals?.sick || '-'}</TableCell>
                      <TableCell className="text-center font-bold bg-yellow-50 text-yellow-700">{emp.totals?.annual || '-'}</TableCell>
                      <TableCell className={`text-center font-bold ${emp.remaining_annual_leave < 0 ? 'bg-red-100 text-red-700' : 'bg-green-50 text-green-700'}`}>
                        {emp.remaining_annual_leave !== undefined ? (
                          <div className="flex flex-col">
                            <span className={emp.remaining_annual_leave < 0 ? 'text-red-700' : 'text-green-700'}>
                              {emp.remaining_annual_leave} gün
                            </span>
                            <span className="text-[10px] text-gray-500">
                              (Hak: {emp.annual_leave_entitlement || 0})
                            </span>
                          </div>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Lejant */}
          <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              <span className="text-sm">Y.İzin = Yıllık İzin</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500"></span>
              <span className="text-sm">Rapor = Raporlu</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-blue-500"></span>
              <span className="text-sm">İzin = Ücretli İzinli</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-cyan-500"></span>
              <span className="text-sm">Üsİz = Ücretsiz İzinli</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-sm">Gelmedi = İşe Gelmedi</span>
            </div>
          </div>
        </div>
      )}

      {/* Avanslar Tab İçeriği */}
      {!showTerminated && activeTab === 'advances' && (
        <div className="mt-4 space-y-4">
          {/* Alt Tab Seçimi ve Avans Ekle Butonu */}
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button
                variant={advancesSubTab === 'taken' ? 'default' : 'outline'}
                onClick={() => setAdvancesSubTab('taken')}
                className={advancesSubTab === 'taken' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Çekilen Avanslar
              </Button>
              <Button
                variant={advancesSubTab === 'deductions' ? 'default' : 'outline'}
                onClick={() => setAdvancesSubTab('deductions')}
                className={advancesSubTab === 'deductions' ? 'bg-pink-600 hover:bg-pink-700' : ''}
              >
                Aylık Avans Kesintileri
              </Button>
            </div>
            <Button
              onClick={() => setShowAddAdvanceModal(true)}
              className="bg-pink-600 hover:bg-pink-700"
            >
              <Plus className="h-4 w-4 mr-2" /> Avans Ekle
            </Button>
          </div>

          {/* Yıl Seçimi */}
          <div className="flex items-center gap-4 p-4 bg-pink-50 border border-pink-200 rounded-lg">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-pink-600" />
              <span className="font-medium text-pink-800">Yıl:</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdvancesYear(advancesYear - 1)}
              >
                ← Önceki Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdvancesYear(new Date().getFullYear())}
              >
                Bu Yıl
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdvancesYear(advancesYear + 1)}
              >
                Sonraki Yıl →
              </Button>
            </div>
            <div className="ml-auto text-lg font-bold text-pink-700">
              {advancesYear}
            </div>
          </div>

          {/* Avanslar Tablosu */}
          <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-pink-100">
                  <TableHead 
                    className="sticky left-0 bg-pink-100 z-10 font-bold cursor-pointer hover:bg-pink-200 select-none"
                    onClick={() => handleAdvancesSort('name')}
                  >
                    Çalışan
                    <GenericSortIcon column="name" sortColumn={advancesSortColumn} sortDirection={advancesSortDirection} color="#db2777" />
                  </TableHead>
                  {monthNames.map((month, idx) => (
                    <TableHead key={idx} className="text-center font-bold min-w-[90px]">
                      {month}
                    </TableHead>
                  ))}
                  <TableHead 
                    className="text-center font-bold bg-pink-200 min-w-[120px] cursor-pointer hover:bg-pink-300 select-none"
                    onClick={() => handleAdvancesSort('total_advance')}
                  >
                    TOPLAM
                    <GenericSortIcon column="total_advance" sortColumn={advancesSortColumn} sortDirection={advancesSortDirection} color="#db2777" />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {advancesLoading ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-pink-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedAdvancesData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Bu şirkette aktif çalışan bulunamadı</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedAdvancesData.map((emp) => {
                    const dataField = advancesSubTab === 'taken' ? 'advances' : 'deductions';
                    const totalField = advancesSubTab === 'taken' ? 'total_advances' : 'total_deductions';
                    return (
                      <TableRow key={emp.employee_id} className={`hover:bg-pink-50 ${emp.termination_date ? 'bg-red-50/30' : ''}`}>
                        <TableCell className="sticky left-0 bg-white z-10 font-medium border-r">
                          <div className="flex items-center gap-2">
                            {emp.name}
                            {emp.termination_date && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">
                                Ayrıldı
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                          const amount = emp[dataField]?.[month] || 0;
                          return (
                            <TableCell key={month} className="text-center">
                              {amount > 0 ? (
                                <div className="flex flex-col items-center gap-1">
                                  <span className="font-medium text-pink-700">
                                    {amount.toLocaleString('tr-TR')} ₺
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {advancesSubTab === 'taken' && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                        onClick={() => fetchAdvanceDetails(emp.employee_id, advancesYear, month)}
                                      >
                                        <FileText className="h-3 w-3 mr-1" />
                                        Belge
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-center bg-pink-50 border-l font-bold">
                          {emp[totalField] > 0 ? (
                            <span className="text-pink-700">
                              {emp[totalField].toLocaleString('tr-TR')} ₺
                            </span>
                          ) : (
                            <span className="text-gray-400">0 ₺</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Bilgi Notu */}
          <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
            <p className="font-medium mb-1">
              {advancesSubTab === 'taken' ? '💰 Çekilen Avanslar' : '📉 Aylık Avans Kesintileri'}
            </p>
            <p>
              {advancesSubTab === 'taken' 
                ? 'Her ay çalışanların aldığı avans miktarları burada görüntülenir.'
                : 'Her ay çalışanların maaşından kesilen avans miktarları burada görüntülenir.'}
            </p>
          </div>
        </div>
      )}

      {/* Maaş Değişiklikleri Tab İçeriği */}
      {!showTerminated && activeTab === 'salaryChanges' && (
        <div className="mt-4 space-y-4">
          {/* Başlık ve Excel İndir Butonu */}
          <div className="flex items-center justify-between p-4 bg-violet-50 border border-violet-200 rounded-lg">
            <div className="flex items-center gap-4">
              <FileText className="h-5 w-5 text-violet-600" />
              <span className="font-medium text-violet-800">Maaş Değişiklikleri - Çalışanların maaş geçmişi ve zam bilgileri</span>
            </div>
            <Button
              onClick={exportToExcel}
              variant="outline"
              className="border-violet-300 text-violet-600 hover:bg-violet-100"
              data-testid="salary-changes-excel-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Excel İndir
            </Button>
          </div>

          {/* Maaş Değişiklikleri Tablosu */}
          <div className="bg-white rounded-lg border border-[hsl(var(--border))] overflow-auto max-h-[600px]">
            <Table>
              <TableHeader className="sticky top-0 z-30">
                <TableRow className="bg-violet-100">
                  <TableHead className="sticky left-0 bg-violet-100 z-10 font-bold min-w-[50px]">#</TableHead>
                  <TableHead className="sticky left-[50px] bg-violet-100 z-10 font-bold min-w-[180px] border-r">Ad Soyad</TableHead>
                  <TableHead className="font-bold min-w-[120px] text-center">İşe Giriş Tarihi</TableHead>
                  <TableHead className="font-bold min-w-[120px] text-right bg-green-100">Başlangıç Maaşı</TableHead>
                  {/* Dinamik zam sütunları - maksimum zam sayısına göre */}
                  {(() => {
                    const maxRaises = Math.max(...(employees.map(emp => {
                      const history = emp.salary_history || [];
                      return Math.max(0, history.length - 1); // İlk kayıt başlangıç maaşı
                    }) || [0]), 0);
                    return Array.from({ length: maxRaises }, (_, i) => (
                      <TableHead key={i} className="font-bold min-w-[180px] text-center bg-amber-100" colSpan={2}>
                        {i + 1}. Zam
                      </TableHead>
                    ));
                  })()}
                  <TableHead className="font-bold min-w-[120px] text-right bg-violet-200">Güncel Maaş</TableHead>
                </TableRow>
                <TableRow className="bg-violet-50">
                  <TableHead className="sticky left-0 bg-violet-50 z-10"></TableHead>
                  <TableHead className="sticky left-[50px] bg-violet-50 z-10 border-r"></TableHead>
                  <TableHead></TableHead>
                  <TableHead className="bg-green-50"></TableHead>
                  {/* Zam alt başlıkları */}
                  {(() => {
                    const maxRaises = Math.max(...(employees.map(emp => {
                      const history = emp.salary_history || [];
                      return Math.max(0, history.length - 1);
                    }) || [0]), 0);
                    return Array.from({ length: maxRaises }, (_, i) => (
                      <React.Fragment key={i}>
                        <TableHead className="font-medium text-xs text-center bg-amber-50 min-w-[90px]">Tarih</TableHead>
                        <TableHead className="font-medium text-xs text-right bg-amber-50 min-w-[90px]">Miktar</TableHead>
                      </React.Fragment>
                    ));
                  })()}
                  <TableHead className="bg-violet-100"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin h-5 w-5 border-2 border-violet-500 border-t-transparent rounded-full"></div>
                        Yükleniyor...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={20} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                      Çalışan bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((emp, index) => {
                    const salaryHistory = (emp.salary_history || []).sort((a, b) => 
                      new Date(a.effective_date || a.created_at) - new Date(b.effective_date || b.created_at)
                    );
                    const startingSalary = salaryHistory[0];
                    const raises = salaryHistory.slice(1); // İlk kayıt hariç (başlangıç maaşı)
                    
                    const maxRaises = Math.max(...(employees.map(e => {
                      const h = e.salary_history || [];
                      return Math.max(0, h.length - 1);
                    }) || [0]), 0);

                    return (
                      <TableRow key={emp.id} className="hover:bg-violet-50/30">
                        <TableCell className="sticky left-0 bg-white z-10 text-center font-medium">{index + 1}</TableCell>
                        <TableCell className="sticky left-[50px] bg-white z-10 border-r font-medium">
                          {emp.name}
                        </TableCell>
                        <TableCell className="text-center">
                          {emp.hire_date ? new Date(emp.hire_date).toLocaleDateString('tr-TR') : '-'}
                        </TableCell>
                        <TableCell className="text-right bg-green-50 font-medium">
                          {startingSalary?.amount 
                            ? `${startingSalary.amount.toLocaleString('tr-TR')} ₺` 
                            : (emp.starting_salary ? `${emp.starting_salary.toLocaleString('tr-TR')} ₺` : '-')}
                        </TableCell>
                        {/* Zam hücreleri */}
                        {Array.from({ length: maxRaises }, (_, i) => {
                          const raise = raises[i];
                          return (
                            <React.Fragment key={i}>
                              <TableCell className="text-center bg-amber-50/50 text-sm">
                                {raise?.effective_date 
                                  ? new Date(raise.effective_date).toLocaleDateString('tr-TR')
                                  : '-'}
                              </TableCell>
                              <TableCell className="text-right bg-amber-50/50 font-medium">
                                {raise?.amount 
                                  ? `${raise.amount.toLocaleString('tr-TR')} ₺`
                                  : '-'}
                              </TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell className="text-right bg-violet-100 font-bold text-violet-700">
                          {emp.current_salary?.toLocaleString('tr-TR')} ₺
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Alt Bilgi */}
          <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-700">
            <p className="font-medium">📊 Maaş Değişiklikleri Tablosu</p>
            <p>Bu tabloda her çalışanın işe giriş tarihinden itibaren maaş geçmişi ve zamları görüntülenir.</p>
          </div>
        </div>
      )}

      {/* Çalışanlar Tab İçeriği */}
      {activeTab === 'employees' && (
        <>
        {/* Table */}
        <div className="bg-white rounded-lg border border-[hsl(var(--border))]">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--muted-foreground))]">Yükleniyor...</div>
        ) : employees.length === 0 ? (
          <div className="p-8 text-center" data-testid="empty-state">
            <Users className="h-12 w-12 mx-auto mb-4 text-[hsl(var(--muted-foreground))]" />
            <h3 className="text-lg font-medium mb-2">Henüz çalışan yok</h3>
            <p className="text-[hsl(var(--muted-foreground))] mb-4">
              {viewOnly ? 'Bu görünümde çalışan bulunmuyor' : 'İlk çalışanı ekleyerek başlayın'}
            </p>
            {!viewOnly && (
              <Button
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Çalışan Ekle
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">No</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('name')}
                >
                  Ad Soyad <SortIcon column="name" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('tc_no')}
                >
                  TC Kimlik No <SortIcon column="tc_no" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('phone')}
                >
                  Telefon <SortIcon column="phone" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('profession')}
                >
                  Meslek <SortIcon column="profession" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('position')}
                >
                  Görev <SortIcon column="position" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('branch')}
                >
                  Şube <SortIcon column="branch" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('salary')}
                >
                  Maaş <SortIcon column="salary" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('yemek')}
                >
                  Yemek Kartı <SortIcon column="yemek" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('yol')}
                >
                  Yol Parası <SortIcon column="yol" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('saglik')}
                >
                  Sağlık Sig. <SortIcon column="saglik" />
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('hire_date')}
                >
                  İşe Giriş <SortIcon column="hire_date" />
                </TableHead>
                {showTerminated && (
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleEmployeeSort('termination_date')}
                  >
                    İşten Ayrılış <SortIcon column="termination_date" />
                  </TableHead>
                )}
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleEmployeeSort('status')}
                >
                  Durum <SortIcon column="status" />
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEmployees.map((employee, index) => {
                // Get current salary
                const currentSalary = employee.salary_history && employee.salary_history.length > 0
                  ? employee.salary_history[employee.salary_history.length - 1]
                  : null;
                
                // Get individual supports - check both 'name' and 'support_type' fields
                const getSupport = (supportName) => {
                  if (!employee.supports || employee.supports.length === 0) return null;
                  return employee.supports.find(s => 
                    (s.name === supportName || s.support_type === supportName) && 
                    (s.enabled !== false && s.is_active !== false)
                  );
                };
                
                const yemekKarti = getSupport('yemek_karti') || getSupport('yemek');
                const yolParasi = getSupport('yol_parasi') || getSupport('yol');
                const saglikSigortasi = getSupport('saglik_sigortasi') || getSupport('saglik');
                
                // Check if employee is terminated
                const isTerminated = employee.employment_status === 'terminated' || employee.termination_date;
                const terminationDate = employee.termination_date ? new Date(employee.termination_date).toLocaleDateString('tr-TR') : null;
                
                return (
                <TableRow 
                  key={`${employee.id || 'emp'}-${index}`} 
                  data-testid="employee-row"
                  className={isTerminated ? 'bg-gray-100 text-gray-600' : ''}
                >
                  <TableCell className="font-medium text-[hsl(var(--muted-foreground))]">{index + 1}</TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {employee.name}
                      {docReqPendingCounts[employee.id] > 0 && (
                        <button
                          type="button"
                          onClick={async () => { await openDocumentsDetailModal(employee); setActiveDocCategory('ik_belge_talepleri'); }}
                          data-testid={`docreq-pending-badge-${employee.id}`}
                          title="Cevap bekleyen belge talepleri"
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors"
                        >
                          <Bell className="h-3 w-3" />
                          {docReqPendingCounts[employee.id]} cevap bekliyor
                        </button>
                      )}
                      {isTerminated && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                          İşten Ayrıldı {terminationDate && `(${terminationDate})`}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{employee.tc_no}</TableCell>
                  <TableCell>{employee.phone}</TableCell>
                  <TableCell>{employee.profession || '-'}</TableCell>
                  <TableCell>{employee.position_name || '-'}</TableCell>
                  <TableCell>{employee.branch_name || '-'}</TableCell>
                  <TableCell className="font-medium">
                    {currentSalary && currentSalary.amount ? `₺${parseFloat(currentSalary.amount).toLocaleString('tr-TR')}` : '-'}
                  </TableCell>
                  <TableCell>
                    {yemekKarti ? `₺${parseFloat(yemekKarti.amount).toLocaleString('tr-TR')}` : '-'}
                  </TableCell>
                  <TableCell>
                    {yolParasi ? `₺${parseFloat(yolParasi.amount).toLocaleString('tr-TR')}` : '-'}
                  </TableCell>
                  <TableCell>
                    {saglikSigortasi ? `₺${parseFloat(saglikSigortasi.amount).toLocaleString('tr-TR')}` : '-'}
                  </TableCell>
                  <TableCell>{employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                  {showTerminated && (
                    <TableCell className="text-red-600 font-medium">
                      {employee.termination_details?.date 
                        ? new Date(employee.termination_details.date).toLocaleDateString('tr-TR')
                        : employee.termination_date 
                        ? new Date(employee.termination_date).toLocaleDateString('tr-TR')
                        : '-'}
                    </TableCell>
                  )}
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      employee.employment_status === 'active' ? 'bg-green-100 text-green-800' :
                      employee.employment_status === 'on_leave' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {employee.employment_status === 'active' ? 'Aktif' :
                       employee.employment_status === 'on_leave' ? 'İzinli' : 'Ayrılmış'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDocumentsModal(employee)}
                        className="relative"
                        data-testid="documents-btn"
                        title="Belgeler"
                      >
                        <FileText className="h-4 w-4" />
                        {employee.documents && employee.documents.length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {employee.documents.length}
                          </span>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPayrollsModal(employee)}
                        className="relative"
                        data-testid="payrolls-btn"
                        title="Bordrolar"
                      >
                        <FileText className="h-4 w-4 text-emerald-600" />
                        {employee.payrolls && employee.payrolls.length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-emerald-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {employee.payrolls.length}
                          </span>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDocumentsDetailModal(employee)}
                        className="relative"
                        data-testid="documents-detail-btn"
                        title="Evraklar"
                      >
                        <FileText className="h-4 w-4 text-purple-600" />
                        {employee.documents_detail && Object.values(employee.documents_detail).flat().length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {Object.values(employee.documents_detail).flat().length}
                          </span>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAssetsModal(employee)}
                        className="relative"
                        data-testid="assets-btn"
                        title="Zimmetler"
                      >
                        <Package className="h-4 w-4" />
                        {getAssetCount(employee) > 0 && (
                          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {getAssetCount(employee)}
                          </span>
                        )}
                      </Button>
                      {!isTerminated && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openTerminationModal(employee)}
                            data-testid="terminate-employee-btn"
                            title="İşten Ayrıl"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(employee)}
                            data-testid="edit-employee-btn"
                            title="Düzenle"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {isTerminated && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRehire(employee)}
                            title="Tekrar İşe Al"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditModal(employee, true)}
                            data-testid="detail-employee-btn"
                            title="Detay"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
        </>
      )}

      {/* Add Employee Modal */}
      <Dialog open={showRehireListModal} onOpenChange={setShowRehireListModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-600" />
              Eski Çalışanlardan Seç (Tekrar İşe Al)
            </DialogTitle>
            <DialogDescription>
              İşten ayrılan bir personeli tekrar işe almak için listeden seçin. Tüm eski özlük belgeleri yeni kayda aktarılacaktır.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>TC No</TableHead>
                  <TableHead>Çıkış Tarihi</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.filter(e => e.is_terminated || e.employment_status === 'terminated' || e.termination_date).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      Sistemde işten ayrılan personel bulunmuyor.
                    </TableCell>
                  </TableRow>
                ) : (
                  employees
                    .filter(e => e.is_terminated || e.employment_status === 'terminated' || e.termination_date)
                    .sort((a, b) => new Date(b.termination_date || 0) - new Date(a.termination_date || 0))
                    .map(emp => (
                      <TableRow key={emp.id}>
                        <TableCell className="font-medium">{emp.name}</TableCell>
                        <TableCell>{emp.tc_no}</TableCell>
                        <TableCell>{emp.termination_date ? new Date(emp.termination_date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setShowRehireListModal(false);
                              handleRehire(emp);
                            }}
                            className="text-green-600 hover:bg-green-50 hover:text-green-700"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" /> Seç ve İşe Al
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <AddEmployeeModal
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        formData={formData}
        setFormData={setFormData}
        handleAddEmployee={handleAddEmployee}
        handleFileUpload={handleFileUpload}
        branches={branches}
        positions={positions}
        professions={professions}
        selectedSupports={selectedSupports}
        setSelectedSupports={setSelectedSupports}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        subcontractors={subcontractors}
        handleOpenRehireList={() => setShowRehireListModal(true)}
      />
      <EditEmployeeModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        editViewOnly={editViewOnly}
        selectedEmployee={selectedEmployee}
        formData={formData}
        setFormData={setFormData}
        handleEditEmployee={handleEditEmployee}
        handleFileUpload={handleFileUpload}
        branches={branches}
        positions={positions}
        professions={professions}
        selectedSupports={selectedSupports}
        setSelectedSupports={setSelectedSupports}
        selectedCompany={selectedCompany}
        subcontractors={subcontractors}
        detailTab={detailTab}
        setDetailTab={setDetailTab}
        historyLoading={historyLoading}
        historyData={historyData}
        renderDetailHistory={renderDetailHistory}
        resetForm={resetForm}
        newSalaryAmount={newSalaryAmount}
        setNewSalaryAmount={setNewSalaryAmount}
        handleSalaryUpdate={handleSalaryUpdate}
      />
      <SalaryHistoryModal
        showSalaryHistoryModal={showSalaryHistoryModal}
        setShowSalaryHistoryModal={setShowSalaryHistoryModal}
        selectedEmployee={selectedEmployee}
        historyLoading={historyLoading}
        salaryHistory={salaryHistory}
        newSalary={newSalary}
        setNewSalary={setNewSalary}
        handleAddSalary={handleAddSalary}
      />
      <Dialog open={showDocumentsModal} onOpenChange={(open) => {
        setShowDocumentsModal(open);
        if (!open) {
          setCustomDocuments([]);
          setShowCustomDocInput(false);
          setCustomDocName('');
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="documents-modal">
          <DialogHeader>
            <DialogTitle className="text-xl">Özlük Dosyaları - {selectedEmployee?.name}</DialogTitle>
            <DialogDescription>
              {isTerminatedEmployee ? 'Belgeleri görüntüleyebilirsiniz (işten ayrılmış çalışan)' : 'Belge yüklemek için ilgili karta tıklayın'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Predefined Document Type Cards - Clickable */}
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {documentTypes.map((docType) => {
                const docs = getDocumentsForType(docType.value);
                const hasDoc = docs.length > 0;
                const latestDoc = docs[docs.length - 1];
                const isImage = latestDoc?.file_url?.startsWith('data:image/');
                
                return (
                  <div key={docType.value} className="relative">
                    {/* Hidden file input for this document type */}
                    <input
                      type="file"
                      id={`doc-upload-${docType.value}`}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleCardDocumentUpload(e, docType.value)}
                      className="hidden"
                    />
                    
                    <Card 
                      className={`${isTerminatedEmployee ? '' : 'cursor-pointer hover:shadow-md hover:scale-[1.02]'} transition-all ${
                        hasDoc 
                          ? 'border-2 border-[hsl(var(--brand-600))] bg-[hsl(var(--brand-50))]' 
                          : 'border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--brand-400))]'
                      }`}
                      onClick={() => !isTerminatedEmployee && triggerFileInput(docType.value)}
                      data-testid={`doc-card-${docType.value}`}
                    >
                      <CardContent className="p-0">
                        {/* Preview Area - Smaller */}
                        <div className={`h-16 flex items-center justify-center relative ${
                          hasDoc ? 'bg-[hsl(var(--brand-100))]' : 'bg-[hsl(var(--muted))]'
                        }`}>
                          {hasDoc ? (
                            isImage ? (
                              <img 
                                src={latestDoc.file_url} 
                                alt={docType.label}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center">
                                <FileText className="h-6 w-6 text-[hsl(var(--brand-700))]" />
                                <span className="text-[10px] text-[hsl(var(--brand-600))] font-medium">
                                  {latestDoc.file_name?.split('.').pop()?.toUpperCase()}
                                </span>
                              </div>
                            )
                          ) : (
                            <div className="flex flex-col items-center text-[hsl(var(--muted-foreground))]">
                              {isTerminatedEmployee ? (
                                <span className="text-[10px]">Boş</span>
                              ) : (
                                <>
                                  <Upload className="h-5 w-5" />
                                  <span className="text-[10px]">Yükle</span>
                                </>
                              )}
                            </div>
                          )}
                          
                          {/* Badge for count */}
                          {docs.length > 0 && (
                            <Badge className="absolute top-1 left-1 bg-[hsl(var(--brand-700))] text-white text-[10px] h-4 w-4 p-0 flex items-center justify-center">
                              {docs.length}
                            </Badge>
                          )}
                          
                          {/* Action buttons for uploaded docs - moved inside preview area */}
                          {hasDoc && (
                            <div className="absolute top-1 right-1 flex gap-0.5 bg-white/90 rounded shadow-sm p-0.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDocumentPreview(latestDoc);
                                }}
                                title="Görüntüle"
                              >
                                <Eye className="h-3 w-3 text-[hsl(var(--brand-600))]" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 w-5 p-0 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadDocument(latestDoc.file_url, latestDoc.file_name);
                                }}
                                title="İndir"
                              >
                                <Download className="h-3 w-3 text-[hsl(var(--brand-600))]" />
                              </Button>
                              {!isTerminatedEmployee && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 rounded"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDocument(latestDoc.id);
                                  }}
                                  title="Sil"
                                >
                                  <Trash2 className="h-3 w-3 text-red-500" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Label - Always visible */}
                        <div className="p-1.5 text-center bg-white">
                          <span className={`text-xs font-medium leading-tight line-clamp-2 ${
                            hasDoc ? 'text-[hsl(var(--brand-700))]' : 'text-[hsl(var(--foreground))]'
                          }`}>
                            {docType.label}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Custom Documents Section */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm">Özel Belgeler</h4>
                {!isTerminatedEmployee && !showCustomDocInput && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomDocInput(true)}
                    className="text-[hsl(var(--brand-700))] border-[hsl(var(--brand-300))] hover:bg-[hsl(var(--brand-50))]"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Yeni Belge Türü
                  </Button>
                )}
              </div>
              
              {/* Add Custom Doc Input */}
              {showCustomDocInput && (
                <div className="flex gap-2 mb-4 p-3 bg-[hsl(var(--muted))] rounded-lg">
                  <Input
                    placeholder="Belge adı (örn: Sertifika, Referans Mektubu)"
                    value={customDocName}
                    onChange={(e) => setCustomDocName(e.target.value)}
                    className="flex-1"
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={handleAddCustomDocSlot}
                    className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
                  >
                    Ekle
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCustomDocInput(false);
                      setCustomDocName('');
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              {/* Custom Document Cards */}
              <div className="grid grid-cols-4 gap-4">
                {/* Show existing custom documents from employee */}
                {employeeDocuments
                  .filter(d => d.document_type === 'custom')
                  .map((doc) => (
                    <div key={doc.id} className="relative">
                      <Card className="border-2 border-[hsl(var(--brand-600))] bg-[hsl(var(--brand-50))]">
                        <CardContent className="p-0">
                          <div className="h-28 flex items-center justify-center bg-[hsl(var(--brand-100))]">
                            {doc.file_url?.startsWith('data:image/') ? (
                              <img 
                                src={doc.file_url} 
                                alt={doc.document_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="flex flex-col items-center">
                                <FileText className="h-10 w-10 text-[hsl(var(--brand-700))]" />
                                <span className="text-xs text-[hsl(var(--brand-600))] mt-1">
                                  {doc.file_name?.split('.').pop()?.toUpperCase()}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="p-2 text-center bg-white">
                            <span className="text-sm font-medium text-[hsl(var(--brand-700))] truncate block">
                              {doc.document_name}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Action buttons */}
                      <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1 bg-white rounded-full shadow-md p-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full"
                          onClick={() => openDocumentPreview(doc)}
                          title="Görüntüle"
                        >
                          <Eye className="h-3 w-3 text-[hsl(var(--brand-600))]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full"
                          onClick={() => downloadDocument(doc.file_url, doc.file_name)}
                          title="İndir"
                        >
                          <Download className="h-3 w-3 text-[hsl(var(--brand-600))]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 rounded-full"
                          onClick={() => handleDeleteDocument(doc.id)}
                          title="Sil"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                
                {/* Show newly added custom doc slots */}
                {customDocuments.map((customDoc) => (
                  <div key={customDoc.id} className="relative">
                    <input
                      type="file"
                      id={`doc-upload-${customDoc.id}`}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={(e) => handleCardDocumentUpload(e, 'custom', customDoc.name)}
                      className="hidden"
                    />
                    
                    <Card 
                      className="cursor-pointer border-2 border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--brand-400))] transition-all hover:shadow-lg hover:scale-[1.02]"
                      onClick={() => document.getElementById(`doc-upload-${customDoc.id}`).click()}
                    >
                      <CardContent className="p-0">
                        <div className="h-28 flex items-center justify-center bg-[hsl(var(--muted))]">
                          <div className="flex flex-col items-center text-[hsl(var(--muted-foreground))]">
                            <Upload className="h-8 w-8 mb-1" />
                            <span className="text-xs">Yükle</span>
                          </div>
                        </div>
                        <div className="p-2 text-center">
                          <span className="text-sm font-medium truncate block">{customDoc.name}</span>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full bg-white shadow-md hover:bg-red-50"
                      onClick={() => setCustomDocuments(customDocuments.filter(d => d.id !== customDoc.id))}
                    >
                      <X className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Empty state for custom docs */}
              {employeeDocuments.filter(d => d.document_type === 'custom').length === 0 && customDocuments.length === 0 && (
                <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">
                  Özel belge eklemek için yukarıdaki butona tıklayın
                </p>
              )}
            </div>
            
            {/* İşten Çıkış Belgeleri Bölümü */}
            {employeeDocuments.filter(d => d.document_type === 'exit_document').length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                <h4 className="font-medium text-red-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  İşten Çıkış Belgeleri
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {employeeDocuments
                    .filter(d => d.document_type === 'exit_document')
                    .map((doc) => (
                      <div 
                        key={doc.id} 
                        className="relative group bg-white rounded-lg border border-red-200 p-3 hover:shadow-md transition-all cursor-pointer"
                        onClick={() => openDocumentPreview(doc)}
                      >
                        <div className="flex flex-col items-center text-center">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                            <FileText className="h-6 w-6 text-red-600" />
                          </div>
                          <span className="text-xs font-medium text-red-800 truncate w-full">
                            {doc.document_name || doc.file_name || 'İşten Çıkış Belgesi'}
                          </span>
                          <span className="text-xs text-red-600">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('tr-TR') : ''}
                          </span>
                        </div>
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 bg-white shadow-sm hover:bg-green-50"
                            onClick={(e) => { e.stopPropagation(); openDocumentPreview(doc); }}
                            title="Önizle"
                          >
                            <Eye className="h-3 w-3 text-green-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 bg-white shadow-sm hover:bg-blue-50"
                            onClick={(e) => { e.stopPropagation(); downloadDocument(doc.file_url, doc.file_name); }}
                            title="İndir"
                          >
                            <Download className="h-3 w-3 text-blue-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocumentsModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle>
              {previewDoc?.document_name || documentTypes.find(dt => dt.value === previewDoc?.document_type)?.label || 'Belge Önizleme'}
            </DialogTitle>
            <DialogDescription>
              {previewDoc?.file_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-center min-h-[400px] bg-[hsl(var(--muted))] rounded-lg overflow-hidden">
            {previewDoc?.file_url?.startsWith('data:image/') ? (
              <img 
                src={previewDoc.file_url} 
                alt={previewDoc.file_name}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : previewDoc?.file_url?.startsWith('data:application/pdf') ? (
              <iframe
                src={previewDoc.file_url}
                title={previewDoc.file_name}
                className="w-full h-[70vh]"
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-8 text-center">
                <FileText className="h-20 w-20 text-[hsl(var(--brand-700))] mb-4" />
                <p className="text-lg font-medium mb-2">{previewDoc?.file_name}</p>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                  Bu dosya türü tarayıcıda önizlenemez
                </p>
                <Button
                  onClick={() => downloadDocument(previewDoc?.file_url, previewDoc?.file_name)}
                  className="inline-flex items-center gap-2 bg-[hsl(var(--brand-700))] text-white hover:bg-[hsl(var(--brand-800))]"
                >
                  <Download className="h-4 w-4" />
                  Dosyayı İndir
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => downloadDocument(previewDoc?.file_url, previewDoc?.file_name)}
              className="inline-flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              İndir
            </Button>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assets/Zimmet Modal */}
      <Dialog open={showAssetsModal} onOpenChange={setShowAssetsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="assets-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-500" />
              Zimmetler - {selectedEmployee?.name}
            </DialogTitle>
            <DialogDescription>
              Çalışana zimmetlenen eşyaları yönetin
            </DialogDescription>
          </DialogHeader>

          {/* Add New Asset Form */}
          {!isTerminatedEmployee && (
          <div className="bg-[hsl(var(--muted))] p-4 rounded-lg space-y-3">
            <h4 className="font-medium text-sm">Yeni Zimmet Ekle</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="asset_name">Ürün Adı *</Label>
                <Input
                  id="asset_name"
                  placeholder="Örn: Dizüstü Bilgisayar"
                  value={newAsset.asset_name}
                  onChange={(e) => setNewAsset({ ...newAsset, asset_name: e.target.value })}
                  data-testid="input-asset-name"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="serial_number">Seri No</Label>
                <Input
                  id="serial_number"
                  placeholder="Örn: SN123456"
                  value={newAsset.serial_number}
                  onChange={(e) => setNewAsset({ ...newAsset, serial_number: e.target.value })}
                  data-testid="input-serial-number"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="asset_description">Açıklama</Label>
                <Input
                  id="asset_description"
                  placeholder="Ürün detayları..."
                  value={newAsset.asset_description}
                  onChange={(e) => setNewAsset({ ...newAsset, asset_description: e.target.value })}
                  data-testid="input-asset-description"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="asset_notes">Notlar</Label>
                <Input
                  id="asset_notes"
                  placeholder="Ek notlar..."
                  value={newAsset.notes}
                  onChange={(e) => setNewAsset({ ...newAsset, notes: e.target.value })}
                  data-testid="input-asset-notes"
                />
              </div>
            </div>
            <Button 
              onClick={handleAddAsset} 
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="add-asset-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Zimmet Ekle
            </Button>
          </div>
          )}

          {/* Assets List */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Mevcut Zimmetler ({employeeAssets.length})</h4>
            {employeeAssets.length === 0 ? (
              <div className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Henüz zimmetlenmiş eşya yok</p>
              </div>
            ) : (
              <div className="space-y-2">
                {employeeAssets.map((asset) => (
                  <div 
                    key={asset.id} 
                    className={`p-3 rounded-lg border ${
                      asset.status === 'returned' 
                        ? 'bg-gray-50 border-gray-200' 
                        : asset.response_status === 'accepted'
                        ? 'bg-green-50 border-green-200'
                        : asset.response_status === 'rejected'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}
                    data-testid="asset-item"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h5 className="font-medium">{asset.asset_name}</h5>
                          {/* Zimmet Durumu */}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            asset.status === 'returned'
                              ? 'bg-gray-200 text-gray-600'
                              : 'bg-orange-200 text-orange-700'
                          }`}>
                            {asset.status === 'returned' ? 'İade Edildi' : 'Zimmetli'}
                          </span>
                          {/* Yanıt Durumu - Kullanıcı Portalındaki Gibi */}
                          {asset.status !== 'returned' && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              asset.response_status === 'accepted'
                                ? 'bg-green-100 text-green-700'
                                : asset.response_status === 'rejected'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {asset.response_status === 'accepted' 
                                ? '✓ Teslim Aldı' 
                                : asset.response_status === 'rejected'
                                ? '✗ Reddetti'
                                : '⏳ Onay Bekliyor'}
                            </span>
                          )}
                        </div>
                        {asset.serial_number && (
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            Seri No: {asset.serial_number}
                          </p>
                        )}
                        {asset.asset_description && (
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            {asset.asset_description}
                          </p>
                        )}
                        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                          Teslim: {asset.assignment_date}
                          {asset.return_date && ` | İade: ${asset.return_date}`}
                        </p>
                        {/* Reddedilme Sebebi */}
                        {asset.response_status === 'rejected' && asset.reject_reason && (
                          <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                            <p className="text-xs text-red-600">
                              <span className="font-medium">Red Sebebi:</span> {asset.reject_reason}
                            </p>
                          </div>
                        )}
                        {asset.notes && (
                          <p className="text-xs text-[hsl(var(--muted-foreground))] italic">
                            Not: {asset.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {!isTerminatedEmployee && asset.status !== 'returned' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReturnAsset(asset.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="İade Al"
                            data-testid="return-asset-btn"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Kabul edilmiş zimmetler silinemez */}
                        {!isTerminatedEmployee && asset.response_status !== 'accepted' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAsset(asset.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Sil"
                            data-testid="delete-asset-btn"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Zimmet Arşivi (İade / Red / Geri Alma) */}
          <div className="space-y-3 mt-4 pt-4 border-t" data-testid="assets-archive-section">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-400" />
              Zimmet Arşivi ({employeeAssetsArchive.length})
            </h4>
            {employeeAssetsArchive.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Arşivde kayıt yok</p>
            ) : (
              <div className="space-y-2">
                {employeeAssetsArchive.map((arsiv, ai) => (
                  <div key={arsiv.id || ai} className="p-3 rounded-lg border bg-gray-50 border-gray-200" data-testid={`asset-archive-item-${ai}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h5 className="font-medium">{arsiv.urun_adi || arsiv.product_name || arsiv.asset_name || 'Ürün'}</h5>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        arsiv.islem === 'reddedildi' ? 'bg-red-100 text-red-700'
                        : arsiv.islem === 'iade' || arsiv.islem === 'iade_edildi' ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                      }`}>
                        {arsiv.islem === 'reddedildi' ? 'Reddedildi'
                          : (arsiv.islem === 'iade' || arsiv.islem === 'iade_edildi') ? 'İade Edildi'
                          : 'Depo Geri Aldı'}
                      </span>
                    </div>
                    {arsiv.seri_no && (
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">Seri No: {arsiv.seri_no}</p>
                    )}
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                      {arsiv.zimmet_tarihi && <>Teslim: {arsiv.zimmet_tarihi}</>}
                      {arsiv.iade_tarihi && <> | İşlem: {new Date(arsiv.iade_tarihi).toLocaleString('tr-TR')}</>}
                    </p>
                    {(arsiv.teslim_eden_adi || arsiv.geri_alan_adi) && (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        {arsiv.teslim_eden_adi && <>Teslim Eden: {arsiv.teslim_eden_adi}</>}
                        {arsiv.geri_alan_adi && <> | Geri Alan: {arsiv.geri_alan_adi}</>}
                      </p>
                    )}
                    {(arsiv.iade_notu || arsiv.red_sebebi) && (
                      <p className="text-xs text-[hsl(var(--muted-foreground))] italic mt-1">
                        {arsiv.red_sebebi ? `Red Sebebi: ${arsiv.red_sebebi}` : `Not: ${arsiv.iade_notu}`}
                      </p>
                    )}
                    {(arsiv.belgeler?.length > 0 || arsiv.geri_alma_belgeler?.length > 0) && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {[...(arsiv.belgeler || []), ...(arsiv.geri_alma_belgeler || [])].map((doc, di) => {
                          const url = doc.url || doc.data || '';
                          const href = url.startsWith('data:') ? url : (url.startsWith('/api') ? `${backendUrl}${url}` : `${backendUrl}${url}`);
                          return (
                            <a key={di} href={href} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 bg-white border rounded px-2 py-0.5 text-xs text-blue-600 hover:bg-blue-50"
                              data-testid={`asset-archive-doc-${ai}-${di}`}>
                              <FileText className="h-3 w-3" />
                              {doc.dosya_adi || doc.filename || doc.name || 'Belge'}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetsModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Taşı Modal */}
      <Dialog open={moveDialog.open} onOpenChange={(o) => !o && setMoveDialog({ open: false, item: null, sourceKey: null })}>
        <DialogContent className="max-w-md" data-testid="move-doc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderInput className="h-5 w-5 text-blue-600" />
              Belgeyi Başka Taba Taşı
            </DialogTitle>
            <DialogDescription>
              "{moveDialog.item?.title || moveDialog.item?.name || 'Belge'}" kaydını taşımak istediğiniz tabı seçin. (Gerçek taşıma: eski tabdan kalkar)
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-2">
            {MOVE_TARGET_CATEGORIES.filter(k => k !== moveDialog.sourceKey).map(k => {
              const cat = documentCategories.find(c => c.key === k);
              return (
                <Button
                  key={k}
                  variant="outline"
                  className="justify-start hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => handleMoveDocument(k)}
                  data-testid={`move-target-${k}`}
                >
                  <FileText className="h-4 w-4 mr-2 text-blue-600" />
                  {cat?.label || k}
                </Button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDialog({ open: false, item: null, sourceKey: null })}>İptal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcontractor Modal */}
      <Dialog open={showSubcontractorModal} onOpenChange={setShowSubcontractorModal}>
        <DialogContent className="max-w-lg" data-testid="subcontractor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              {newSubcontractor.id ? 'Taşeron Düzenle' : 'Yeni Taşeron Ekle'}
            </DialogTitle>
            <DialogDescription>
              Taşeron firma bilgilerini girin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subcontractor_name">Firma Adı *</Label>
              <Input
                id="subcontractor_name"
                placeholder="Taşeron firma adı"
                value={newSubcontractor.name}
                onChange={(e) => setNewSubcontractor({ ...newSubcontractor, name: e.target.value })}
                data-testid="input-subcontractor-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subcontractor_tax">Vergi No</Label>
              <Input
                id="subcontractor_tax"
                placeholder="Vergi numarası"
                value={newSubcontractor.tax_number}
                onChange={(e) => setNewSubcontractor({ ...newSubcontractor, tax_number: e.target.value })}
                data-testid="input-subcontractor-tax"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subcontractor_phone">Telefon</Label>
                <Input
                  id="subcontractor_phone"
                  placeholder="0xxx xxx xxxx"
                  value={newSubcontractor.phone}
                  onChange={(e) => setNewSubcontractor({ ...newSubcontractor, phone: e.target.value })}
                  data-testid="input-subcontractor-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="subcontractor_email">E-posta</Label>
                <Input
                  id="subcontractor_email"
                  type="email"
                  placeholder="info@firma.com"
                  value={newSubcontractor.email}
                  onChange={(e) => setNewSubcontractor({ ...newSubcontractor, email: e.target.value })}
                  data-testid="input-subcontractor-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subcontractor_service">Verdiği Hizmet</Label>
              <Input
                id="subcontractor_service"
                placeholder="Örn: Temizlik, Güvenlik, Yemek"
                value={newSubcontractor.provided_service}
                onChange={(e) => setNewSubcontractor({ ...newSubcontractor, provided_service: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>İşe Başlama Tarihi</Label>
                <Input
                  type="date"
                  value={newSubcontractor.start_date}
                  onChange={(e) => setNewSubcontractor({ ...newSubcontractor, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={newSubcontractor.end_date}
                  onChange={(e) => setNewSubcontractor({ ...newSubcontractor, end_date: e.target.value })}
                />
              </div>
            </div>

            {/* Custom Fields */}
            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Ek Bilgiler (İsteğe Bağlı)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const custom_fields = [...(newSubcontractor.custom_fields || [])];
                    custom_fields.push({ key: '', value: '' });
                    setNewSubcontractor({ ...newSubcontractor, custom_fields });
                  }}
                  className="gap-1 h-8 text-xs"
                >
                  <Plus className="h-3 w-3" /> Bilgi Ekle
                </Button>
              </div>
              
              {(newSubcontractor.custom_fields || []).map((field, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input 
                    placeholder="Başlık (Örn: SGK No)"
                    value={field.key}
                    onChange={(e) => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields[index].key = e.target.value;
                      setNewSubcontractor({ ...newSubcontractor, custom_fields });
                    }}
                    className="flex-1"
                  />
                  <Input 
                    placeholder="Değer (Örn: 12345)"
                    value={field.value}
                    onChange={(e) => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields[index].value = e.target.value;
                      setNewSubcontractor({ ...newSubcontractor, custom_fields });
                    }}
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-10 w-10 p-0 text-red-500"
                    onClick={() => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields.splice(index, 1);
                      setNewSubcontractor({ ...newSubcontractor, custom_fields });
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Yetkili Kişiler</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const contacts = [...(newSubcontractor.contacts || [])];
                    contacts.push({ name: '', role: '', phone: '', email: '' });
                    setNewSubcontractor({ ...newSubcontractor, contacts });
                  }}
                  className="gap-1 h-8 text-xs"
                >
                  <Plus className="h-3 w-3" /> Yetkili Ekle
                </Button>
              </div>
              
              {(newSubcontractor.contacts || []).map((contact, index) => (
                <div key={index} className="p-3 bg-gray-50 border rounded-md relative space-y-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-500">Yetkili {index + 1}</span>
                    {index > 0 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts.splice(index, 1);
                          setNewSubcontractor({ ...newSubcontractor, contacts });
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Ad Soyad</Label>
                      <Input 
                        value={contact.name} 
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].name = e.target.value;
                          setNewSubcontractor({ ...newSubcontractor, contacts });
                        }}
                        placeholder="Örn: Ali Yılmaz"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Görev</Label>
                      <Input 
                        value={contact.role} 
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].role = e.target.value;
                          setNewSubcontractor({ ...newSubcontractor, contacts });
                        }}
                        placeholder="Örn: Şantiye Şefi"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Telefon</Label>
                      <Input 
                        value={contact.phone} 
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].phone = e.target.value;
                          setNewSubcontractor({ ...newSubcontractor, contacts });
                        }}
                        placeholder="05..."
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">E-posta</Label>
                      <Input 
                        type="email"
                        value={contact.email} 
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].email = e.target.value;
                          setNewSubcontractor({ ...newSubcontractor, contacts });
                        }}
                        placeholder="ali@..."
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="subcontractor_branch">Çalışacağı Şube *</Label>
              <select
                id="subcontractor_branch"
                className="flex h-10 w-full rounded-md border border-[hsl(var(--border))] bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                value={newSubcontractor.branch_id}
                onChange={(e) => setNewSubcontractor({ ...newSubcontractor, branch_id: e.target.value })}
                data-testid="select-subcontractor-branch"
              >
                <option value="">Şube Seçin</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Taşeron seçilen şubenin Şube Yönetim Paneli'nde görünecektir</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcontractorModal(false)}>İptal</Button>
            <Button 
              onClick={handleSaveSubcontractor}
              className="bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
              data-testid="submit-subcontractor"
              disabled={!newSubcontractor.name.trim() || !newSubcontractor.branch_id}
            >
              {newSubcontractor.id ? 'Güncelle' : 'Taşeron Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subcontractor List Modal */}
      <Dialog open={showSubcontractorListModal} onOpenChange={setShowSubcontractorListModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-[hsl(var(--brand-600))]" />
              Taşeron Listesi
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto py-4">
            {selectedSubDetail ? (
              <div className="space-y-6">
                <div className="flex items-center gap-2 mb-4">
                  <Button variant="outline" size="sm" onClick={() => setSelectedSubDetail(null)}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Geri
                  </Button>
                  <h3 className="text-lg font-bold text-gray-900">{selectedSubDetail.name} Detayları</h3>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <h4 className="font-semibold text-gray-700 flex items-center gap-2"><Building className="h-4 w-4" /> Firma Bilgileri</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">Şube:</span>
                      <span className="font-medium">{branches.find(b => b.id === selectedSubDetail.branch_id)?.name || selectedSubDetail.branch_id}</span>
                      <span className="text-gray-500">Vergi No:</span>
                      <span className="font-medium">{selectedSubDetail.tax_number || '-'}</span>
                      <span className="text-gray-500">Telefon:</span>
                      <span className="font-medium">{selectedSubDetail.phone || '-'}</span>
                      <span className="text-gray-500">E-posta:</span>
                      <span className="font-medium">{selectedSubDetail.email || '-'}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Çalışma Bilgileri</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-500">İşe Başlama:</span>
                      <span className="font-medium">{selectedSubDetail.start_date ? new Date(selectedSubDetail.start_date).toLocaleDateString('tr-TR') : '-'}</span>
                      <span className="text-gray-500">Bitiş Tarihi:</span>
                      <span className="font-medium">{selectedSubDetail.end_date ? new Date(selectedSubDetail.end_date).toLocaleDateString('tr-TR') : '-'}</span>
                      <span className="text-gray-500">Verdiği Hizmet:</span>
                      <span className="font-medium">{selectedSubDetail.provided_service || '-'}</span>
                    </div>
                  </div>
                </div>

                {selectedSubDetail.contacts && selectedSubDetail.contacts.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Users className="h-4 w-4" /> Yetkili Kişiler</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSubDetail.contacts.map((contact, idx) => (
                        <div key={idx} className="bg-white border rounded-lg p-3 text-sm">
                          <p className="font-bold text-gray-900 mb-1">{contact.name || 'İsimsiz'}</p>
                          <p className="text-gray-600 mb-2">{contact.role || 'Görev belirtilmemiş'}</p>
                          <div className="flex flex-col gap-1">
                            <span className="flex items-center gap-2 text-gray-500"><Phone className="h-3 w-3" /> {contact.phone || '-'}</span>
                            <span className="flex items-center gap-2 text-gray-500"><Mail className="h-3 w-3" /> {contact.email || '-'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedSubDetail.custom_fields && selectedSubDetail.custom_fields.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Ek Bilgiler</h4>
                    <div className="bg-white border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          {selectedSubDetail.custom_fields.map((field, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="px-4 py-2 font-medium bg-gray-50 w-1/3">{field.key}</td>
                              <td className="px-4 py-2">{field.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Firma Adı</TableHead>
                    <TableHead>Şube</TableHead>
                    <TableHead>Verdiği Hizmet</TableHead>
                    <TableHead>Durum</TableHead>
                    <TableHead>Başlama / Bitiş</TableHead>
                    <TableHead className="text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subcontractors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Sistemde henüz taşeron bulunmuyor.
                      </TableCell>
                    </TableRow>
                  ) : (
                    subcontractors.map(sub => (
                      <TableRow key={sub.id}>
                        <TableCell className="font-medium">{sub.name}</TableCell>
                        <TableCell>{branches.find(b => b.id === sub.branch_id)?.name || sub.branch_id}</TableCell>
                        <TableCell>{sub.provided_service || '-'}</TableCell>
                        <TableCell>
                          {sub.completion_requests && sub.completion_requests.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {sub.completion_requests.map((req, i) => (
                                <Badge key={i} variant="outline" className="bg-amber-100 text-amber-700 text-[10px] py-0">
                                  {req.requested_by_name} görevi bitirdi
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">Aktif</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {sub.start_date ? new Date(sub.start_date).toLocaleDateString('tr-TR') : '-'}
                            <br/>
                            <span className="text-gray-400">Bitiş: {sub.end_date ? new Date(sub.end_date).toLocaleDateString('tr-TR') : '-'}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setNewSubcontractor({
                                id: sub.id,
                                name: sub.name || '',
                                tax_number: sub.tax_number || '',
                                phone: sub.phone || '',
                                email: sub.email || '',
                                contact_person: sub.contact_person || '',
                                contacts: sub.contacts?.length > 0 ? sub.contacts : [{ name: '', role: '', phone: '', email: '' }],
                                branch_id: sub.branch_id || '',
                                start_date: sub.start_date || '',
                                end_date: sub.end_date || '',
                                provided_service: sub.provided_service || '',
                                custom_fields: sub.custom_fields || []
                              });
                              setShowSubcontractorModal(true);
                            }}
                            className="mr-2 text-blue-600 hover:bg-blue-50"
                          >
                            <Pencil className="h-4 w-4 mr-1" /> Düzenle
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setSelectedSubDetail(sub)}
                          >
                            <Eye className="h-4 w-4 mr-1" /> Detaylar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubcontractorListModal(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İşten Ayrılma Modal */}
      <Dialog open={showTerminationModal} onOpenChange={setShowTerminationModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="termination-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <UserMinus className="h-5 w-5" />
              İşten Ayrılma İşlemi - {selectedEmployee?.name}
            </DialogTitle>
            <DialogDescription>
              Çalışanın işten ayrılma bilgilerini, belgelerini ve zimmet iadelerini girin
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Temel Bilgiler */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-red-800 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                İşten Çıkış Bilgileri
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="termination_date">İşten Çıkış Tarihi *</Label>
                  <Input
                    id="termination_date"
                    type="date"
                    value={terminationData.termination_date}
                    onChange={(e) => setTerminationData({...terminationData, termination_date: e.target.value})}
                    data-testid="termination-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="termination_reason">Ayrılma Nedeni</Label>
                  <select
                    id="termination_reason"
                    value={terminationData.termination_reason}
                    onChange={(e) => setTerminationData({...terminationData, termination_reason: e.target.value})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="termination-reason"
                  >
                    <option value="">Seçiniz...</option>
                    <option value="istifa">İstifa</option>
                    <option value="sozlesme_feshi">Sözleşme Feshi</option>
                    <option value="emeklilik">Emeklilik</option>
                    <option value="karsilikli_fesih">Karşılıklı Fesih</option>
                    <option value="deneme_suresi">Deneme Süresi Sonu</option>
                    <option value="diger">Diğer</option>
                  </select>
                </div>
              </div>
              
              {/* Diğer seçilince açıklama alanı */}
              {terminationData.termination_reason === 'diger' && (
                <div className="space-y-2">
                  <Label htmlFor="termination_reason_other">Diğer Nedeni Açıklayın *</Label>
                  <Input
                    id="termination_reason_other"
                    placeholder="Lütfen ayrılma nedenini açıklayın..."
                    value={terminationData.termination_reason_other || ''}
                    onChange={(e) => setTerminationData({...terminationData, termination_reason_other: e.target.value})}
                    data-testid="termination-reason-other"
                  />
                </div>
              )}
            </div>

            {/* İşten Çıkış Belgeleri */}
            <div className="bg-[hsl(var(--muted))] rounded-lg p-4 space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Upload className="h-4 w-4" />
                İşten Çıkış Belgeleri
              </h4>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                İstifa dilekçesi, ibraname, SGK çıkış belgesi vb. belgeleri yükleyin
              </p>
              
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  onChange={handleTerminationDocUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="flex-1"
                  data-testid="termination-doc-upload"
                />
              </div>

              {terminationDocuments.length > 0 && (
                <div className="space-y-2">
                  {terminationDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <span className="text-sm">{doc.file_name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTerminationDoc(doc.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Zimmet İade Durumu */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-4">
              <h4 className="font-medium text-orange-800 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Zimmet İade Durumu
              </h4>
              
              {(() => {
                const assets = selectedEmployee?.assets || [];
                const assignedAssets = assets.filter(a => a.status === 'assigned');
                
                if (assignedAssets.length === 0) {
                  return (
                    <div className="text-center py-4 text-[hsl(var(--muted-foreground))]">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Bu çalışanda zimmetli eşya bulunmuyor</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-3">
                    {assignedAssets.map((asset) => (
                      <div key={asset.id} className="bg-white rounded-lg border p-3 space-y-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium">{asset.asset_name}</h5>
                            {asset.serial_number && (
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                Seri No: {asset.serial_number}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant={assetReturnStatus[asset.id]?.returned ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setAssetReturnStatus({
                                ...assetReturnStatus,
                                [asset.id]: { ...assetReturnStatus[asset.id], returned: true }
                              })}
                              className={assetReturnStatus[asset.id]?.returned ? 'bg-green-600 hover:bg-green-700' : 'border-green-300 text-green-600'}
                              data-testid={`asset-returned-${asset.id}`}
                            >
                              <Check className="h-4 w-4 mr-1" />
                              İade Alındı
                            </Button>
                            <Button
                              variant={assetReturnStatus[asset.id]?.returned === false ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setAssetReturnStatus({
                                ...assetReturnStatus,
                                [asset.id]: { ...assetReturnStatus[asset.id], returned: false }
                              })}
                              className={assetReturnStatus[asset.id]?.returned === false ? 'bg-red-600 hover:bg-red-700' : 'border-red-300 text-red-600'}
                              data-testid={`asset-not-returned-${asset.id}`}
                            >
                              <X className="h-4 w-4 mr-1" />
                              Alınmadı
                            </Button>
                          </div>
                        </div>
                        
                        {assetReturnStatus[asset.id]?.returned && (
                          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                            <div className="space-y-1">
                              <Label className="text-xs">İade Durumu</Label>
                              <select
                                value={assetReturnStatus[asset.id]?.condition || 'iyi'}
                                onChange={(e) => setAssetReturnStatus({
                                  ...assetReturnStatus,
                                  [asset.id]: { ...assetReturnStatus[asset.id], condition: e.target.value }
                                })}
                                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                              >
                                <option value="iyi">İyi Durumda</option>
                                <option value="normal">Normal</option>
                                <option value="hasarli">Hasarlı</option>
                                <option value="bozuk">Bozuk/Çalışmıyor</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Açıklama</Label>
                              <Input
                                placeholder="Ne halde alındı..."
                                value={assetReturnStatus[asset.id]?.notes || ''}
                                onChange={(e) => setAssetReturnStatus({
                                  ...assetReturnStatus,
                                  [asset.id]: { ...assetReturnStatus[asset.id], notes: e.target.value }
                                })}
                                className="h-9"
                              />
                            </div>
                          </div>
                        )}
                        
                        {assetReturnStatus[asset.id]?.returned === false && (
                          <div className="pt-2 border-t">
                            <div className="space-y-1">
                              <Label className="text-xs">Neden Alınmadı / Açıklama</Label>
                              <Input
                                placeholder="İade alınmama sebebi..."
                                value={assetReturnStatus[asset.id]?.notes || ''}
                                onChange={(e) => setAssetReturnStatus({
                                  ...assetReturnStatus,
                                  [asset.id]: { ...assetReturnStatus[asset.id], notes: e.target.value }
                                })}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          <DialogFooter className="flex gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowTerminationModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleTerminateEmployee}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-termination"
            >
              <UserMinus className="h-4 w-4 mr-2" />
              İşten Ayrıldı Olarak İşaretle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteEmployeeModal
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        selectedEmployee={selectedEmployee}
        handleDeleteEmployee={handleDeleteEmployee}
        isDeleting={loading}
      />
      <PayrollsModal
        showPayrollsModal={showPayrollsModal}
        setShowPayrollsModal={setShowPayrollsModal}
        selectedEmployee={selectedEmployee}
        handlePayrollUpload={handleAddPayroll}
        payrolls={selectedEmployee?.payrolls || []}
      />
      <DocumentsDetailModal
        showDocumentsDetailModal={showDocumentsDetailModal}
        setShowDocumentsDetailModal={setShowDocumentsDetailModal}
        selectedEmployee={selectedEmployee}
        documentsDetail={documentsDetail}
        activeDocCategory={activeDocCategory}
        setActiveDocCategory={setActiveDocCategory}
        documentCategories={documentCategories}
        isTerminatedEmployee={isTerminatedEmployee}
        newDocumentDetail={newDocumentDetail}
        setNewDocumentDetail={setNewDocumentDetail}
        handleAddDocumentDetail={handleAddDocumentDetail}
        handleDeleteDocumentDetail={handleDeleteDocumentDetail}
        setPreviewDoc={setPreviewDoc}
        setShowPreviewModal={setShowPreviewModal}
        hrDocReqTab={hrDocReqTab}
        setHrDocReqTab={setHrDocReqTab}
        loadDocumentReminders={loadDocumentReminders}
        setShowReminderModal={setShowReminderModal}
        documentReminders={documentReminders}
        deleteReminder={handleDeleteReminder}
      />
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-600" />
              Yeni Belge Hatırlatıcısı
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Belge Türü</Label>
              <select
                value={newReminder.document_type}
                onChange={(e) => setNewReminder({...newReminder, document_type: e.target.value})}
                className="w-full border rounded-md p-2 mt-1"
              >
                <option value="">Seçiniz...</option>
                {documentCategories.map(cat => (
                  <option key={cat.key} value={cat.key}>{cat.label}</option>
                ))}
                <option value="ehliyet">Ehliyet Geçerlilik Belgesi</option>
                <option value="saglik_raporu">Sağlık Raporu</option>
                <option value="diger">Diğer</option>
              </select>
            </div>
            <div>
              <Label>Belge Adı / Açıklama *</Label>
              <Input 
                value={newReminder.document_name}
                onChange={(e) => setNewReminder({...newReminder, document_name: e.target.value})}
                placeholder="Örn: Ehliyet yenileme belgesi"
              />
            </div>
            <div>
              <Label>Son Tarih *</Label>
              <Input 
                type="date"
                value={newReminder.due_date}
                onChange={(e) => setNewReminder({...newReminder, due_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Notlar</Label>
              <Input 
                value={newReminder.notes}
                onChange={(e) => setNewReminder({...newReminder, notes: e.target.value})}
                placeholder="Ek notlar..."
              />
            </div>
            <Button onClick={handleCreateReminder} className="w-full bg-amber-600 hover:bg-amber-700">
              <Bell className="h-4 w-4 mr-2" /> Hatırlatıcı Oluştur
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Avans Detay Modal */}
      <Dialog open={showAdvanceDetailModal} onOpenChange={setShowAdvanceDetailModal}>

      {/* Kesinti Ekleme Modal */}
      <Dialog open={showAddDeductionModal} onOpenChange={setShowAddDeductionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-red-600" />
              Kesinti Ekle
            </DialogTitle>
            <DialogDescription>
              {selectedDeductionEmployee?.name} - {
                selectedDeductionType === 'sgk' ? 'SGK Kesintisi' :
                selectedDeductionType === 'damage' ? 'Zarar Kesintisi' :
                'Diğer Kesinti'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="deduction-amount">Miktar (₺) *</Label>
              <Input
                id="deduction-amount"
                type="number"
                placeholder="0"
                value={newDeduction.amount}
                onChange={(e) => setNewDeduction({ ...newDeduction, amount: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="deduction-description">Açıklama</Label>
              <Textarea
                id="deduction-description"
                placeholder="Kesinti açıklaması..."
                value={newDeduction.description}
                onChange={(e) => setNewDeduction({ ...newDeduction, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="deduction-file">Belge Yükle</Label>
              <Input
                id="deduction-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setDeductionFile(e.target.files[0])}
                className="file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              />
              {deductionFile && (
                <p className="text-xs text-gray-500 mt-1">Seçilen: {deductionFile.name}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDeductionModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleAddDeduction}
              className="bg-red-600 hover:bg-red-700"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Kesinti Detay Modal */}
      <Dialog open={showDeductionDetailModal} onOpenChange={setShowDeductionDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-red-600" />
              Kesinti Detayları
            </DialogTitle>
            <DialogDescription>
              {deductionDetailData?.employee_name} - {monthNames[deductionDetailData?.month - 1]} {deductionDetailData?.year}
            </DialogDescription>
          </DialogHeader>
          
          {deductionDetailData?.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-red-50 rounded-lg">
                <div>
                  <span className="text-xs text-gray-500">Kesinti Türü</span>
                  <p className="font-bold text-red-700">
                    {deductionDetailData.deduction_type === 'sgk' ? 'SGK Kesintisi' :
                     deductionDetailData.deduction_type === 'damage' ? 'Zarar Kesintisi' :
                     'Diğer Kesinti'}
                  </p>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Toplam Miktar</span>
                  <p className="font-bold text-red-700">{deductionDetailData.data.amount?.toLocaleString('tr-TR')} ₺</p>
                </div>
              </div>
              
              {deductionDetailData.data.description && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <span className="text-xs text-gray-500">Açıklama</span>
                  <p className="text-gray-700">{deductionDetailData.data.description}</p>
                </div>
              )}
              
              {/* Kesinti Geçmişi */}
              {deductionDetailData.data.history?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-2">Kesinti Geçmişi</h4>
                  <div className="space-y-2">
                    {deductionDetailData.data.history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium text-red-600">+{entry.amount?.toLocaleString('tr-TR')} ₺</span>
                          {entry.description && <span className="text-gray-500 ml-2">- {entry.description}</span>}
                        </div>
                        <div className="text-xs text-gray-400">
                          {entry.added_by} - {new Date(entry.added_at).toLocaleDateString('tr-TR')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Yüklenen Belgeler */}
              {deductionDetailData.data.documents?.length > 0 && (
                <div className="border rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Yüklenen Belgeler ({deductionDetailData.data.documents.length})
                  </h4>
                  <div className="space-y-2">
                    {deductionDetailData.data.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <div>
                            <span className="text-sm font-medium">{doc.filename}</span>
                            <p className="text-xs text-gray-400">
                              {doc.uploaded_by} - {new Date(doc.uploaded_at).toLocaleDateString('tr-TR')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              const fileUrl = doc.file_url || (doc.data ? `data:${doc.content_type};base64,${doc.data}` : null);
                              setPreviewDoc({ file_url: fileUrl, file_name: doc.filename });
                              setShowPreviewModal(true);
                            }}
                            title="Önizle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-green-600 hover:text-green-800"
                            onClick={() => {
                              const fileUrl = doc.file_url || (doc.data ? `data:${doc.content_type};base64,${doc.data}` : null);
                              const link = document.createElement('a');
                              link.href = fileUrl;
                              link.download = doc.filename;
                              link.click();
                            }}
                            title="İndir"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeductionDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-pink-600" />
              Avans Detayları
            </DialogTitle>
            <DialogDescription>
              {advanceDetailData && (
                <span>
                  {advanceDetailData.employee_name} - {['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'][advanceDetailData.month]} {advanceDetailData.year}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {advanceDetailLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
            </div>
          ) : advanceDetailData?.details?.length > 0 ? (
            <div className="space-y-4">
              {advanceDetailData.details.map((detail, index) => (
                <Card key={index} className="border-pink-200">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="font-mono text-sm text-gray-500">#{detail.form_number || 'N/A'}</span>
                          Avans Talep Formu
                        </CardTitle>
                        <CardDescription>
                          Onay Tarihi: {detail.approved_at ? new Date(detail.approved_at).toLocaleDateString('tr-TR') : 'N/A'}
                        </CardDescription>
                      </div>
                      <Badge className="bg-green-500">Onaylandı</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-4 p-3 bg-pink-50 rounded-lg">
                      <div>
                        <span className="text-xs text-gray-500">Avans Miktarı</span>
                        <p className="font-bold text-pink-700">{detail.amount?.toLocaleString('tr-TR')} ₺</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Taksit Sayısı</span>
                        <p className="font-bold">{detail.installments} Taksit</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500">Aylık Taksit</span>
                        <p className="font-bold text-orange-600">{detail.installment_amount?.toLocaleString('tr-TR')} ₺</p>
                      </div>
                    </div>
                    
                    {detail.hr_request && (
                      <div className="border rounded-lg p-3">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Form Bilgileri
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Talep Eden:</span>
                            <span className="ml-2 font-medium">{detail.hr_request.requester_name}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Talep Tarihi:</span>
                            <span className="ml-2 font-medium">
                              {new Date(detail.hr_request.created_at).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                          {detail.hr_request.form_data?.avansSebebi && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Avans Sebebi:</span>
                              <p className="mt-1 text-gray-700 bg-gray-50 p-2 rounded">
                                {detail.hr_request.form_data.avansSebebi || detail.hr_request.form_data?.form_data?.avansSebebi}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Onay Geçmişi */}
                        {detail.hr_request.status_history && detail.hr_request.status_history.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <h5 className="text-xs font-medium text-gray-500 mb-2">Onay Geçmişi</h5>
                            <div className="space-y-1">
                              {detail.hr_request.status_history.map((history, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  <span className={`w-2 h-2 rounded-full ${
                                    history.action === 'approved' || history.action === 'approve' ? 'bg-green-500' :
                                    history.action === 'rejected' || history.action === 'reject' ? 'bg-red-500' :
                                    'bg-blue-500'
                                  }`}></span>
                                  <span className="text-gray-600">
                                    {new Date(history.timestamp).toLocaleDateString('tr-TR')}
                                  </span>
                                  <span className="font-medium">{history.actor_name}</span>
                                  <span className="text-gray-500">-</span>
                                  <span className={
                                    history.action === 'approved' || history.action === 'approve' ? 'text-green-600' :
                                    history.action === 'rejected' || history.action === 'reject' ? 'text-red-600' :
                                    'text-blue-600'
                                  }>
                                    {history.action === 'forward_hr' ? 'İK\'ya İletti' :
                                     history.action === 'forward_management' ? 'Yönetime İletti' :
                                     history.action === 'approved' || history.action === 'approve' ? 'Onayladı' :
                                     history.action === 'rejected' || history.action === 'reject' ? 'Reddetti' :
                                     history.action}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Bu ay için avans detayı bulunamadı
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdvanceDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fazla Mesai Ekle Modal */}
      <Dialog open={showAddOvertimeModal} onOpenChange={setShowAddOvertimeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-600" />
              Fazla Mesai Ekle
            </DialogTitle>
            <DialogDescription>
              {monthNames[overtimeMonth - 1]} {overtimeYear} için fazla mesai kaydı ekleyin
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Çalışan *</Label>
              <Select
                value={newOvertime.employee_id}
                onValueChange={(value) => setNewOvertime({...newOvertime, employee_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Çalışan seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(emp => !emp.is_terminated).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || `${emp.first_name} ${emp.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Mesai Tarihi *</Label>
              <Input 
                type="date"
                value={newOvertime.date}
                onChange={(e) => setNewOvertime({...newOvertime, date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Mesai Türü *</Label>
              <Select
                value={newOvertime.type}
                onValueChange={(value) => setNewOvertime({...newOvertime, type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekday">Gün İçi Mesai</SelectItem>
                  <SelectItem value="holiday">Tatil Mesaisi</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Mesai Saati *</Label>
              <Input 
                type="number"
                step="0.5"
                min="0.5"
                max="24"
                placeholder="Saat"
                value={newOvertime.hours}
                onChange={(e) => setNewOvertime({...newOvertime, hours: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Açıklama</Label>
              <Textarea 
                placeholder="Mesai açıklaması..."
                value={newOvertime.description}
                onChange={(e) => setNewOvertime({...newOvertime, description: e.target.value})}
              />
            </div>
            
            {/* Belge Yükleme */}
            <div>
              <Label>Belgeler</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewOvertime(prev => ({
                      ...prev,
                      documents: [...prev.documents, ...files]
                    }));
                  }}
                  className="hidden"
                  id="overtime-file-upload"
                />
                <label
                  htmlFor="overtime-file-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Belge Yükle (PDF, JPG, PNG, DOC)</span>
                </label>
                {newOvertime.documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newOvertime.documents.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewOvertime(prev => ({
                              ...prev,
                              documents: prev.documents.filter((_, i) => i !== idx)
                            }));
                          }}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOvertimeModal(false)}>
              İptal
            </Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleAddOvertime}
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avans Ekle Modal */}
      <Dialog open={showAddAdvanceModal} onOpenChange={setShowAddAdvanceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-pink-600" />
              Avans Ekle
            </DialogTitle>
            <DialogDescription>
              Çalışan için yeni avans kaydı oluşturun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Çalışan *</Label>
              <Select
                value={newAdvance.employee_id}
                onValueChange={(value) => setNewAdvance({...newAdvance, employee_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Çalışan seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(emp => !emp.is_terminated).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || `${emp.first_name} ${emp.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Avans Miktarı (₺) *</Label>
              <Input 
                type="number"
                min="0"
                step="100"
                placeholder="Örn: 5000"
                value={newAdvance.amount}
                onChange={(e) => setNewAdvance({...newAdvance, amount: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Taksit Sayısı *</Label>
              <Select
                value={newAdvance.installments.toString()}
                onValueChange={(value) => setNewAdvance({...newAdvance, installments: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} Taksit {num > 1 ? `(Aylık ${newAdvance.amount ? (parseFloat(newAdvance.amount) / num).toFixed(2) : '0'} ₺)` : '(Tek Seferde)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Avans Veriliş Tarihi *</Label>
              <Input 
                type="date"
                value={newAdvance.given_date}
                onChange={(e) => setNewAdvance({...newAdvance, given_date: e.target.value})}
                data-testid="advance-given-date"
              />
              <p className="text-xs text-gray-500 mt-1">
                Bu tarih "Çekilen Avanslar" tablosuna kaydedilecek
              </p>
            </div>
            
            <div>
              <Label>Kesinti Başlama Tarihi *</Label>
              <Input 
                type="date"
                value={newAdvance.deduction_start_date}
                onChange={(e) => setNewAdvance({...newAdvance, deduction_start_date: e.target.value})}
                data-testid="advance-deduction-start-date"
              />
              <p className="text-xs text-gray-500 mt-1">
                Bu tarihten itibaren her ay maaştan kesilecek (Aylık Avans Kesintileri)
              </p>
            </div>
            
            <div>
              <Label>Açıklama</Label>
              <Textarea 
                placeholder="Avans açıklaması (isteğe bağlı)..."
                value={newAdvance.description}
                onChange={(e) => setNewAdvance({...newAdvance, description: e.target.value})}
              />
            </div>
            
            {/* Belge Yükleme */}
            <div>
              <Label>Belgeler</Label>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewAdvance(prev => ({
                      ...prev,
                      documents: [...prev.documents, ...files]
                    }));
                  }}
                  className="hidden"
                  id="advance-file-upload"
                />
                <label
                  htmlFor="advance-file-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-pink-500 hover:bg-pink-50 transition-colors"
                  data-testid="advance-file-upload-label"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Belge Yükle (PDF, JPG, PNG, DOC)</span>
                </label>
                {newAdvance.documents.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newAdvance.documents.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewAdvance(prev => ({
                              ...prev,
                              documents: prev.documents.filter((_, i) => i !== idx)
                            }));
                          }}
                          data-testid={`remove-advance-doc-${idx}`}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdvanceModal(false)}>
              İptal
            </Button>
            <Button 
              className="bg-pink-600 hover:bg-pink-700"
              onClick={handleAddAdvance}
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İzin Ekle Modal */}
      <Dialog open={showAddLeaveModal} onOpenChange={setShowAddLeaveModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-600" />
              İzin Ekle
            </DialogTitle>
            <DialogDescription>
              Çalışan için yeni izin kaydı oluşturun
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Çalışan Seçimi */}
            <div>
              <Label>Çalışan *</Label>
              <Select
                value={newLeave.employee_id}
                onValueChange={(value) => setNewLeave({...newLeave, employee_id: value})}
              >
                <SelectTrigger data-testid="leave-employee-select">
                  <SelectValue placeholder="Çalışan seçin" />
                </SelectTrigger>
                <SelectContent>
                  {employees.filter(emp => !emp.is_terminated).map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name || `${emp.first_name} ${emp.last_name}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* İzin Türü */}
            <div>
              <Label>İzin Türü *</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  variant={newLeave.izinTuru === 'yillik' ? 'default' : 'outline'}
                  className={`flex-1 ${newLeave.izinTuru === 'yillik' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''}`}
                  onClick={() => setNewLeave({ ...newLeave, izinTuru: 'yillik' })}
                  data-testid="leave-type-yillik"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Yıllık İzin
                </Button>
                <Button
                  type="button"
                  variant={newLeave.izinTuru === 'mazeret' ? 'default' : 'outline'}
                  className={`flex-1 ${newLeave.izinTuru === 'mazeret' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''}`}
                  onClick={() => setNewLeave({ ...newLeave, izinTuru: 'mazeret' })}
                  data-testid="leave-type-mazeret"
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Ücretli İzin
                </Button>
                <Button
                  type="button"
                  variant={newLeave.izinTuru === 'ucretsiz' ? 'default' : 'outline'}
                  className={`flex-1 ${newLeave.izinTuru === 'ucretsiz' ? 'bg-cyan-600 hover:bg-cyan-700 text-white' : ''}`}
                  onClick={() => setNewLeave({ ...newLeave, izinTuru: 'ucretsiz' })}
                  data-testid="leave-type-ucretsiz"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Ücretsiz İzin
                </Button>
              </div>
            </div>
            
            {/* Tarih Seçimi */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="izinBaslangic">İzin Başlangıç Tarihi *</Label>
                <Input
                  id="izinBaslangic"
                  type="date"
                  value={newLeave.izinBaslangic || ''}
                  onChange={(e) => setNewLeave({ ...newLeave, izinBaslangic: e.target.value })}
                  data-testid="leave-start-date"
                />
              </div>
              <div>
                <Label htmlFor="izinBitis">İzin Bitiş Tarihi *</Label>
                <Input
                  id="izinBitis"
                  type="date"
                  value={newLeave.izinBitis || ''}
                  onChange={(e) => setNewLeave({ ...newLeave, izinBitis: e.target.value })}
                  min={newLeave.izinBaslangic || undefined}
                  data-testid="leave-end-date"
                />
              </div>
            </div>
            
            {/* Gün Sayısı Hesaplama */}
            {newLeave.izinBaslangic && newLeave.izinBitis && (
              <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-cyan-800 font-medium">Toplam İzin Süresi:</span>
                  <Badge className="bg-cyan-600 text-white">
                    {(() => {
                      const start = new Date(newLeave.izinBaslangic);
                      const end = new Date(newLeave.izinBitis);
                      let days = 0;
                      let current = new Date(start);
                      while (current <= end) {
                        // Yıllık izin için Pazar günlerini sayma (getDay() === 0 = Sunday)
                        if (newLeave.izinTuru === 'yillik') {
                          if (current.getDay() !== 0) days++;
                        } else {
                          days++;
                        }
                        current.setDate(current.getDate() + 1);
                      }
                      return days;
                    })()} gün
                    {newLeave.izinTuru === 'yillik' && (
                      <span className="text-xs ml-1">(Pazar hariç)</span>
                    )}
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Açıklama */}
            <div>
              <Label>Açıklama</Label>
              <Textarea 
                placeholder="İzin açıklaması (isteğe bağlı)..."
                value={newLeave.aciklama}
                onChange={(e) => setNewLeave({...newLeave, aciklama: e.target.value})}
                data-testid="leave-description"
              />
            </div>
            
            {/* Belge Yükleme */}
            <div>
              <Label>Belgeler</Label>
              <p className="text-xs text-gray-500 mb-2">
                Yöneticinizin ve sizin ıslak imzalı formunuzu iletmeniz gerekmektedir.
              </p>
              <div className="mt-2">
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setNewLeave(prev => ({
                      ...prev,
                      belgeler: [...prev.belgeler, ...files]
                    }));
                  }}
                  className="hidden"
                  id="leave-file-upload"
                />
                <label
                  htmlFor="leave-file-upload"
                  className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-500 hover:bg-cyan-50 transition-colors"
                  data-testid="leave-file-upload-label"
                >
                  <Upload className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Belge Yükle (PDF, JPG, PNG, DOC)</span>
                </label>
                {newLeave.belgeler.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newLeave.belgeler.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="truncate">{file.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewLeave(prev => ({
                              ...prev,
                              belgeler: prev.belgeler.filter((_, i) => i !== idx)
                            }));
                          }}
                          data-testid={`remove-leave-doc-${idx}`}
                        >
                          <X className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLeaveModal(false)}>
              İptal
            </Button>
            <Button 
              className="bg-cyan-600 hover:bg-cyan-700"
              onClick={handleAddLeave}
              data-testid="save-leave-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İzin Detay Modalı */}
      {/* Mesai Notu Modalı (Şube yöneticisi notu) */}
      <Dialog open={noteModalOpen} onOpenChange={(o) => { if (!o) { setNoteModalOpen(false); setNoteModalEmp(null); setNoteModalText(''); } }}>
        <DialogContent className="max-w-md" data-testid="hr-attendance-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Mesai Notu
            </DialogTitle>
            <DialogDescription>
              {noteModalEmp?.name} · {noteModalDate} — şube yöneticisinin bu güne dair notu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea
              value={noteModalText}
              onChange={(e) => setNoteModalText(e.target.value)}
              rows={4}
              placeholder="Örn: Sistemde işaretli ama işe gelmedi / saat hatalı..."
              data-testid="hr-attendance-note-textarea"
            />
            {noteModalEmp && monthlyNotes[`${noteModalEmp.employee_id}|${noteModalDate}`]?.updated_by_name && (
              <p className="text-xs text-gray-400">
                Not giren: {monthlyNotes[`${noteModalEmp.employee_id}|${noteModalDate}`].updated_by_name}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNoteModalOpen(false); setNoteModalEmp(null); setNoteModalText(''); }}>İptal</Button>
            <Button onClick={saveNoteModal} disabled={noteModalSaving} className="bg-red-600 hover:bg-red-700 text-white" data-testid="hr-attendance-note-save-btn">
              {noteModalSaving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={showLeaveDetailModal} onOpenChange={setShowLeaveDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-600" />
              Onaylı İzin Formu
            </DialogTitle>
            <DialogDescription>
              Bu güne ait onaylanmış izin talebinin detayları
            </DialogDescription>
          </DialogHeader>
          
          {selectedLeaveDetail && (
            <div className="space-y-4">
              {/* Talep Bilgileri */}
              <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Talep Eden:</span>
                    <p className="font-medium">{selectedLeaveDetail.requester_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">İzin Türü:</span>
                    <p className="font-medium">
                      {selectedLeaveDetail.form_data?.izinTuru === 'yillik' ? 'Yıllık İzin' : 
                       selectedLeaveDetail.form_data?.izinTuru === 'mazeret' ? 'Ücretli İzin' : 'Ücretsiz İzin'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Başlangıç:</span>
                    <p className="font-medium">
                      {selectedLeaveDetail.form_data?.izinBaslangic ? 
                        new Date(selectedLeaveDetail.form_data.izinBaslangic).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Bitiş:</span>
                    <p className="font-medium">
                      {selectedLeaveDetail.form_data?.izinBitis ? 
                        new Date(selectedLeaveDetail.form_data.izinBitis).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Talep Tarihi:</span>
                    <p className="font-medium">
                      {selectedLeaveDetail.created_at ? 
                        new Date(selectedLeaveDetail.created_at).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Toplam Gün:</span>
                    <p className="font-medium">
                      {selectedLeaveDetail.hr_approval?.selected_days?.length || 0} gün
                    </p>
                  </div>
                </div>
                
                {selectedLeaveDetail.form_data?.izinNedeni && (
                  <div className="border-t border-cyan-200 pt-3">
                    <span className="text-gray-500 text-sm">Neden:</span>
                    <p className="mt-1 text-gray-700 bg-white p-2 rounded">{selectedLeaveDetail.form_data.izinNedeni}</p>
                  </div>
                )}
              </div>

              {/* Onay Bilgileri */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <h4 className="font-medium text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Onay Bilgileri
                </h4>
                {selectedLeaveDetail.branch_approval && (
                  <div className="text-sm">
                    <span className="text-gray-500">Şube Onayı:</span>
                    <span className="ml-2 font-medium">{selectedLeaveDetail.branch_approval.approved_by_name || 'Şube Müdürü'}</span>
                    <span className="text-gray-400 ml-2">
                      ({new Date(selectedLeaveDetail.branch_approval.timestamp).toLocaleDateString('tr-TR')})
                    </span>
                  </div>
                )}
                {selectedLeaveDetail.hr_approval && (
                  <div className="text-sm">
                    <span className="text-gray-500">İK Onayı:</span>
                    <span className="ml-2 font-medium">{selectedLeaveDetail.hr_approval.approved_by_name || 'İK'}</span>
                    <span className="text-gray-400 ml-2">
                      ({new Date(selectedLeaveDetail.hr_approval.timestamp).toLocaleDateString('tr-TR')})
                    </span>
                    {selectedLeaveDetail.hr_approval.note && (
                      <p className="mt-1 text-gray-600 bg-white p-2 rounded text-xs">
                        Not: {selectedLeaveDetail.hr_approval.note}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedLeaveDetail.form_data?.izinBelgeleri && selectedLeaveDetail.form_data.izinBelgeleri.length > 0 && (
                <div className="p-4 bg-gray-50 border rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Eklenen Belgeler
                  </h4>
                  <div className="space-y-2">
                    {selectedLeaveDetail.form_data.izinBelgeleri.map((belge, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                        <span>{belge.name}</span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPreviewDoc({ file_url: belge.data, file_name: belge.name });
                              setShowPreviewModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = belge.data;
                              link.download = belge.name;
                              link.click();
                            }}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şifre Doğrulama Modalı */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-red-500" />
              Şifre Doğrulama
            </DialogTitle>
            <DialogDescription>
              Bu işlemi gerçekleştirmek için yönetici şifresini girin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="password-input">Şifre</Label>
              <Input
                id="password-input"
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Şifreyi girin"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePasswordConfirm();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
              İptal
            </Button>
            <Button onClick={handlePasswordConfirm} className="bg-red-600 hover:bg-red-700">
              Onayla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeeList;
